import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CASE_KEY,
  CASES_KEY,
  fillWall,
  planManual,
  postVersion,
  splitBlank,
  StaleVersionError,
  useCase,
  useLatestCalculation,
  type DemoConsoleCatalogueEntry,
  type DemoConsoleLine,
  type DemoConsoleMark,
  type DemoElementCutout,
  type DemoPlacedElement,
  type DemoRunResult,
  type DemoSplitRule,
  type DemoWallSplitVariants,
  type CaseDetailOut,
  type GratingTypeOut,
  type GratingTypeWord,
  type PlacementInput,
  type PlanInput,
  type SplitPriority,
} from "@/api";
import { ProblemError, type components } from "@k60/contracts";
import { Button } from "@/components/Button";
import { CancelledNotice } from "@/components/CancelledNotice";
import { ConfirmReleaseLoss } from "@/components/ConfirmReleaseLoss";
import { PageTitle } from "@/components/PageTitle";
import { SketchPane } from "@/components/SketchPane";
import {
  ChangeFormButton,
  FORM_CHANGED,
  FORM_CHANGED_PARAM,
  FORM_CHANGED_VALUE,
} from "@/components/ChangeFormDialog";
import { DocumentsButton } from "@/components/DocumentsButton";
import { EmptyState } from "@/components/EmptyState";
import { PresetChoice } from "@/components/fields/PresetChoice";
import { Select } from "@/components/fields/Select";
import { AlertIcon, CheckIcon } from "@/components/icons";
import { Kbd } from "@/components/Kbd";
import { Panel } from "@/components/Panel";
import {
  CUT_NAMES,
  consoleMarksOf,
  cornerFactsOf,
  cutsOf,
  elementsOf,
  indexConsoleMarks,
  notDrawableFinding,
  placementsOfAnswer,
  type CornerFact,
  type ElementCut,
} from "@/components/planAnswer";
import {
  GRATING_TYPE_NAMES,
  KIND_NAMES,
  PlanningBoard,
  VIEW_PADDING,
  type ConsoleMark,
  type ConsoleMenuRequest,
  type CornerMenuRequest,
  type FeatureRefusal,
  type Tool,
  type WallRefusal,
} from "@/components/PlanningBoard";
import { StateMessage } from "@/components/StateMessage";
import { useScreenToast, type ToastInput } from "@/components/Toast";
import { Tooltip } from "@/components/Tooltip";
import { FACADE_SENT, facadeEditability, restorePath } from "@/lib/facadeEditability";
import { formatMm, formatMoney } from "@/lib/format";
import { coatingSentence, WELDED_NOT_PRICED } from "@/lib/mailRequest";
import {
  gratingTypeLabel,
  handSetSentence,
  isGratingTypeWord,
  laidGratingsOf,
  type LaidGratings,
} from "@/lib/gratingTypes";
import { withViewTransition } from "@/lib/motion";
import { placeFloating } from "@/lib/placeFloating";
import {
  FASE_BUTT_ONLY,
  FASE_MEASURE,
  FASE_NO_OBJECTS,
  isFase,
  isFaseCorner,
  outlineProjection,
  shortenedWallLabel,
  type DemoOutline,
} from "@/lib/outline";
import {
  CUTOUT_DEFAULT_MM,
  FEATURE_DEFAULTS,
  FEATURE_TYPES,
  HINGE_WORDS,
  SWING_TYPES,
  hingeOf,
  bandWidthMm,
  carrierAt,
  cutoutAlongMm,
  cutoutExtentMm,
  GRID_MM,
  distanceToReference,
  effectiveWidthMm,
  freeCutoutAt,
  freeFeatureOffsetMm,
  freeStretches,
  linkedCutoutFor,
  PIPE_PRESETS_MM,
  holeForPipe,
  pipeOffsetMm,
  nextCutoutId,
  nextFeatureId,
  offsetFromReference,
  placementBoxMm,
  newSplitId,
  settleFeature,
  splitPlacement,
  snapCutout,
  wallAcceptsGrating,
  wallLengthMm,
  WALL_START,
  type Cutout,
  type CutoutReference,
  type CutoutShape,
  type DoorHinge,
  type FacadeFeature,
  type FeatureType,
  type GratingSpec,
  type CornerMark,
  type Placement,
} from "@/lib/placement";
import { firstChordCaps, isMac, type ShortcutId } from "@/lib/shortcuts";
import { useMaximize } from "@/lib/useMaximize";
import { useRouteNotice } from "@/lib/useRouteNotice";
import { keepingFocus, modalDialogOpen, undoKeys, useUndoKeys } from "@/lib/useUndoKeys";
import { viewForBox } from "@/lib/viewport";
import {
  EMPTY_DRAFT,
  usePlanning,
  type ConsoleSeries,
  type CutoutSurcharge,
  type PlanningDraft,
  type PlanningStep,
} from "@/store/planning";

import { axisGap, axisOf, closureSentence, directedWalls, directionOf } from "./axisGap";
import { PLANNING_TITLE } from "./head";
import { JobGratingType, type JobGratingTypeHandle } from "./JobGratingType";
import styles from "./PlanningScreen.module.css";
import {
  mismatchSentence,
  RelayOffer,
  SplitPriorityField,
  SplitVariantList,
} from "./SplitVariants";
import { describeStep } from "./undoLabel";
import { skippedWallsSentence, unmeasuredWallsOf, unmeasuredWallsSentence } from "./unmeasuredWalls";
import {
  disputedWallOf,
  heldOffPlacementIds,
  ImplausibleMeasure,
  MEASURE_IMPLAUSIBLE,
  withReplayFindings,
  type ImplausibleWriteTarget,
} from "./ImplausibleMeasure";
import { WallMeasureField, type LostMeasure } from "./WallMeasureField";

/** One geometry op on the wire (`OpIn`) — what a corner or feature gesture
 * writes into a version's `ops` (design §4.1). Grating/console gestures send
 * `[]`: their whole statement is the new `planningState`. */
type OpIn = components["schemas"]["OpIn"];

/**
 * The document's own id for one wall, or `null` where this wall has none.
 *
 * `set_corner`/`set_feature`/`remove_feature` are keyed by
 * `schema.document.DocumentWall.id` (`w1`, `w2`, …) — never by `wallIndex`,
 * which is only a position in *this* arrangement's segment list, and **never
 * by `edgeId`**, which is a display artefact and no document key at all
 * (design §4). The backend puts the real id on the wire as
 * `SegmentOut.wallId`, filled on the replay path, and that is the one this
 * reads.
 *
 * `null` is a real answer and has to travel as one: on the enumeration path,
 * and on a contour whose document could not be built, there is no id to name.
 * Until fix round 1 this fell back to `edgeId` and then to `String(index)` —
 * which posts a *fabricated* key. Ops are parsed when the version is appended
 * and only applied at replay, so such a version is accepted and then makes
 * every later `GET /cases/{id}/plan` fail: the case is bricked, permanently,
 * by a gesture that looked like it worked.
 */
function wallIdFor(outline: DemoOutline | null, wallIndex: number): string | null {
  return outline?.segments[wallIndex]?.wallId ?? null;
}

/** `SetCorner`, on — `null` where the wall carries no document id. */
function setCornerOp(outline: DemoOutline | null, mark: CornerMark): OpIn | null {
  const wallId = wallIdFor(outline, mark.wallIndex);
  if (wallId === null) return null;
  let filledWallId: string | null = null;
  if (mark.filledWallIndex !== null) {
    filledWallId = wallIdFor(outline, mark.filledWallIndex);
    // „Auffüllen" names *which* of the two walls covers the corner square. A
    // fill whose named wall has no id is not a fill with one field missing —
    // it is a different statement, so it is refused whole.
    if (filledWallId === null) return null;
  }
  return {
    op: "set_corner",
    wallId,
    on: true,
    treatment: mark.treatment,
    filledWallId,
    kindOverride: mark.kindOverride,
    gainOverrideMm: mark.gainOverrideMm,
  };
}

/** `SetCorner`, off — takes the whole mark off, overrides included. */
function clearCornerOp(outline: DemoOutline | null, wallIndex: number): OpIn | null {
  const wallId = wallIdFor(outline, wallIndex);
  return wallId === null ? null : { op: "set_corner", wallId, on: false };
}

/** `SetFeature` — creating and changing are the same op (design §4.1). */
function setFeatureOp(outline: DemoOutline | null, feature: FacadeFeature): OpIn | null {
  const wallId = wallIdFor(outline, feature.wallIndex);
  if (wallId === null) return null;
  return {
    op: "set_feature",
    id: feature.id,
    wallId,
    offsetMm: feature.offsetMm,
    lengthMm: feature.lengthMm,
    featureType: feature.featureType,
    covered: feature.covered,
    widthOffsetMm: feature.widthOffsetMm,
    splitsRun: feature.splitsRun,
    label: feature.label,
    // On every set, not only the one that switched it: `set_feature`
    // replaces the whole object, and an omitted hinge reads as left on the
    // server — a moved right-hinged door would swing back.
    hinge: hingeOf(feature),
  };
}

/** `RemoveFeature`, by id — keyed by the object's own id, so no wall is
 * named and there is nothing here that can be missing. */
function removeFeatureOp(id: string): OpIn {
  return { op: "remove_feature", id };
}

/**
 * `SetCutout` — creating and changing are the same op; `reference` travels
 * verbatim (D-11).
 *
 * `offsetMm` is resolved **at emit time** against the objects as they stand:
 * a linked hole has no position of its own along the wall (§4), so the
 * draft's field goes stale the moment its object moves, and the wire must
 * carry the object's current middle, never that leftover (walk B-5). The
 * server derives the position from the object either way; a reader of the
 * ops log alone would otherwise see the hole where the object used to be.
 */
function setCutoutOp(
  outline: DemoOutline | null,
  cutout: Cutout,
  features: readonly FacadeFeature[],
): OpIn | null {
  const wallId = wallIdFor(outline, cutout.wallIndex);
  if (wallId === null) return null;
  return {
    op: "set_cutout",
    id: cutout.id,
    wallId,
    offsetMm: cutoutAlongMm(cutout, features),
    acrossMm: cutout.acrossMm,
    shape: cutout.shape,
    diameterMm: cutout.diameterMm,
    sizeAlongMm: cutout.sizeAlongMm,
    sizeAcrossMm: cutout.sizeAcrossMm,
    obstacleType: cutout.obstacleType,
    confirmed: cutout.confirmed,
    confirmedOn: cutout.confirmedOn,
    featureId: cutout.featureId,
    reference: cutout.reference,
  };
}

function removeCutoutOp(id: string): OpIn {
  return { op: "remove_cutout", id };
}

/**
 * Whether the server has *refused* this write, as opposed to failing to
 * receive it.
 *
 * A `ProblemError` is only ever built from a real HTTP answer
 * (`contracts/client.ts`'s middleware), so its status is the server's verdict;
 * a network failure never gets that far and arrives as a plain `Error`. A 4xx
 * is a verdict about this request and will be the same verdict next time — a
 * malformed op, a state the endpoint rejects. A 5xx is not: the server fell
 * over, and the same request may well go through in a second.
 *
 * The distinction decides whether the pending gesture is kept for another go
 * or dropped. Keeping a permanently refused one poisons every later write,
 * because it travels along with them (fix round 2, item 2).
 *
 * Three 4xx are not verdicts about *this* write and are excluded, or the
 * gesture would be thrown away over something that will pass on the next try:
 *
 * - **401** — the session ended. The write never had a chance to be judged,
 *   and calling it refused would tell the person their gesture was wrong when
 *   what expired was their session. It stays pending and is re-sent on the
 *   next settle; the screen shows the server's „Nicht angemeldet." — nothing
 *   redirects to `/anmelden` yet (that belongs to a later task).
 * - **408** — the request timed out on the way, which is a transport failure
 *   wearing a 4xx.
 * - **429** — too many requests, the one 4xx whose whole meaning is „later".
 */
const NOT_A_VERDICT = new Set([401, 408, 429]);

function permanentlyRefused(cause: unknown): cause is ProblemError {
  return (
    cause instanceof ProblemError &&
    cause.status >= 400 &&
    cause.status < 500 &&
    !NOT_A_VERDICT.has(cause.status)
  );
}

/**
 * Whether the server said there was nothing in this write — `EmptyVersion`,
 * the 400 for `ops: []` on an unchanged `planningState`.
 *
 * Not a refusal to recover from: the board already stands where the log
 * stands. Reloading the Vorgang for it threw away the zoom and the
 * selection, and on a Vorgang without a stored grating type (§15.14) it
 * was the end of the screen (walk B-8). The sentence is the server's own,
 * `schema/service.py`.
 */
function nothingToSave(cause: unknown): boolean {
  return (
    cause instanceof ProblemError &&
    cause.status === 400 &&
    cause.userMessage.includes("nichts zu speichern")
  );
}

/**
 * Where the board stands with the server, as its head says it (Welle 1,
 * idea 23): saved as a Stand, on its way, or not saved and why. The why is a
 * few words — the sentence a person can act on stands in the messages above
 * the board, where it always stood.
 */
type SaveState =
  | { kind: "saved"; versionNo: number }
  | { kind: "saving" }
  | { kind: "unsaved"; reason: string };

/** „Gespeichert · Stand 4“ — the head's words and the toast's, one spelling. */
function savedSentence(versionNo: number): string {
  return `Gespeichert · Stand ${versionNo}`;
}

/**
 * Why a write did not land, in the head's few words — only the failures that
 * keep the gesture pending reach here (a transport failure, 401, 408, 429,
 * 5xx; `permanentlyRefused` and a 409 take other ways). Read off the status,
 * not off `ProblemError.message`: that is the title, „Fehler“ for every 5xx
 * and capitalised after the dash („Nicht angemeldet“). Anything that is not
 * an answer of the server is the network.
 */
function unsavedReason(cause: unknown): string {
  if (!(cause instanceof ProblemError)) return "keine Verbindung";
  if (cause.status === 401) return "nicht angemeldet";
  if (cause.status === 408) return "Zeitüberschreitung";
  if (cause.status === 429) return "zu viele Anfragen";
  if (cause.status >= 500) return "Serverfehler";
  return "Fehler";
}

/** The planning state as a version carries it, `PlanningStateIn`. */
type PlanningStateWire = components["schemas"]["PlanningStateIn"];

/**
 * Whether two JSON values say the same thing — key order aside, and a key
 * that is absent on one side and `undefined` on the other counts as the
 * same, since neither survives serialisation. `null` stays `null`.
 */
function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((each, index) => sameJson(each, b[index]));
  }
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!sameJson(left[key], right[key])) return false;
  }
  return true;
}

type PlanningStore = ReturnType<typeof usePlanning.getState>;

/**
 * What a Stand put on the board, as the store holds it — the undo stack and
 * every Vorgang-level setting a gesture can change. Kept from the last
 * hydration or write, so „Abbrechen“ under the release question can put the
 * board back exactly where the server has it (UX-Gutachten 23.10.2026, Nr. 5).
 */
type WrittenBoard = Pick<
  PlanningStore,
  | "history"
  | "future"
  | "baseSteps"
  | "consoleSeries"
  | "consoleAdjustableFirst"
  | "consoleOverrides"
  | "cutoutClearanceMm"
  | "cutoutRatePerMNet"
  | "cutoutBaseNet"
  | "cutoutSurcharges"
  | "splitPriority"
>;

function writtenBoardOf(state: PlanningStore): WrittenBoard {
  return {
    history: state.history,
    future: state.future,
    baseSteps: state.baseSteps,
    consoleSeries: state.consoleSeries,
    consoleAdjustableFirst: state.consoleAdjustableFirst,
    consoleOverrides: state.consoleOverrides,
    cutoutClearanceMm: state.cutoutClearanceMm,
    cutoutRatePerMNet: state.cutoutRatePerMNet,
    cutoutBaseNet: state.cutoutBaseNet,
    cutoutSurcharges: state.cutoutSurcharges,
    splitPriority: state.splitPriority,
  };
}

/** The picked wall's „Maß“ field in the side panel takes the focus, where there is one. */
function focusMeasureField(): void {
  document.querySelector<HTMLInputElement>('[data-role="side-panel"] [data-role="wall-measure"] input')?.focus();
}

/** How many plan answers the board keeps for „Abbrechen“ — the gesture's few frames and the board before them. */
const KEPT_ANSWERS = 12;

/** Keeps `answer` under `key`, newest last, dropping the oldest beyond {@link KEPT_ANSWERS}. */
function rememberAnswer(answers: Map<string, DemoRunResult>, key: string, answer: DemoRunResult): void {
  answers.delete(key);
  answers.set(key, answer);
  while (answers.size > KEPT_ANSWERS) answers.delete(answers.keys().next().value!);
}

/**
 * How long the board holds its gestures while a measure or a change of type
 * is saved and read back (re-review of 41503767, N-2) — the bound the
 * calculation keeps for its own reading of the Vorgang (Task 13, M-1).
 */
const WRITE_HOLD_MS = 3000;

/** What the board says while it holds (the tool line, and a press meanwhile). */
const WRITE_WAIT = "Speichert … — einen Moment.";

/** A gesture a hydration threw away before it went out. */
const LOST_GESTURE =
  "Nicht übernommen — deine letzte Änderung kam, während die Planung neu geladen wurde. Bitte wiederhole sie.";

/** What the live region says once „Abbrechen“ put the board back. */
const RELEASE_KEPT = "Nicht geändert — die Freigabe bleibt.";

/** Whether two corner marks say the same thing — every field, because every
 * field of one travels in `set_corner`. */
function sameCorner(a: CornerMark, b: CornerMark): boolean {
  return (
    a.wallIndex === b.wallIndex &&
    a.treatment === b.treatment &&
    a.filledWallIndex === b.filledWallIndex &&
    a.kindOverride === b.kindOverride &&
    a.gainOverrideMm === b.gainOverrideMm
  );
}

/** Whether two facade objects say the same thing — as {@link sameCorner}. */
function sameFeature(a: FacadeFeature, b: FacadeFeature): boolean {
  return (
    a.wallIndex === b.wallIndex &&
    a.offsetMm === b.offsetMm &&
    a.lengthMm === b.lengthMm &&
    a.featureType === b.featureType &&
    a.covered === b.covered &&
    a.widthOffsetMm === b.widthOffsetMm &&
    a.splitsRun === b.splitsRun &&
    a.label === b.label &&
    hingeOf(a) === hingeOf(b)
  );
}

/** Whether two holes say the same thing — every field, `reference` included. */
function sameCutout(a: Cutout, b: Cutout): boolean {
  return (
    a.wallIndex === b.wallIndex &&
    a.offsetMm === b.offsetMm &&
    a.acrossMm === b.acrossMm &&
    a.shape === b.shape &&
    a.diameterMm === b.diameterMm &&
    a.sizeAlongMm === b.sizeAlongMm &&
    a.sizeAcrossMm === b.sizeAcrossMm &&
    a.obstacleType === b.obstacleType &&
    a.confirmed === b.confirmed &&
    a.confirmedOn === b.confirmedOn &&
    a.featureId === b.featureId &&
    a.reference.kind === b.reference.kind &&
    a.reference.featureId === b.reference.featureId &&
    a.reference.side === b.reference.side
  );
}

/**
 * Whether the server takes this hole away by itself — its object is gone or
 * has lost its grating in the same step (design §5.1). A `remove_cutout` for
 * it would be refused („Unbekannter Ausschnitt") and the whole write with it.
 */
function cascaded(cutout: Cutout, featuresAfter: Map<string, FacadeFeature>): boolean {
  if (cutout.featureId === null) return false;
  const feature = featuresAfter.get(cutout.featureId);
  return feature === undefined || !feature.covered;
}

/** The draft's holes without those that hang on `featureId` — what the
 * server cascade does to the document, mirrored so the stateless plan never
 * names a hole whose object is gone (design §5.1). */
function withoutLinkedCutouts(list: readonly Cutout[], featureId: string): Cutout[] {
  return list.filter((each) => each.featureId !== featureId);
}

/**
 * The ops that turn `before`'s geometry into `after`'s — the corner marks,
 * the facade objects and the holes, which are the only three things in a
 * draft the ops log carries at all (placements travel as `planningState`).
 *
 * Written for Rückgängig, which is the one gesture that does not know what it
 * is about to change: it pops a step off the stack, and what comes back may be
 * a corner, an object, an element, or several at once. Sending no op for it —
 * as fix round 0 did — meant the board walked back and the server did not, and
 * the next reload brought the undone mitre straight back (review, Critical:
 * ruling 2).
 *
 * `null` entries are walls with no document id ({@link wallIdFor}); they travel
 * as `null` rather than being dropped so the caller can refuse the write
 * instead of posting a silently incomplete one.
 */
function diffOps(
  outline: DemoOutline | null,
  before: PlanningDraft,
  after: PlanningDraft,
): (OpIn | null)[] {
  const ops: (OpIn | null)[] = [];

  const cornersBefore = new Map(before.corners.map((mark) => [mark.wallIndex, mark]));
  const cornersAfter = new Map(after.corners.map((mark) => [mark.wallIndex, mark]));
  for (const [wallIndex, mark] of cornersAfter) {
    const was = cornersBefore.get(wallIndex);
    if (was === undefined || !sameCorner(was, mark)) ops.push(setCornerOp(outline, mark));
  }
  for (const wallIndex of cornersBefore.keys()) {
    if (!cornersAfter.has(wallIndex)) ops.push(clearCornerOp(outline, wallIndex));
  }

  const featuresBefore = new Map(before.features.map((feature) => [feature.id, feature]));
  const featuresAfter = new Map(after.features.map((feature) => [feature.id, feature]));
  for (const [id, feature] of featuresAfter) {
    const was = featuresBefore.get(id);
    if (was === undefined || !sameFeature(was, feature)) {
      ops.push(setFeatureOp(outline, feature));
    }
  }
  for (const id of featuresBefore.keys()) {
    if (!featuresAfter.has(id)) ops.push(removeFeatureOp(id));
  }

  // Holes last, and sets before removes: a hole set after Rückgängig needs
  // its object back first (`set_feature` above), and a hole set while an
  // object goes must follow the `remove_feature` that would otherwise sweep
  // it (design §5.1). Nothing is sent for a hole the cascade takes anyway.
  const cutoutsBefore = new Map(before.cutouts.map((cutout) => [cutout.id, cutout]));
  const cutoutsAfter = new Map(after.cutouts.map((cutout) => [cutout.id, cutout]));
  for (const [id, cutout] of cutoutsAfter) {
    const was = cutoutsBefore.get(id);
    if (was === undefined || !sameCutout(was, cutout)) ops.push(setCutoutOp(outline, cutout, after.features));
  }
  for (const [id, cutout] of cutoutsBefore) {
    if (!cutoutsAfter.has(id) && !cascaded(cutout, featuresAfter)) ops.push(removeCutoutOp(id));
  }

  return ops;
}

/**
 * Laying grating on a settled outline — the fourth step (design 2026-08-15).
 *
 * **The person says where grating lies. How many elements and how many
 * brackets follow from it is calculated** (§2) — here that means: this screen
 * holds placements and shows an answer, and every domain statement in it comes
 * from `POST /demo/plan/manual`. The console count, every finding, every split
 * is the backend's word. What is decided in the browser is where the pointer
 * put something and what a person typed.
 *
 * On a Vorgang every gesture is written as a Stand (`persist`), and the head
 * says where the board stands with the server (Welle 1, idea 23). The
 * register demo's own `/planung` writes nothing and shows no save state.
 */

/** How long the screen waits after a change before it asks for a new plan. */
/** The banner over a Stand without a single measure (19.10.2026). */
const NEEDS_MEASURE = "Noch kein Maß an diesem Umriss.";

const SETTLE_MS = 300;

/**
 * The three working steps, **in the order the work is done** — objects,
 * then grating, then brackets (design 2026-09-22).
 *
 * That order is the customer's, three times over: „Objekte sollten prozessual
 * vor den Rosten eingegeben werden" (30.08.2026), „erst der Umriss, dann die
 * Objekte, und sobald die in der richtigen Position vorliegen, die Roste drum
 * herum" (03.09.2026), and „die Winkelkonsolen erst legen, nachdem die
 * Gitterroste drin sind" (22.09.2026). A toolbar that offered everything at
 * once taught no order at all, and the console settings standing above an
 * empty board said the opposite of what the lede promises.
 *
 * Each step owns its tools. „Auswählen" belongs to all of them, and the third
 * step opens only once a grating lies — that is the rule as a mechanism, not
 * as a hint (§2 of the design). Every step stays reachable in any order
 * (Denis, 30.08.2026: „falls etwas vergessen wurde"); „Planung aktualisieren"
 * is what makes the late object cost nothing.
 */
const STEPS: { step: PlanningStep; name: string; tools: Tool[] }[] = [
  { step: "objects", name: "Objekte", tools: ["feature", "area"] },
  { step: "grating", name: "Roste", tools: ["grating", "cutout"] },
  { step: "consoles", name: "Konsolen", tools: ["bracket"] },
];

/** The step in force — see `effectiveStep` in the screen (design 2026-09-22 §3). */
function stepInForce(picked: PlanningStep | null, hasGrating: boolean): PlanningStep {
  if (picked === null) return hasGrating ? "grating" : "objects";
  return picked === "consoles" && !hasGrating ? "grating" : picked;
}

/** What a read-only board does with a gesture it is handed (V-1): nothing. */
function ignoreGesture(): void {}

/** The tool actually in hand — see `effectiveTool` in the screen. Module
 * scope so the Escape handler, which runs before the screen's early return,
 * reads the same answer the toolbar shows. */
function toolInHand(tool: Tool, step: PlanningStep, typeMissing: boolean): Tool {
  const spec = STEPS.find((each) => each.step === step)!;
  return typeMissing || !(tool === "select" || spec.tools.includes(tool)) ? "select" : tool;
}

/**
 * The tools, with what each one says in the status line while it is in hand.
 * „Auswählen" comes first and is one of them: without it „no tool active" is
 * an invisible state and the way out of a mode is not findable — the classic
 * source of „it keeps drawing things" (§5.2).
 */
const TOOLS: { tool: Tool; name: string; status: string }[] = [
  {
    tool: "select",
    name: "Auswählen",
    status: "Element anklicken zum Ändern. Wand anklicken, um sie zu belegen.",
  },
  {
    tool: "feature",
    name: "Fassadenobjekt",
    status: "Was an der Fassade steht: Tür, Fensterbank, Fallrohr, Stufe. Vor den Rosten setzen.",
  },
  {
    tool: "area",
    name: "Außenfläche",
    status: "Kiesstreifen und Umgebung schraffieren. Geht in keine Stückliste.",
  },
  { tool: "grating", name: "Gitterrost", status: "Klicken oder ziehen. 1.000 mm." },
  {
    tool: "cutout",
    name: "Ausschnitt",
    status: "Loch im Rost, eckig 120 × 120. Auf das Band klicken, dann ziehen oder im Panel tippen.",
  },
  { tool: "bracket", name: "Konsole extra", status: "Zusätzlich zu den gerechneten." },
];

/**
 * The findings a **re-lay** answers — the ones that say „the division on this
 * wall no longer matches what stands on it".
 *
 * Each of the three is a contradiction between a laid element and an object,
 * and none of them is a mistake the person made: they are what is left over
 * when an object arrives, moves or grows *after* the wall was covered. That
 * is the ordinary case — Denis, 03.09.2026: „wenn beispielsweise das Objekt
 * noch mal verschoben wird, dann müsste die Zeichnung aktualisiert werden."
 *
 * Used for one thing only: to say out loud that „Planung aktualisieren" has
 * something to do. It does not gate the button — a person may re-lay a wall
 * that carries no finding at all, and a button that greys itself out on a
 * judgement of ours is a button nobody trusts.
 */
const RELAY_FIXES: ReadonlySet<string> = new Set([
  "width_ignores_feature",
  "grating_over_feature",
  "cutout_without_grating",
]);

/**
 * What an interruption is, and therefore whether it ends the run.
 *
 * Asked as the thing rather than as the switch: a tick box „trennt den Lauf"
 * would ask the operator to carry the trade rule in their head, while the kind
 * of interruption is something they know anyway. The house rule is in the case
 * register (§3.6): a downpipe and a notch do not divide a Teilzug, an
 * extension does.
 */
const GAP_KINDS: { key: string; name: string; splitsRun: boolean }[] = [
  { key: "door", name: "Tür, Stufe, Anbau", splitsRun: true },
  { key: "downpipe", name: "Fallrohr, Schacht, Ausklinkung", splitsRun: false },
];

/** Findings whose subject is a stretch that cannot be built on. */
const SPLIT_RULE = "free_stretch_below_minimum";

/**
 * The finding whose subject is a **blank** that ran past the maximum.
 *
 * E-47 left it at the report and said the pile-up would be the feedback that
 * corrected it; the feedback arrived on 30.08.2026 at the product owner's own
 * case (1.350 + 288 = 1.638). The finding stays a finding — it carries an
 * offered way out now, exactly as `SPLIT_RULE` carries „So aufteilen".
 */
const BLANK_RULE = "miter_blank_over_maximum";

/**
 * Findings that mark something rather than refuse it.
 *
 * A special width is **allowed** — §3.2.1 changed the rule precisely because
 * A-2, the one reference solution in the stock, is built at 233 mm. It travels
 * as a finding so nobody can miss it, but it is a *marking*: it must not put a
 * red cross through the element and it must not stand under „Passt nicht".
 * Seen on screen, 16.08.2026 — the element was struck through for being built
 * the way the reference case is built.
 *
 * The four that joined it on 30.08.2026 were each decided on their own, not
 * waved through as a group (walk finding 5):
 *
 * - `grating_width_from_feature` is `grating_width_special` with its reason
 *   attached (Entwurf 2026-08-29 §5.1). Two names for one allowed departure
 *   may not carry two verdicts — that is the 16.08. regression exactly.
 * - `grating_width_below_known` says the effective width falls under the
 *   smallest width the stock knows (150 mm, MK-08). The design calls it a
 *   „**Befund** … und keine Ablehnung" in as many words (§2.1): whether such
 *   a width is permissible is Chiara's answer and not this file's.
 * - `feature_beyond_wall` is „**Gemeldet, nicht abgelehnt**" (§2.2) — it
 *   arises when a wall length changes *after* an object was drawn, so a
 *   verdict here would condemn a legitimate edit in progress.
 * - `width_ignores_feature` is the one the design words as „kann richtig
 *   sein, muss sichtbar sein" (§5): a hand-typed width that departs from
 *   the zone is exactly the manual way A-014 keeps open.
 * - `cutout_without_grating` and `cutout_wider_than_grating` are both
 *   markings by name in the design (§14, „Zwei Befunde, beide
 *   Markierungen"). The first is a pipe standing where nobody has laid
 *   grating *yet* — cover the wall and it goes; the second asks whether a
 *   notch at the edge is permissible instead of a hole, and that is a trade
 *   question this file does not decide.
 *
 * **`grating_over_feature` is deliberately *not* here.** It is the only one
 * of the five that states a contradiction rather than a deviation: somebody
 * said no grating covers this object and then laid grating over it. There is
 * no reading of that document in which both halves are true, and the way to
 * say „grating does lie here" is the object's own „Rost liegt darauf" tick.
 * `free_stretch_below_minimum` stays a violation for the same reason — the
 * remainder rule is a hard one (PRD §22.5), and it is the finding that
 * carries the „So aufteilen" button.
 *
 * **`corner_fill_leaves_nothing` is not here either, and for that same
 * reason** (30.08.2026): it states a contradiction between a laid length and
 * a corner treatment — this element is to butt at a filled corner and is not
 * long enough to give that corner square up, so no blank comes out of it at
 * all. Unlike `cutout_wider_than_grating` there is no trade question open
 * behind it and no figure the person could have meant; the two ways out are
 * in the sentence itself (take the filling back, or lay the element longer),
 * and the element is drawn as the empty stretch it is (`data-blank="none"`,
 * design §12.1).
 *
 * `cutout_over_element_edge` and `cutout_second_in_element` are markings like
 * their two predecessors (design 2026-09-10 §9): whether a hole may sit over
 * a joint is Chiara's question (§15.5), not this file's to answer; a second
 * hole in one element is statically permitted with an extra bracket, and the
 * sentence only asks for that to be checked.
 */
const NOTICES: ReadonlySet<string> = new Set([
  "grating_width_special",
  "grating_width_from_feature",
  "grating_width_below_known",
  "feature_beyond_wall",
  "width_ignores_feature",
  "cutout_without_grating",
  "cutout_wider_than_grating",
  "cutout_over_element_edge",
  "cutout_second_in_element",
  // Design 2026-10-18 §5.2, call 6: a hint to look at, not a defect.
  "fase_console_check",
]);

const CORNER_NAMES = { aussen: "Außenecke", innen: "Innenecke" } as const;

/**
 * Why this width offset cannot be taken, or `null` where it can.
 *
 * Arithmetic, not a trade rule (design §2.1): „die wirksame Breite … muss
 * über `0` bleiben, sonst Ablehnung". Checked at the field, because the
 * alternative was measured on 30.08.2026 — the object went into the document,
 * and from then on *every* call answered a refusal („Fensterbank nimmt 400 mm
 * von 288 mm ab — das ergibt keine Breite"), with no way out but Rückgängig
 * or discarding the whole draft. A refusal that arrives after the state it
 * refuses is not a refusal, it is a trap.
 *
 * A `null` type width means nobody has said what kind of grating this is, and
 * then there is no figure to subtract from — that is `typeMissing`'s refusal
 * one level up, not this one's.
 */
function widthOffsetRefusal(typeWidthMm: number | null, offsetMm: number): string | null {
  if (typeWidthMm === null || typeWidthMm + offsetMm > 0) return null;
  return (
    `${formatMm(typeWidthMm)} minus ${formatMm(Math.abs(offsetMm))} ergibt keine Breite. ` +
    "Der Abzug muss kleiner bleiben als die Rostbreite."
  );
}

/** A figure without its unit, for the first half of „400 bis 1.500 mm". Built
 * off `formatMm` so the German grouping is stated in one place only. */
function plain(valueMm: number): string {
  return formatMm(valueMm).replace(/\s*mm$/, "");
}

function toWire(placement: Placement): PlacementInput {
  return { ...placement, widensOutward: placement.widensOutward ?? false } as PlacementInput;
}

/** One article a person can pick in the bracket chooser. */
interface ConsoleArticleOption {
  articleNo: string;
  label: string;
}

/**
 * The articles a person can pick for one bracket — read off the server's own
 * catalogue (`result.consoleCatalogue`), not assembled from what this
 * drawing's answer happens to mention already.
 *
 * The first version built this from `consoleLines` plus each mark's own two
 * article numbers — on an ordinary drawing of one grate width `consoleLines`
 * holds exactly one row and every mark carries the same article, so the menu
 * offered the proposal and „Vorschlag wiederherstellen" and nothing to
 * choose *between*; on design §4.3's own headline case (a width matching no
 * article at all) the mark carries `articleNo: null` and contributed
 * nothing, closing the human way out E-16 and A-014 promise exactly where it
 * is needed (whole-branch review, 05.09.2026, Critical 2). The catalogue is
 * already active-articles-only and sorted stably on the backend
 * (`_console_catalogue`, `_plan_run`), so this only reshapes it for the menu.
 */
function consoleArticleOptions(catalogue: DemoConsoleCatalogueEntry[]): ConsoleArticleOption[] {
  return catalogue.map((entry) => ({ articleNo: entry.articleNo, label: entry.label }));
}

export function PlanningScreen({
  outline,
  jobGratingType: statedType,
  seed,
  caseId = null,
  versionNo = null,
  initialResult = null,
}: {
  outline: DemoOutline | null;
  /**
   * The type the job states, or `null` where it states none.
   *
   * `null` is the ordinary case on the hand-drawn way in: nothing was read, so
   * nobody said what kind of grating this is. The screen then asks, and asks
   * *before* the first element can be laid — see `typeMissing`.
   */
  jobGratingType: string | null;
  /** Placements to start from. Only the tests use it; the store is the source. */
  seed?: Placement[];
  /**
   * The Vorgang this board writes to, or `null` on the register demo's own
   * `/planung` — which stays exactly as it was (task 15): every gesture
   * still asks `planManual`/`fillWall`/`splitBlank` for the board's own
   * answer, but nothing is posted anywhere.
   */
  caseId?: string | null;
  /** The version this screen was opened on — the optimistic lock's starting
   * number. `null` wherever `caseId` is, for the same reason. */
  versionNo?: number | null;
  /**
   * What `GET /cases/{id}/plan` answered on the way in — the same shape
   * `planManual` returns, shown at once instead of waiting out the first
   * debounced round trip. The placements themselves are not in this answer
   * (design §5: they are `plan_version.planning_state`, not part of the run
   * shape) — the board still starts from what the store holds, `seed` in a
   * test and empty otherwise.
   */
  initialResult?: DemoRunResult | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /** What was picked here, where the job stated nothing. */
  const [pickedType, setPickedType] = useState<string | null>(null);
  const jobGratingType = statedType ?? pickedType;
  const history = usePlanning((state) => state.history);
  /** What Wiederholen would put back — read here so its button follows it. */
  const future = usePlanning((state) => state.future);
  const tool = usePlanning((state) => state.tool);
  const pickedStep = usePlanning((state) => state.step);
  const selected = usePlanning((state) => state.selected);
  const selectedWall = usePlanning((state) => state.selectedWall);
  const selectedFeature = usePlanning((state) => state.selectedFeature);
  const selectedCutout = usePlanning((state) => state.selectedCutout);
  const consoleSeries = usePlanning((state) => state.consoleSeries);
  const consoleAdjustableFirst = usePlanning((state) => state.consoleAdjustableFirst);
  const consoleOverrides = usePlanning((state) => state.consoleOverrides);
  /** The cutout half of the Vorgang's settings — posted with every version
   * and sent on every planning request, exactly as the bracket settings are. */
  const cutoutClearanceMm = usePlanning((state) => state.cutoutClearanceMm);
  const cutoutSurcharges = usePlanning((state) => state.cutoutSurcharges);
  /** The splitting rule „Wand belegen" lays (Fall C) — sent and posted like
   * the settings above, set by the „Aufteilung" select in step 2. */
  const splitPriority = usePlanning((state) => state.splitPriority);
  /** What part of the drawing is on screen — passed straight through to the
   * board (task 8, design 2026-09-10). `FIT_VIEW` until something sets it,
   * either the board's own zoom bar/wheel/pan or „Diesen Rost groß" below. */
  const view = usePlanning((state) => state.view);
  const store = usePlanning;

  /**
   * Whether the facade may still change, and on what terms (spec 2026-10-15
   * §4) — read off the Vorgang `CaseRoute` already loaded (same cache key).
   * „open" where nothing is loaded: the register demo has no Vorgang, and the
   * server is the gate that holds either way (409 from `sent` on).
   */
  const caseDetail = useCase(caseId);
  const editability = caseDetail.data ? facadeEditability(caseDetail.data.status) : "open";
  /** The release a measure gesture would end — asked for only where there is one. */
  const latestCalculation = useLatestCalculation(editability === "approved" ? caseId : null);
  const release = latestCalculation.data?.review ?? null;
  const approval =
    release !== null && release.kind !== "withdrawn" ? { by: release.by, at: release.at } : null;
  /**
   * The board's own first write on a released Vorgang asks first
   * (UX-Gutachten 23.10.2026, Nr. 5) — the question the measure, the form
   * and the type ask already (spec 2026-10-15 §4), in the same words
   * (`ConfirmReleaseLoss`). Asked where every gesture is written — the
   * settle below, not thirty handlers — so the gesture stands on the board
   * under the question, and „Abbrechen“ puts back the board the server has
   * (`writtenBoardRef`). `{ subtitle }` while it stands: what is about to
   * change, in the words the Stand's summary would have used.
   */
  const [releaseQuestion, setReleaseQuestion] = useState<{ subtitle: string } | null>(null);
  /** The same, for the settle's timer — nothing is written while it stands. */
  const releaseQuestionRef = useRef(false);
  /**
   * The release this board stands on is already spent: a Stand **landed**
   * on it — the board's own write, a measure, a change of type (`persist`,
   * `measureSaved`, `typeChanged`). The server took it with that write
   * (D-11); nothing is asked twice while the cached Vorgang still says
   * „Freigegeben“, and the measure field and the type select ask no more
   * either (review of d8516611, M-2). Never set on the yes alone: a write
   * that fails after it leaves the release standing (I-1). A new release —
   * the Vorgang read as released again — asks again.
   */
  const [releaseSpent, setReleaseSpent] = useState(false);
  /** The same, for the settle's timer. */
  const releaseSpentRef = useRef(false);
  /** Read by the settle's timer, which outlives the render it was set in. */
  const editabilityRef = useRef(editability);
  useEffect(() => {
    editabilityRef.current = editability;
    if (editability === "approved") releaseSpentRef.current = false;
  }, [editability]);
  // And the state with it, in the render that notices — no effect between.
  const [releaseSeenAs, setReleaseSeenAs] = useState(editability);
  if (releaseSeenAs !== editability) {
    setReleaseSeenAs(editability);
    if (editability === "approved") setReleaseSpent(false);
  }
  /** What a gesture's question is about on this board: released, and not spent here yet. */
  const releaseStands = editability === "approved" && !releaseSpent;
  /**
   * The settle waits for a reason to pass. Every write waits for one still on
   * its way — the board's own, a measure, a change of type — so no two go
   * against the same Stand (review of Task 13/14, I-3); the release question
   * also waits for a pointer still pressed (a drag paused under a still
   * hand) and for another modal question (review of d8516611, I-2/M-5). Set
   * where the settle stood down; the end of the reason settles again
   * (`rearmSettle`).
   */
  const settleDeferredRef = useRef(false);
  const rearmSettle = useCallback(() => {
    if (!settleDeferredRef.current) return;
    settleDeferredRef.current = false;
    setGestureTick((tick) => tick + 1);
  }, []);
  /**
   * How many measures and changes of type are out — the write and the plan
   * read after it (`measureWriting`, `typeWriting`). A count, not a flag
   * (re-review N-3): two of them overlapping must not let go at the first
   * one's end. The state is the same for the render (the board's hold).
   */
  const otherWritesRef = useRef(0);
  const [otherWrites, setOtherWrites] = useState(0);
  /** Whether a pointer is pressed anywhere — the board's drags, a press on a control. */
  const pointerDownRef = useRef(false);
  useEffect(() => {
    const down = () => {
      pointerDownRef.current = true;
    };
    const up = () => {
      if (!pointerDownRef.current) return;
      pointerDownRef.current = false;
      rearmSettle();
    };
    // A release the page never heard (outside the window, a lost capture):
    // a move with no button down says the press is over.
    const move = (event: PointerEvent) => {
      if (pointerDownRef.current && event.buttons === 0) up();
    };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("pointerup", up, true);
    document.addEventListener("pointercancel", up, true);
    document.addEventListener("pointermove", move, true);
    window.addEventListener("blur", up);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("pointerup", up, true);
      document.removeEventListener("pointercancel", up, true);
      document.removeEventListener("pointermove", move, true);
      window.removeEventListener("blur", up);
    };
  }, [rearmSettle]);
  // Another modal question closed: the board's own may ask now (M-5).
  // `Dialog` portals onto the body, so the body's children are watched.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!modalDialogOpen()) rearmSettle();
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [rearmSettle]);
  /**
   * The stage a new Stand moves (D-11, `workflow.append`): from „In
   * Kalkulation“, „Freigegeben“ and the first stages back to planning. Only
   * then does a write have news for the head, the register and the record.
   */
  const caseStatus = caseDetail.data?.status ?? null;
  /** The Vorgang's drawings, for the sketch beside the board (Task 18) — none where the Vorgang says none. */
  const caseDocuments = caseDetail.data?.documents ?? [];
  /**
   * The stored Vorgang carries a chamfer its form can no longer cut (design
   * 2026-10-18, Task 4 ruling 1 and its review, I-1): the replay answered on
   * the **uncut** ring with the laid elements moved back onto it, while the
   * stored planning state still counts the walls of the cut one. Nothing on
   * this board may then be planned again or written — a plan asked for from
   * that state against these walls would put every grating on the wrong wall,
   * and a Stand written from it would keep it there. The board shows the
   * replay as it is, and the way out is „Form ändern …“.
   */
  const notDrawable = notDrawableFinding(initialResult);
  const faseBlocked = notDrawable !== null;
  /**
   * The board takes no gesture (spec 2026-10-15 §4/§5, screen check V-1): a
   * cancelled Vorgang takes nothing but „Wiederherstellen“, and from „Angebot
   * raus“ on it is frozen — the server answers every write with 409, and the
   * board used to find that out only after the gesture. Reading stays:
   * picking a wall or an element, zoom, the lists, the way on to the
   * calculation and the documents. A chamfer that cannot be drawn freezes it
   * the same way (`faseBlocked`).
   */
  const readOnly = editability === "cancelled" || editability === "sent" || faseBlocked;

  /**
   * Opening the board puts a person down on its heading, as every screen of
   * the repository does (global constraint, UX ruling 12) — above all after
   * „Übernehmen" on `/umriss?case=…`, which left the focus on the page body
   * (UX gate round 1, wave B2 #2). Mount only: `CaseRoute` keeps this board
   * mounted while a plan is read again, and a new answer must not pull the
   * focus off the board.
   */
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** The head's select, for „… übernehmen“ beside what the mails name (Task 15a). */
  const typeHandleRef = useRef<JobGratingTypeHandle>(null);
  /**
   * „Wand N zeigen“ asked for that wall's „Maß“ field to take the focus once
   * its panel is drawn (review of d8516611, M-8). Checked after every render:
   * the wall may already be the one picked, and then nothing else changes.
   *
   * The request names its wall, and it is spent only by the render that has
   * that wall picked. A bare flag was spent by the first effect to see it —
   * and that need not be this pick's: React runs the passive effects an
   * earlier commit left waiting before it renders a click's pick, so with a
   * plan answer committed just before the click the flag was taken by the
   * answer's effect, which found no field yet, and the field then rendered
   * for nobody (CI run 40754100908). Such an effect stands aside; a pick of
   * another wall (or none) withdraws the request; and the render that draws
   * the pick spends it either way — a slant's reading, an object tool's form
   * or no Vorgang have no field to wait for, and a request left standing
   * would pull the focus in some later, unrelated moment.
   */
  const focusMeasureWallRef = useRef<number | null>(null);
  useEffect(() => {
    const wanted = focusMeasureWallRef.current;
    if (wanted === null) return;
    if (store.getState().selectedWall !== wanted) {
      focusMeasureWallRef.current = null;
      return;
    }
    if (selectedWall !== wanted) return;
    focusMeasureWallRef.current = null;
    focusMeasureField();
  });
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  /**
   * The sentence a navigation brought — „Umriss als Stand 1 von „…“
   * gespeichert." after „Übernehmen" on `/umriss?case=…` (round-2 screen
   * check, R2-6) — shown under the head until put away, and said once: the
   * live region below is there before it, and gets the sentence a moment
   * after mount, as on the record.
   */
  const arrived = useRouteNotice();
  const [arrivedShown, setArrivedShown] = useState(true);
  const [announcement, setAnnouncement] = useState("");
  /**
   * Says `text` in the live region even where it already stands there
   * (review of 89ca82a9, B-5): emptied first, set a moment later — a region
   * that does not change is not read again. Refs and a setter only, so the
   * editing callbacks need no dependency on it.
   */
  const sayAgainRef = useRef<number | null>(null);
  function sayAgain(text: string): void {
    setAnnouncement("");
    if (sayAgainRef.current !== null) window.clearTimeout(sayAgainRef.current);
    sayAgainRef.current = window.setTimeout(() => {
      sayAgainRef.current = null;
      setAnnouncement(text);
    }, 50);
  }
  useEffect(
    () => () => {
      if (sayAgainRef.current !== null) window.clearTimeout(sayAgainRef.current);
    },
    [],
  );
  useEffect(() => {
    if (arrived === null) return undefined;
    const timer = window.setTimeout(() => setAnnouncement(arrived), 50);
    return () => window.clearTimeout(timer);
  }, [arrived]);

  /**
   * Arrived from „Form ändern …“ (`?form=neu`, spec 2026-10-15 §3): the board
   * stands on the new form, empty, and says so until a grating lies or its ×
   * puts it away. Read once, on mount. The parameter leaves the address, so a
   * reload or „Zurück“ does not say it again, and the live region says it
   * once — both from a timer, as the record does with `?uebergeben=1`.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [formChanged, setFormChanged] = useState(
    () => searchParams.get(FORM_CHANGED_PARAM) === FORM_CHANGED_VALUE,
  );
  const formChangedOnArrival = useRef(formChanged);
  useEffect(() => {
    if (!formChangedOnArrival.current) return undefined;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(FORM_CHANGED_PARAM);
          return next;
        },
        { replace: true },
      );
      setAnnouncement(FORM_CHANGED);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [setSearchParams]);

  /** The optimistic lock's current number — starts at the prop, moves on
   * every accepted version. A ref because it must be current the instant a
   * gesture posts, never a render behind (design §5's idempotency rests on
   * sending the number this screen actually last saw). */
  const versionRef = useRef<number | null>(versionNo);
  /**
   * And it follows the prop from then on. A refetch of `useCase` — after a 409
   * above all, where the whole point is that the server is somewhere else —
   * hands down a new `latestVersionNo`, and a ref initialised once would post
   * against the number this screen was mounted with forever (review, Critical:
   * „409 … nie überschrieben").
   */
  useEffect(() => {
    // Only ever forward (UX-Gutachten Nr. 5's reads): a read of the Vorgang
    // that left before this board's write landed hands down the number before
    // it, and posting against that would come back 409 — „Jemand anders hat
    // diesen Vorgang inzwischen geändert“ about one's own gesture.
    if (versionNo === null || versionRef.current === null || versionNo > versionRef.current) {
      versionRef.current = versionNo;
    }
  }, [versionNo]);

  /**
   * Whether this board wrote a Stand, and whether it still stands. Every
   * write raises the Vorgang's number in the cache, but the plan answer
   * `CaseRoute` hydrates from stays the one read on arrival — and is fresh
   * for 15 s. Back on the board within them, the old gratings stood under the
   * new number and the next gesture wrote that old board as a new Stand
   * (review 23.10.2026, confirmed at the screen). So a board that wrote takes
   * the answer out of the cache once it is left — not while it stands, where
   * `CaseRoute` would take the board away with it — and so does a write
   * landing after it was left (`persist`). The next arrival reads it again,
   * the precedent being „Form ändern …“ (`OutlineDraftScreen`).
   */
  const wroteRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (wroteRef.current && caseId !== null) {
        queryClient.removeQueries({ queryKey: [...CASE_KEY(caseId), "plan"], exact: true });
      }
    };
  }, [caseId, queryClient]);

  /**
   * The board's save state, in its head (Welle 1, idea 23). Not a live
   * region: a save is heard from the toast that says it, a failure from the
   * message above the board — the head is where a person looks it up.
   * Follows the Vorgang's own number while nothing is under way, as the lock
   * above does; a write in flight or a failure keeps its word until it is
   * answered.
   */
  const [saveState, setSaveState] = useState<SaveState | null>(
    versionNo !== null ? { kind: "saved", versionNo } : null,
  );
  // Adjusted during the render that notices the new number, not in an
  // effect: nothing in between is painted, and the guard ends the loop.
  const [followedNo, setFollowedNo] = useState(versionNo);
  if (followedNo !== versionNo) {
    setFollowedNo(versionNo);
    if (versionNo !== null && (saveState === null || saveState.kind === "saved")) {
      setSaveState({ kind: "saved", versionNo });
    }
  }

  /**
   * The toast that says the last Stand (Welle 1, idea 23) — one at a time: a
   * board saves on every gesture, and a pile of „Gespeichert" notes would
   * say less than the newest one. A newer Stand sends the older note away.
   */
  const toast = useScreenToast();
  const savedToastRef = useRef<string | null>(null);
  const sayStand = useCallback(
    (shown: ToastInput) => {
      if (savedToastRef.current !== null) toast.dismiss(savedToastRef.current);
      savedToastRef.current = toast.show(shown);
    },
    [toast],
  );
  /**
   * A laying („Alle Wände belegen", „Wand belegen", „Wand neu aufteilen")
   * waiting for the Stand that writes it — the toast says both at once, with
   * its way back. `draft` is the board the laying left: Rückgängig takes
   * exactly that laying back, so it is offered only while that board is the
   * one that stands.
   */
  const pendingLaidRef = useRef<{ text: string; draft: PlanningDraft } | null>(null);
  /** The toast that offers that way back, while it is up. */
  const laidToastRef = useRef<{ id: string; draft: PlanningDraft } | null>(null);
  /** Always the newest „Rückgängig" — a toast calls it long after it was shown. */
  const undoRef = useRef<() => void>(() => {});

  /**
   * Says a laying, with „Rückgängig" while its board still stands. Called
   * where the laying was written as a Stand (the text carries the Stand), or
   * at once where nothing is written — the register demo.
   *
   * Under a modal dialog's question the laying is said without its way back:
   * its Stand landed after the dialog opened, and a „Rückgängig" there would
   * be refused (`takeBack`) — see the observer below.
   */
  const sayLaid = useCallback(
    (text: string, draft: PlanningDraft) => {
      if (modalDialogOpen()) {
        sayStand({ text });
        return;
      }
      if (savedToastRef.current !== null) toast.dismiss(savedToastRef.current);
      const id = toast.show({
        text,
        action: {
          label: "Rückgängig",
          onAction: () => {
            // Only the laying it names: anything since and it is not this
            // gesture's way back any more (the toast is gone by then too).
            if (store.getState().current() !== draft) return;
            undoRef.current();
          },
        },
      });
      savedToastRef.current = id;
      laidToastRef.current = { id, draft };
    },
    [toast, store, sayStand],
  );

  /**
   * The geometry ops of every gesture since the last accepted version, in the
   * order they were made — **accumulating**, not the last one.
   *
   * Several gestures inside one settle window coalesce into one version (a
   * mitre set and immediately re-typed, an object drawn and labelled), and
   * before fix round 1 the second overwrote the first: the board carried both,
   * the version carried one, and the difference only showed on the next
   * reload.
   */
  const pendingOpsRef = useRef<OpIn[]>([]);
  /**
   * The planning state the server holds, as far as this screen knows: what
   * the last accepted version carried, or what the last hydration put on the
   * board. A settle with no ops and a state equal to this has nothing to
   * write and does not post (walk B-8) — the server would only answer 400
   * „nichts zu speichern", and that answer used to cost a reload.
   */
  const lastSavedStateRef = useRef<PlanningStateWire | null>(null);
  /** The store's half of the same — what „Abbrechen“ under the release
   * question puts back ({@link WrittenBoard}). */
  const writtenBoardRef = useRef<WrittenBoard | null>(null);
  /** The German sentences those gestures announced, joined with „ · " when
   * several coalesce. The `changeSummary` is what a person reads in the
   * Vorgang's history, so it names everything that happened, not the last. */
  const pendingSummariesRef = useRef<string[]>([]);
  /**
   * Whether anything a *person* did is waiting to be written.
   *
   * The one condition on which a version is posted at all. Mounting,
   * hydrating, a refetch and a prop identity change all run the settle effect
   * below and none of them is a gesture — a version without a change is
   * refused by the server (400 „Weder eine Geste noch …"), which is exactly
   * what opening a Vorgang did until fix round 1.
   */
  const gesturePendingRef = useRef(false);
  /**
   * Counts announced gestures, so the settle window below starts on **every**
   * gesture and not only on one that changes the draft (fix round 1, task
   * 11). A hole's release repeats the last frame's figures — the draft does
   * not move, `request` does not change, and without this the op for the
   * settled position sat in `pendingOpsRef` with nothing to send it.
   */
  const [gestureTick, setGestureTick] = useState(0);
  /**
   * Why a pending gesture cannot be written at all — a corner or facade object
   * on a wall the answer carries no `wallId` for ({@link wallIdFor}).
   *
   * Not a failure to retry: there is no id to find later. The gesture stands on
   * the board and is said out loud instead of being posted under an invented
   * key, which would brick the case at the next replay.
   */
  const unwritableRef = useRef<string | null>(null);
  /** The same, on screen. */
  const [unwritable, setUnwritable] = useState<string | null>(null);
  /**
   * Whether this screen has asked for the Vorgang to be reloaded and is
   * waiting for it — after a 409, and after a refusal the server will never
   * accept.
   *
   * **Nothing is written while it stands.** The gap it closes is a real one
   * (fix round 2, item 1): the 409 branch takes the server's new version
   * number immediately, but the refetch it asks for lands a round trip later.
   * A gesture made in between would post the *pre-hydration* local state —
   * the board that lost the conflict — against the version number that won,
   * and the server would accept it. That is precisely the overwrite the
   * optimistic lock exists to prevent, and it would look like it worked.
   *
   * Gestures made in the gap stay pending and are discarded by the hydration,
   * which is what „die abgelehnte Geste ist vom Board verschwunden" means.
   */
  const awaitingHydrationRef = useRef(false);
  /**
   * Why the board's own gestures are back in the pending list after a
   * transport failure — and so wait for the next gesture's settle, with
   * nothing on its way until then. `null` once a gesture opens a new settle
   * window or a write lands (review, Welle 1 idea 23).
   */
  const unsentReasonRef = useRef<string | null>(null);
  /**
   * The head's word once a write has come back — with a Stand, or without
   * one that changed the board (a measure that was refused or failed). Saved
   * as `versionNo` only if nothing of the board's own waits: a gesture made
   * while the write was on the wire is „Speichert …" (its settle is coming),
   * a gesture put back after a failure is „Nicht gespeichert" (nothing is
   * coming until the next gesture). A reload on its way discards them both.
   */
  const settledSaveState = useCallback((versionNo: number): SaveState => {
    if (!gesturePendingRef.current || awaitingHydrationRef.current) return { kind: "saved", versionNo };
    const unsent = unsentReasonRef.current;
    return unsent !== null ? { kind: "unsaved", reason: unsent } : { kind: "saving" };
  }, []);
  /** Why the last write to the Vorgang did not land — a stale lock, worded
   * for a person rather than the exception's own name — and, where a measure
   * gesture was what did not land, which one (V-3): the reload closes its
   * field, and the typed figure must not vanish without a word. */
  const [staleMessage, setStaleMessage] = useState<{ text: string; lost: LostMeasure | null } | null>(null);
  /**
   * The messages above the board (V-3), and whether a reload just took the
   * focus away from under a person: a 409 on a measure closes the panel the
   * field stood in, and the focus fell to the page. The hydration that
   * follows hands it to the message that says why.
   */
  const messagesRef = useRef<HTMLDivElement>(null);
  const focusMessagesRef = useRef(false);
  /**
   * A change of the Vorgang's type that went through and waits for the plan
   * that carries it (Ruling G-3): the type, and the hydration it was made on.
   * The head's select shows it and takes no other choice until the next
   * hydration (`typeArriving` below, read off `hydratedAt` — no effect).
   */
  const [typeChange, setTypeChange] = useState<{ type: string; hydratedAt: number } | null>(null);
  /**
   * Why a change of the Vorgang's type was refused with a 409 — cancelled,
   * or the offer went out under the board. Said above the board and not
   * under the select: the head gives way to the reading once the Vorgang is
   * read again, and the select with it.
   */
  const [typeRefusal, setTypeRefusal] = useState<string | null>(null);
  /** Every other reason a write to the Vorgang failed — the network, the
   * server. Kept apart from `staleMessage`: one is answered by reloading,
   * the other only by trying again. */
  const [persistFailure, setPersistFailure] = useState<string | null>(null);
  /** A gesture a hydration threw away before it was written (re-review N-1) — until the next gesture. */
  const [lostGesture, setLostGesture] = useState(false);
  /** The board waits for the plan a change of type wrote (`typeChanged`) — a reload of its own, not a 409's. */
  const typeAwaitingRef = useRef(false);
  /**
   * Why the last write was refused outright and dropped — a verdict
   * (`permanentlyRefused`), not a transport failure. Kept apart from
   * `persistFailure` (S-8): that one promises a retry that will actually
   * happen; this one has already been discarded and the Vorgang reloaded,
   * and the two need different words or the refused case reads a promise
   * that is false — a 409 on a sent Vorgang, for one, is never retried.
   */
  const [refusedMessage, setRefusedMessage] = useState<string | null>(null);

  const [planned, setResult] = useState<DemoRunResult | null>(initialResult);
  /**
   * The way back out of `faseBlocked` (final review I-1): a corrected
   * measure closes the ring, and the next answer cuts the chamfer again.
   * `planned` was never touched while the board was blocked — it still holds
   * the answer the board opened on, the finding included — so the new answer
   * takes its place at once, before the board's own plan comes back.
   */
  const [blockedBefore, setBlockedBefore] = useState(faseBlocked);
  if (blockedBefore !== faseBlocked) {
    setBlockedBefore(faseBlocked);
    if (!faseBlocked) setResult(initialResult);
  }
  /** What the board shows. While a chamfer cannot be drawn (`faseBlocked`)
   * that is the replay's answer as `CaseRoute` hands it down, a newer one
   * included — no plan of the board's own replaces it. */
  const result = faseBlocked ? initialResult : planned;
  const [failure, setFailure] = useState<string | null>(null);
  const [filling, setFilling] = useState<number | "all" | null>(null);
  /** Walls the last whole-house covering left out — shown under „Zur Kenntnis". */
  const [skippedWalls, setSkippedWalls] = useState<number[]>([]);
  const [fillFailure, setFillFailure] = useState<string | null>(null);
  /**
   * Which wall the last „So aufteilen" left exactly as it found it, and at
   * which step of the draft — pinned like {@link relaid} beside it.
   *
   * §6.0 says it about „Wand neu aufteilen" and it is just as true here: a
   * command that quietly does nothing is the worse of the two evils, because
   * nobody notices. The button re-covers, `fill_wall` skips a stretch
   * `plan_run` escalates, and until 30.08.2026 the answer came back
   * unchanged with no word about it — the finding still standing, the button
   * still there, nothing to tell the two apart from a click that worked.
   */
  const [nothingLaid, setNothingLaid] = useState<{ wallIndex: number; step: number } | null>(
    null,
  );
  /**
   * Why the last „Rohling aufteilen" divided nothing, and at which step of the
   * draft — pinned like {@link nothingLaid} beside it.
   *
   * The sentence is worded by the endpoint and shown verbatim: the reason is
   * arithmetic the backend did and this file did not, and a second wording
   * here would be a second answer nobody could keep in step.
   */
  const [blankRefusal, setBlankRefusal] = useState<{
    placementId: string;
    message: string;
    step: number;
  } | null>(null);
  /**
   * What the last re-laying replaced, and at which step of the draft — shown
   * under „Zur Kenntnis" for exactly as long as that step is what stands.
   *
   * It used to be cleared only by the next `cover()`, so after Rückgängig it
   * went on asserting a replacement that had been undone (round 2 of the
   * walk, finding 4).
   */
  const [relaid, setRelaid] = useState<{
    wallIndex: number | null;
    count: number;
    handTyped: number;
    step: number;
  } | null>(null);
  const [hover, setHover] = useState<{ wallIndex: number; reason: WallRefusal } | null>(null);
  /**
   * Why the last gesture about a facade object did not happen — and at which
   * step of the draft it was said.
   *
   * A message about a state has to go when that state goes. The refusal was
   * cleared only by the next *successful* settle, so an early return („the
   * settled position is the one it already has") let it survive its own
   * gesture, and Rückgängig let it survive the document it was about (round 2
   * of the walk, finding 4). Pinning it to the step it belongs to says the
   * rule once, instead of scattering `setFeatureRefusal(null)` over every
   * call site that might invalidate it.
   */
  /** The downpipe just set, whose diameter choice is in hand (A5). Only
   * while its panel is the one open — a click elsewhere is „keep 120". */
  const [freshPipe, setFreshPipe] = useState<string | null>(null);
  if (freshPipe !== null && selectedFeature !== freshPipe) setFreshPipe(null);
  const [featureRefusal, setFeatureRefusal] = useState<{
    wallIndex: number;
    reason: FeatureRefusal;
    step: number;
  } | null>(null);
  /** Which element's length field is mid-edit, so „too small" can wait for blur. */
  const [lengthText, setLengthText] = useState<{ id: string; text: string } | null>(null);

  /**
   * What a new facade object is built with — the „Art" tool's own little
   * form, not the placement panel: nothing has been drawn yet, so there is
   * no object to select and edit (that editing is Task 14, E-47 follow-up).
   *
   * The type only prefills the three effect fields; once somebody adjusts one
   * by hand it stays adjusted until the type is changed again. `label` is
   * carried across a type change — a typed name is not a guess the type
   * makes, it is what somebody wrote.
   */
  const [featureType, setFeatureType] = useState<FeatureType>(FEATURE_TYPES[0]);
  const [featureCovered, setFeatureCovered] = useState(FEATURE_DEFAULTS[FEATURE_TYPES[0]].covered);
  const [featureWidthOffsetMm, setFeatureWidthOffsetMm] = useState(
    FEATURE_DEFAULTS[FEATURE_TYPES[0]].widthOffsetMm,
  );
  // What the field shows, kept apart from the committed number above for the
  // reason `lengthText` is kept apart from `placement.lengthMm` one page up:
  // „-" is not a number yet, and a field bound straight to the committed value
  // would erase the sign the moment somebody typed it (E-47 follow-up).
  const [featureWidthOffsetText, setFeatureWidthOffsetText] = useState(
    String(FEATURE_DEFAULTS[FEATURE_TYPES[0]].widthOffsetMm),
  );
  const [featureSplitsRun, setFeatureSplitsRun] = useState(FEATURE_DEFAULTS[FEATURE_TYPES[0]].splitsRun);
  // Whether the next object brings a linked hole with it (D-1: the hole is a
  // `Cutout` of its own, this is only the wish for one). Prefilled from the
  // type — a downpipe says yes — and read when the object is set.
  const [featureWithCutout, setFeatureWithCutout] = useState(
    FEATURE_DEFAULTS[FEATURE_TYPES[0]].withCutout,
  );
  const [featureLabel, setFeatureLabel] = useState("");
  /** Why the typed width offset was not taken, or `null`. */
  const [featureWidthRefusal, setFeatureWidthRefusal] = useState<string | null>(null);

  /**
   * Unticking „Rost liegt darauf" takes the width departure with it.
   *
   * Three layers gave three answers until 30.08.2026: `document.py` refuses a
   * non-zero `width_offset_mm` on `covered = false` outright („ohne Rost
   * ändert sich keine Breite"), `demo.py` coerces it to `0` on the way in, and
   * this screen kept it — so a draft in `localStorage` held exactly the state
   * the model calls a lie about itself, waiting for the first `SetFeature`
   * that would meet it. The browser now says what the model says.
   *
   * **Re-ticking does not bring the old number back**, and that is the
   * decision rather than an oversight: a figure that reappears without
   * anybody typing it is the silent guess {@link FEATURE_DEFAULTS} refuses to
   * make („it starts at 0 for every type"). What is shown after re-ticking is
   * `0`; whoever meant −55 types it again, where the refusal check can see it.
   */
  function chooseFeatureCovered(covered: boolean): void {
    setFeatureCovered(covered);
    if (covered) return;
    setFeatureWidthOffsetMm(0);
    setFeatureWidthOffsetText("0");
    setFeatureWidthRefusal(null);
    // And the wish for a hole goes with the grating, for the same reason and
    // by the same rule the document keeps: „ohne Blech kein Loch".
    setFeatureWithCutout(false);
  }

  function chooseFeatureType(next: FeatureType): void {
    setFeatureType(next);
    const defaults = FEATURE_DEFAULTS[next];
    setFeatureCovered(defaults.covered);
    setFeatureWidthOffsetMm(defaults.widthOffsetMm);
    setFeatureWidthOffsetText(String(defaults.widthOffsetMm));
    setFeatureWidthRefusal(null);
    setFeatureSplitsRun(defaults.splitsRun);
    setFeatureWithCutout(defaults.withCutout);
  }

  /** Both signs are valid (§Task 12) — negative is the common case, a sill
   * set back from the facade, but a departure the other way is just as real
   * a measurement. Only a complete number commits; „-" and „" stay on
   * screen as what they are, typing in progress. */
  function typeFeatureWidthOffset(raw: string): void {
    setFeatureWidthOffsetText(raw);
    if (raw.trim() === "" || raw.trim() === "-") {
      setFeatureWidthRefusal(null);
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next)) return;
    const refusal = widthOffsetRefusal(jobSpec?.widthMm ?? null, next);
    setFeatureWidthRefusal(refusal);
    // Refused means not committed: the number stays on screen as typed — it
    // is what somebody wrote — but nothing is built with it.
    if (refusal === null) setFeatureWidthOffsetMm(next);
  }

  /**
   * Leaving the field with an incomplete or unreadable text — „-", „", „abc"
   * — left the display and the committed number disagreeing about what the
   * next object would carry (review round 1, 30.08.2026, minor 3): type
   * „-5", delete the „5", drag, and the object got -5 while the field showed
   * „-". The field is not the source of truth, the committed number is —
   * this writes it back so what is shown is always what gets built.
   */
  function finishFeatureWidthOffset(): void {
    setFeatureWidthOffsetText(String(featureWidthOffsetMm));
    setFeatureWidthRefusal(null);
  }

  /**
   * What stands now — memoised, and that is load-bearing rather than tidy.
   *
   * `history.at(-1) ?? []` hands back a **fresh** empty array on every render
   * while nothing is placed. `request` below is keyed on it, the effect is
   * keyed on `request`, and the answer sets state — so an empty board asked
   * the backend for a plan, re-rendered on the answer, and asked again,
   * forever. The identity is the fix; `EMPTY` is one array for all of them.
   */
  const draft = useMemo(() => history.at(-1) ?? EMPTY_DRAFT, [history]);
  /**
   * Where a chamfer cannot be drawn (`faseBlocked`), the elements stand where
   * the server put them on the uncut ring — its answer, not the stored state,
   * whose wall numbers belong to the cut ring and would put each grating on
   * the wrong wall here. Read-only either way: nothing is planned or written
   * from these.
   */
  const answered = useMemo(
    () => (faseBlocked ? placementsOfAnswer(initialResult, statedType) : null),
    [faseBlocked, initialResult, statedType],
  );
  const placements = answered ?? draft.placements;
  const corners = draft.corners;
  // Not yet edited on this screen — carried through unchanged so every push
  // below stays a `PlanningDraft`, the same way `corners` had to be carried
  // when nothing here touched it either.
  const features = draft.features;
  /** The holes in the grating — one stack with `features` (design §5.1). */
  const cutouts = draft.cutouts;

  // A laying's toast goes the moment its board does — a gesture since, the
  // board's own Rückgängig, a reload: its „Rückgängig" would take back
  // something other than what it names. And when the Vorgang is frozen under
  // the board (storniert, Angebot raus — review): the board's own Rückgängig
  // goes grey then, and the toast's must not walk back a frozen facade.
  useEffect(() => {
    const laid = laidToastRef.current;
    if (laid === null || (laid.draft === draft && !readOnly)) return;
    toast.dismiss(laid.id);
    laidToastRef.current = null;
  }, [draft, readOnly, toast]);
  // And when a modal dialog opens over the board — the shell's „?"
  // overview, „Form ändern …", „Freigabe wird ungültig": its question is
  // about the board as it stands, and the toast host sits above its
  // backdrop on purpose. A way back pressable there is refused (`takeBack`)
  // and goes with nothing taken back, which reads as done. The board's own
  // Rückgängig is there again once the dialog closes. `Dialog` portals
  // straight onto the body, so the body's own children are all that is
  // watched — not the board's every redraw.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const laid = laidToastRef.current;
      if (laid === null || !modalDialogOpen()) return;
      toast.dismiss(laid.id);
      laidToastRef.current = null;
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [toast]);
  // And with the board itself: the shell's toasts outlive this screen, and a
  // way back pressed on the calculation would walk back a draft that nothing
  // writes any more — the board and the server would part.
  useEffect(() => {
    const holder = laidToastRef;
    return () => {
      const laid = holder.current;
      if (laid !== null) toast.dismiss(laid.id);
    };
  }, [toast]);

  useEffect(() => {
    if (seed) {
      store.getState().push({ placements: seed, corners: [], features: [], cutouts: [] });
    }
    // Seeding is a test affordance. One shot, on entering the screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Ask for the Vorgang again — both the plan (the board) and the case (its
   * `latestVersionNo`). The answer arrives through `CaseRoute`, which hydrates
   * the store from it; this screen only asks. */
  const reload = useCallback(() => {
    if (caseId === null) return;
    // Asked before the refetch goes out: after a refused write the answer is
    // the one already cached, and only this makes it replace the board
    // (`hydrationAskedAt`, V-2) — without it the refused gesture stayed on
    // screen and the board waited for a hydration that never came.
    store.getState().askHydration();
    void queryClient.invalidateQueries({ queryKey: ["case", caseId, "plan"] });
    void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
  }, [caseId, queryClient, store]);

  /**
   * Everything a person did since the last accepted version, on its way to
   * `POST /cases/{id}/versions` as one — a no-op where there is no Vorgang to
   * write to (the register demo's own `/planung`).
   *
   * `ops` is `[]` for a grating/console gesture (design §4.1: the whole
   * statement is the new `planningState`) and carries the `SetCorner`/
   * `SetFeature`/`RemoveFeature` of every outline gesture that has come in
   * since. Both kinds carry the *current* `planningState` regardless — a stand
   * is the board as it stands, not a diff.
   *
   * The pending list is emptied **before** the request goes out and put back
   * in front of anything newer if it fails, so a gesture made while the write
   * was in flight is neither lost nor sent twice, and a failure retries on the
   * next settle rather than disappearing.
   */
  /**
   * A 409 „Stand veraltet": take the server's number, write nothing until the
   * reload lands, say so, reload. The board's own gestures and a measure
   * gesture (`WallMeasureField.onStale`) land here alike — one handling for
   * one conflict.
   */
  const acceptStale = useCallback(
    (cause: StaleVersionError, lost: LostMeasure | null = null) => {
      versionRef.current = cause.latestVersionNo;
      awaitingHydrationRef.current = true;
      focusMessagesRef.current = true;
      setStaleMessage({
        text: `${cause.message} Jemand anders hat diesen Vorgang inzwischen geändert — der Stand wird neu geladen.`,
        lost,
      });
      // The persistent region says it: the notice mounts already full, and a
      // region that arrives full is not reliably read out.
      setAnnouncement("Nicht gespeichert — der Stand war veraltet und wird neu geladen.");
      setSaveState({ kind: "unsaved", reason: "Stand veraltet" });
      reload();
    },
    [reload],
  );

  /**
   * The board as a Stand states it (`plan_version.planning_state`) — what
   * every version carries, a gesture of the board's own (`persist`) and a
   * measure gesture (`WallMeasureField`) alike. One object, so the two cannot
   * state the board two ways.
   */
  const boardState: PlanningStateWire = useMemo(
    () => ({
      placements: placements.map(toWire),
      consoleSeries,
      consoleAdjustableFirst,
      consoleOverrides,
      cutoutClearanceMm,
      // The per-case rate (D-6) is no source any more: the cut price comes
      // from the supplier's `ausschnitt` rules in Max (E-51/D-8,
      // `pricing/cutouts.py`, 15.10.2026). The wire keeps both fields, and
      // every new version states them as „not named" — a rate stored before
      // must not keep pricing holes on the board through a replay.
      cutoutRatePerMNet: null,
      cutoutBaseNet: null,
      cutoutSurcharges,
      splitPriority,
    }),
    [
      placements,
      consoleSeries,
      consoleAdjustableFirst,
      consoleOverrides,
      cutoutClearanceMm,
      cutoutSurcharges,
      splitPriority,
    ],
  );

  /** The board as the settle's timer reads it — whether a gesture would write anything at all. */
  const boardStateRef = useRef(boardState);
  useEffect(() => {
    boardStateRef.current = boardState;
  }, [boardState]);

  const persist = useCallback(async () => {
    if (caseId === null || versionRef.current === null) return;
    // The store as this write states it — what „Abbrechen“ puts back later.
    const written = writtenBoardOf(store.getState());
    const ops = pendingOpsRef.current;
    const summaries = pendingSummariesRef.current;
    // A laying in this write is said with its Stand — taken with the ops, so
    // a write already on the wire never claims a laying it does not carry.
    const laid = pendingLaidRef.current;
    pendingOpsRef.current = [];
    pendingSummariesRef.current = [];
    pendingLaidRef.current = null;
    gesturePendingRef.current = false;
    const planningState = boardState;
    // **A post without content does not happen** (walk B-8). „Wand neu
    // aufteilen" with the same result, a drag released where it began: a
    // gesture, so the window opened — but nothing the log does not hold.
    // The window simply closes; the server's own 400 for the same case
    // stays below as the second net.
    if (ops.length === 0 && sameJson(planningState, lastSavedStateRef.current)) {
      setSaveState({ kind: "saved", versionNo: versionRef.current });
      return;
    }
    try {
      const { versionNo: nextVersionNo } = await postVersion(caseId, {
        expectedVersionNo: versionRef.current,
        ops,
        planningState,
        changeSummary: summaries.length > 0 ? summaries.join(" · ") : "Planung geändert",
      });
      versionRef.current = nextVersionNo;
      unsentReasonRef.current = null;
      // Saved — unless a gesture made while this was on the wire waits for
      // its own settle: then the board is not saved yet, whatever landed.
      setSaveState(settledSaveState(nextVersionNo));
      // Said once, by the toast host — nothing else on the board says a save
      // (Welle 1, idea 23). A laying whose board still stands brings its
      // way back with it; a gesture since, and the Stand says only itself.
      if (laid !== null && store.getState().current() === laid.draft) {
        sayLaid(`${laid.text} · ${savedSentence(nextVersionNo)}`, laid.draft);
      } else {
        sayStand({ text: savedSentence(nextVersionNo) });
      }
      // The Vorgang's own record carries the same number, and the
      // calculation screen posts against it. Left at the Stand this board
      // opened on, „Alle Wände belegen" → „Weiter zur Kalkulation" →
      // „Kalkulation starten" answered 409 „Stand veraltet" on the first
      // press and showed nothing (Generalprobe 15.10.2026). Only ever
      // forward: an older answer never lowers the number.
      queryClient.setQueryData<CaseDetailOut>(CASE_KEY(caseId), (old) =>
        old && old.latestVersionNo < nextVersionNo
          ? { ...old, latestVersionNo: nextVersionNo }
          : old,
      );
      writtenBoardRef.current = written;
      // Landed: a release this board stood on is gone with it (I-1).
      if (editabilityRef.current === "approved") {
        releaseSpentRef.current = true;
        setReleaseSpent(true);
      }
      // A Stand moves the stage (D-11): „Freigegeben“ or „In Kalkulation“ is
      // „In Planung“ again, and the head, the register's rows and the record
      // said otherwise (UX-Gutachten Nr. 5). Everything under the Vorgang is
      // read again **but its plan** — a plan read again would be hydrated
      // over a gesture made since.
      if (caseStatus !== null && caseStatus !== "planning_in_progress") {
        void queryClient.invalidateQueries({
          queryKey: CASE_KEY(caseId),
          predicate: (query) => query.queryKey[2] !== "plan",
        });
        void queryClient.invalidateQueries({ queryKey: CASES_KEY });
      }
      // The cached plan is older than this Stand now (`wroteRef`).
      wroteRef.current = true;
      if (!mountedRef.current) {
        queryClient.removeQueries({ queryKey: [...CASE_KEY(caseId), "plan"], exact: true });
      }
      lastSavedStateRef.current = planningState;
      setPersistFailure(null);
      setRefusedMessage(null);
      setTypeRefusal(null);
    } catch (cause: unknown) {
      if (nothingToSave(cause)) {
        // The server holds exactly this state already — the comparison
        // above missed it (a key it normalises), and the verdict is the
        // same: nothing to do. Pending is already empty; no reload, no
        // notice, and the next gesture writes as usual.
        lastSavedStateRef.current = planningState;
        setPersistFailure(null);
        setRefusedMessage(null);
        unsentReasonRef.current = null;
        if (versionRef.current !== null) setSaveState(settledSaveState(versionRef.current));
        return;
      }
      if (cause instanceof StaleVersionError) {
        // Somebody else's gesture landed first, so this one is **gone** — it
        // was made against a board that no longer exists. Nothing is put back
        // and nothing is retried: the plan is refetched, the store is hydrated
        // from it, and what the person sees is the new truth (design §9,
        // review ruling 2). Retrying here would overwrite their colleague.
        acceptStale(cause);
        return;
      }
      if (permanentlyRefused(cause)) {
        // The server will never take this write: a malformed op, a state it
        // refuses, a wall the document does not have. Retrying it would fail
        // in exactly the same way on every settle from here on — and, worse,
        // would carry the poisoned op along with every *later* gesture, so
        // nothing would ever be written again (fix round 2, item 2).
        //
        // So it is dropped, said out loud, and the Vorgang is reloaded: the
        // board then shows what the log actually holds instead of a change
        // the server refused.
        awaitingHydrationRef.current = true;
        focusMessagesRef.current = true;
        setRefusedMessage(cause.userMessage);
        setSaveState({ kind: "unsaved", reason: "vom Server abgelehnt" });
        reload();
        return;
      }
      // A transport failure — the network, not a verdict. Worth another go,
      // in front of whatever arrived while this was in flight, so the version
      // order stays the gesture order.
      pendingOpsRef.current = [...ops, ...pendingOpsRef.current];
      pendingSummariesRef.current = [...summaries, ...pendingSummariesRef.current];
      gesturePendingRef.current = true;
      // Nothing retries it until the next gesture opens a settle window.
      const reason = unsavedReason(cause);
      unsentReasonRef.current = reason;
      setSaveState({ kind: "unsaved", reason });
      setPersistFailure(
        // `ProblemError.message` is the *title* („Serverfehler"); the sentence
        // a person can act on is the detail underneath it.
        cause instanceof ProblemError
          ? cause.userMessage
          : cause instanceof Error
            ? cause.message
            : String(cause),
      );
    }
  }, [caseId, caseStatus, boardState, reload, queryClient, acceptStale, store, sayLaid, sayStand, settledSaveState]);

  /**
   * Note that a person changed something, in their words and — for an outline
   * gesture — as ops. Called by a handler right before it changes the draft.
   *
   * Accumulating, never overwriting: within one settle window several handlers
   * may run, and the version that goes out has to carry all of them.
   *
   * A `null` op is a wall with no document id ({@link wallIdFor}). It is
   * recorded as a reason not to write at all rather than dropped, because a
   * version carrying the other half of a gesture is a version that disagrees
   * with the board.
   */
  function announce(summary: string, ops: (OpIn | null)[] = []): void {
    // Held (a measure or a change of type is being saved): the store takes
    // no writing mutation now, so neither is anything noted to be written.
    // The head says why; a screen reader hears it with the press.
    // The module's store, not the render's `store`: the same object, but a
    // hook dependency nobody needs (the editing callbacks call this).
    if (usePlanning.getState().held) {
      // `sayAgain`, spelled out: refs and a setter only, so the editing
      // callbacks that call this need no dependency on it.
      setAnnouncement("");
      if (sayAgainRef.current !== null) window.clearTimeout(sayAgainRef.current);
      sayAgainRef.current = window.setTimeout(() => {
        sayAgainRef.current = null;
        setAnnouncement(WRITE_WAIT);
      }, 50);
      return;
    }
    setLostGesture(false);
    gesturePendingRef.current = true;
    setGestureTick((tick) => tick + 1);
    // A person did something, so the last conflict is no longer the news on
    // this screen. Cleared here and not on the next successful write: a
    // message that disappears because a *write* succeeded says the conflict
    // resolved itself, which it did not (fix round 1, item 12).
    //
    // **Except while the reload is still on its way** (fix round 2, item 1):
    // the board on screen is still the one that lost, so a gesture made now is
    // a gesture on stale ground and about to be discarded. Taking the notice
    // down at that moment would say the conflict is over exactly when it is
    // least over. It goes on the person's next gesture *after* the reload.
    if (!awaitingHydrationRef.current) setStaleMessage(null);
    // On its way from now on — the head says so at once, not only when the
    // settle window has passed. Not while a reload is coming: this gesture
    // is about to be discarded, and the head keeps saying why. Only with a
    // Stand to write against (the register demo has none and shows no head).
    if (versionRef.current !== null && !awaitingHydrationRef.current) {
      setSaveState({ kind: "saving" });
      // Whatever a failure put back goes out with this gesture's settle.
      unsentReasonRef.current = null;
    }
    if (!pendingSummariesRef.current.includes(summary)) {
      pendingSummariesRef.current.push(summary);
    }
    // A laying that answers under the open question is part of what it asks
    // about — its subtitle names it too (M-4).
    if (releaseQuestionRef.current) {
      setReleaseQuestion({ subtitle: pendingSummariesRef.current.join(" · ") });
    }
    for (const op of ops) {
      if (op === null) {
        unwritableRef.current =
          "Diese Wand trägt keine Kennung im gespeicherten Umriss — die Geste steht auf dem Board, wurde aber nicht in den Vorgang geschrieben.";
        continue;
      }
      // The last statement about a hole in one settle window is the
      // statement: a drag opens with one `set_cutout` and settles with
      // another, and a version carrying both would carry twenty on a slow
      // hand. The op for the final position replaces the one that opened.
      if (op.op === "set_cutout") {
        pendingOpsRef.current = pendingOpsRef.current.filter(
          (pending) => !(pending.op === "set_cutout" && pending.id === op.id),
        );
      }
      pendingOpsRef.current.push(op);
    }
  }

  const specs: GratingSpec[] = useMemo(
    () => (result?.gratingSpecs as GratingSpec[] | undefined) ?? [],
    [result],
  );
  const minGapMm = result?.minGapMm ?? 120;
  const jobSpec = specs.find((spec) => spec.type === jobGratingType) ?? null;

  /**
   * How long a new placement is, per tool — and only grating gets 1.000 mm.
   *
   * The first version handed the standard length to every tool, so a new
   * interruption arrived 1.000 mm wide: **a plausible door width that nobody
   * stated.** That is the class of number A-014 is about — worse than an
   * obviously wrong one, because it looks like an answer.
   *
   * A console has no length along the wall at all; it gets one grid step so
   * it has somewhere to sit. A facade object is not sized off this figure at
   * all — it is drawn to the extent of the drag that made it (§Task 13,
   * 30.08.2026), the same way `minGapMm` used to bound the old interruption
   * tool this replaced.
   */
  const newLengthMm = tool === "bracket" ? 10 : (jobSpec?.standardLengthMm ?? 1000);

  // ── Asking for a plan ──────────────────────────────────────────────────
  //
  // Debounced, and every change goes through it: the board shows the placement
  // at once, and consoles, quantities and findings come back from the answer.
  // Nothing in this file works out a domain figure for itself.
  /**
   * The gratings the replay held off the plan because their wall's figure is
   * in question (E-64) — left out of every plan asked here, as the server
   * left them out of its own: the planner refuses a grating on a wall without
   * a figure (422). The store keeps them, so every Stand still states them.
   */
  const heldOff = useMemo(() => heldOffPlacementIds(initialResult), [initialResult]);
  const request = useMemo(() => {
    if (!outline) return null;
    const request: PlanInput = {
      outline: {
        vertices: outline.nodes.map((node) => ({ xMm: node.xMm, yMm: node.yMm })),
        closed: outline.closed,
        walls: outline.segments.map((segment) => ({
          lengthMm: segment.lengthMm,
          lengthSource: segment.lengthSource ?? ("read" as const),
          label: segment.label || null,
          // A chamfer's slant is the one wall that runs along neither axis,
          // and the planner takes it only marked as one — unmarked, every
          // request of a chamfered board is a 422 („läuft schräg“). The
          // customer's measure travels back beside the usable stretch the
          // planner counts (design 2026-10-18 §6.2).
          kind: segment.kind ?? ("wall" as const),
          cornerPointLengthMm: segment.cornerPointLengthMm ?? null,
        })),
      },
      gratingType: jobGratingType,
      placements: placements.filter((placement) => !heldOff.has(placement.id)).map(toWire),
      // The two settings and the per-bracket corrections travel on every
      // request, never only when they differ from the backend's own
      // default — task 11's whole point is that they are decided here, not
      // rediscovered there.
      consoleSeries,
      consoleAdjustableFirst,
      consoleOverrides,
      splitPriority,
    };
    // Attached rather than spread into the literal above so the outline keeps
    // one shape whether or not anything is mitred — an empty list and no list
    // mean the same thing to the endpoint, and the wire says the same.
    if (corners.length > 0) request.outline.corners = corners;
    // The objects standing on the walls, the same treatment `corners` gets
    // one line up — without this the board could draw a narrowed zone but
    // the endpoint would never see what narrowed it (E-47 follow-up).
    if (features.length > 0) request.outline.features = features;
    // And the holes, the same way (design §5.1). The typed amounts ride
    // along on every request like the bracket settings do. The per-case
    // rate is `null` on purpose — a statement, not an omission: the price of
    // a hole comes from the supplier's rules and stands in the calculation
    // (D-8), so the board computes no amount of its own.
    if (cutouts.length > 0) request.outline.cutouts = cutouts;
    request.cutoutRatePerMNet = null;
    request.cutoutBaseNet = null;
    request.cutoutSurcharges = cutoutSurcharges;
    return request;
  }, [
    outline,
    jobGratingType,
    placements,
    heldOff,
    corners,
    features,
    cutouts,
    consoleSeries,
    consoleAdjustableFirst,
    consoleOverrides,
    cutoutSurcharges,
    splitPriority,
  ]);

  /**
   * The board's own write while it is on the wire, or `null` — so a measure
   * gesture can wait for it instead of racing it for the same version number
   * (`settleWrites`).
   */
  const writingRef = useRef<Promise<void> | null>(null);
  // Refs and a state setter only, so one function for the whole mount — the
  // settle's timer and `settleWrites` call it without depending on a render.
  const trackWrite = useCallback((write: Promise<void>): Promise<void> => {
    const tracked: Promise<void> = write.finally(() => {
      if (writingRef.current === tracked) writingRef.current = null;
      // A question that waited for this write may ask now — or, where it
      // landed on a release, never (`rearmSettle`, spelled out for the
      // same reason).
      if (settleDeferredRef.current) {
        settleDeferredRef.current = false;
        setGestureTick((tick) => tick + 1);
      }
    });
    writingRef.current = tracked;
    return tracked;
  }, []);

  /**
   * What a measure gesture (`WallMeasureField.settle`) posts against.
   *
   * A length is its own Stand, made by its own field — and the board may
   * still hold a gesture of its own that has not gone out (the settle window)
   * or is on the wire. Posting past either would win the version number from
   * a colleague who is in fact oneself: the board's write would come back
   * 409, its gesture lost, under „Jemand anders hat diesen Vorgang
   * inzwischen geändert". So the board's write goes first, and the measure
   * takes the number it left. `null` where a reload is on its way — nothing
   * may be written on ground about to be replaced.
   */
  const settleWrites = useCallback(async (): Promise<number | null> => {
    // Called by a measure and by a change of type, each after its own
    // question: the board's pending gesture goes out with it (`persist`
    // never asks — only the settle's timer does). Whether the release is
    // spent is decided where a write lands, not here (review, I-1).
    if (writingRef.current !== null) await writingRef.current;
    if (
      gesturePendingRef.current &&
      !awaitingHydrationRef.current &&
      unwritableRef.current === null
    ) {
      // Tracked like every write of the board's own (second review, N-1): a
      // gesture made while this one is out waits for it in the settle — and
      // it is the write that may spend the release — instead of asking a
      // question this write is about to make moot.
      await trackWrite(persistRef.current());
    }
    return awaitingHydrationRef.current ? null : versionRef.current;
  }, [trackWrite]);

  /** Always the newest `persist` — so the effect below can call it without
   * depending on it directly. Depending on it would defeat the debounce it
   * rides on: `persist`'s own identity changes with every placement. */
  const persistRef = useRef(persist);
  useEffect(() => {
    persistRef.current = persist;
  }, [persist]);

  /**
   * A Vorgang put on the board by {@link CaseRoute} is not a gesture — and
   * neither is anything that was pending when it landed.
   *
   * A hydration replaces the board with the server's own state, which is by
   * definition already written. What was queued before it was queued against a
   * board that no longer exists; after a 409 it is the *rejected* gesture, and
   * writing it now would be the overwrite the conflict exists to prevent.
   */
  const hydratedAt = usePlanning((state) => state.hydratedAt);
  /** The type a change waits with — until the board has hydrated since it went through. */
  const typeArriving = typeChange !== null && typeChange.hydratedAt === hydratedAt ? typeChange.type : null;

  /** One measure or change of type more, or one fewer, on its way — and a settle that waited for it goes. */
  function countOtherWrite(writing: boolean): void {
    otherWritesRef.current = Math.max(0, otherWritesRef.current + (writing ? 1 : -1));
    setOtherWrites(otherWritesRef.current);
    if (!writing) rearmSettle();
  }
  /**
   * The board takes no writing gesture while a measure or a change of type is
   * saved and its plan read again (re-review of 41503767, N-1, controller's
   * ruling): the hydration that answers it puts the server's board on, and a
   * gesture made meanwhile — held back from writing (I-3) — would be gone
   * without a word. Said, not silent (`WRITE_WAIT`); and bounded
   * (`WRITE_HOLD_MS`, N-2): a reading that does not answer must not hold the
   * board for good. A gesture made after the bound that a late hydration
   * still throws away is named (`lostGesture`).
   */
  const holdActive = otherWrites > 0 || typeArriving !== null;
  const [holdExpired, setHoldExpired] = useState(false);
  const [holdSeen, setHoldSeen] = useState(holdActive);
  if (holdSeen !== holdActive) {
    setHoldSeen(holdActive);
    if (!holdActive) setHoldExpired(false);
  }
  useEffect(() => {
    if (!holdActive) return undefined;
    const timer = window.setTimeout(() => setHoldExpired(true), WRITE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [holdActive]);
  const gesturesHeld = holdActive && !holdExpired;
  useEffect(() => {
    store.getState().setHeld(gesturesHeld);
  }, [gesturesHeld, store]);
  useEffect(() => () => store.getState().setHeld(false), [store]);
  useEffect(() => {
    // A person's gesture still waiting to be written goes with this
    // hydration — named, never silent (re-review N-1): after a 409 the
    // notice says it already (`awaitingHydrationRef`), otherwise nothing did.
    // The plan a change of type waits for counts too (B-1): its reload is no
    // 409, and nothing else says a gesture went with it.
    if (gesturePendingRef.current && (!awaitingHydrationRef.current || typeAwaitingRef.current)) {
      setLostGesture(true);
      sayAgain(LOST_GESTURE);
    }
    typeAwaitingRef.current = false;
    // A laying still on its way was computed against the board this replaces
    // — dropped when it answers (B-2).
    layingTicketRef.current += 1;
    pendingOpsRef.current = [];
    pendingSummariesRef.current = [];
    pendingLaidRef.current = null;
    gesturePendingRef.current = false;
    unwritableRef.current = null;
    unsentReasonRef.current = null;
    // The reload this screen asked for has landed. Writing is open again —
    // against the board the server actually holds.
    awaitingHydrationRef.current = false;
    // And what stands on the board now is the server's own Stand: saved, by
    // definition. Why the last gesture did not land stays in the messages.
    const standing = versionRef.current;
    if (standing !== null) setSaveState({ kind: "saved", versionNo: standing });
    // And if it took the focus with the panel it closed, the message that
    // says why takes it (V-3). Only then: a person working elsewhere on the
    // page keeps their place.
    if (focusMessagesRef.current) {
      focusMessagesRef.current = false;
      const lost = document.activeElement === null || document.activeElement === document.body;
      if (lost) messagesRef.current?.focus();
    }
    // And what it holds is, by definition, written: a later settle that
    // finds the board still equal to this has nothing to post (walk B-8).
    // Read off the store, not the render, so this effect keys on the
    // hydration alone.
    const snapshot = store.getState();
    writtenBoardRef.current = writtenBoardOf(snapshot);
    lastSavedStateRef.current = {
      placements: snapshot.current().placements.map(toWire),
      consoleSeries: snapshot.consoleSeries,
      consoleAdjustableFirst: snapshot.consoleAdjustableFirst,
      consoleOverrides: snapshot.consoleOverrides,
      cutoutClearanceMm: snapshot.cutoutClearanceMm,
      cutoutRatePerMNet: snapshot.cutoutRatePerMNet,
      cutoutBaseNet: snapshot.cutoutBaseNet,
      cutoutSurcharges: snapshot.cutoutSurcharges,
      splitPriority: snapshot.splitPriority,
    };
  }, [hydratedAt, store]);

  /**
   * The document wall a blocked board's measure was written on (final review
   * I-1, `measureSaved`). The answer that closes the ring cuts its chamfers
   * again and the numbering moves, so the pick kept through that hydration
   * (`holdPick`, by index) is found again by the wall's id.
   */
  const heldWallIdRef = useRef<string | null>(null);
  useEffect(() => {
    const wallId = heldWallIdRef.current;
    if (wallId === null) return;
    heldWallIdRef.current = null;
    const index = outline?.segments.findIndex((segment) => segment.wallId === wallId) ?? -1;
    if (index >= 0 && index !== store.getState().selectedWall) store.getState().selectWall(index);
    // Keyed on the hydration alone: the outline handed down with it is the
    // new one by then, and an outline change on its own is no pick to follow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedAt]);

  const pending = useRef(0);
  useEffect(() => {
    // A chamfer that cannot be drawn: the replay's answer is the plan, and
    // the only one there may be (`faseBlocked`) — a plan asked for here would
    // be built from the stored state against the uncut walls and replace the
    // replay's findings with its own.
    if (!request || faseBlocked) return;
    const key = JSON.stringify(request);
    // „Abbrechen“ put the board back (`keepRelease`): its own answer goes
    // back with it at once — not the discarded gesture's, until a re-plan
    // lands, and for good where that re-plan fails (UX-Zweitgutachten, N2).
    if (restoreAnswerRef.current) {
      restoreAnswerRef.current = false;
      const known = answersRef.current.get(key);
      if (known !== undefined) setResult(known);
    }
    const ticket = ++pending.current;
    const timer = setTimeout(() => {
      planManual(request)
        .then((next) => {
          rememberAnswer(answersRef.current, key, next);
          // An answer that was overtaken is dropped: two plans in flight would
          // otherwise settle in whatever order the network returned them.
          if (ticket === pending.current) {
            setResult(next);
            setFailure(null);
          }
        })
        .catch((cause: unknown) => {
          // **No ticket guard on the failure**, deliberately, and this was
          // found by running it: an overtaken request drops its *answer*
          // because a newer plan supersedes it — but a failure is not an
          // answer about a plan, it is news about the endpoint, and dropping
          // it left a 422 visible only in the network tab. Silence on a
          // screen whose whole job is to say what is wrong is the one thing
          // it may not do.
          setFailure(cause instanceof Error ? cause.message : String(cause));
        });
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [request, faseBlocked]);

  /**
   * The write to the Vorgang, on the same settle as the plan but on its own
   * trigger: every announced gesture (`gestureTick`), not only a draft change.
   * Split from the plan effect in fix round 1 of task 11 — keyed on `request`
   * alone, a hole's release (same figures as its last frame, so no draft
   * change) never started this window and its op never left the browser.
   */
  useEffect(() => {
    if (!request) return;
    const timer = setTimeout(() => {
      // **Only a gesture writes a version.** This effect also runs on mount,
      // on hydration, on a refetch and whenever a prop identity changes, and
      // none of those is a change to record: the server refuses a version that
      // carries neither an op nor a new state (400 „Weder eine Geste noch …"),
      // which is exactly what opening a Vorgang used to do (fix round 1,
      // item 8 — API log, `screen-03-vorgang-planung-400.png`).
      if (caseId === null || !gesturePendingRef.current || faseBlocked) return;
      // The release question stands: nothing goes out until it is answered.
      if (releaseQuestionRef.current) return;
      // A reload is on its way and the board on screen is still the old one.
      // Posting now would send the pre-hydration state against the version
      // number the *other* person's gesture won — accepted by the server, and
      // an overwrite (fix round 2, item 1). What is pending stays pending and
      // the hydration discards it, which is what „die abgelehnte Geste ist vom
      // Board verschwunden" means.
      if (awaitingHydrationRef.current) return;
      if (unwritableRef.current !== null) {
        // Said out loud instead of posted under an invented `wallId`: an op
        // naming a wall the document does not have is accepted at append time
        // and fails at every replay afterwards — one lost gesture against a
        // permanently unopenable Vorgang.
        setUnwritable(unwritableRef.current);
        unwritableRef.current = null;
        pendingOpsRef.current = [];
        pendingSummariesRef.current = [];
        pendingLaidRef.current = null;
        gesturePendingRef.current = false;
        setSaveState({ kind: "unsaved", reason: "Wand ohne Kennung" });
        return;
      }
      setUnwritable(null);
      // One write at a time (review of Task 13/14, I-3): a write of the
      // board's own, a measure or a change of type still on its way holds
      // this one back — posted now, both would go against the same Stand and
      // one would come back 409, a person's own gesture worded as somebody
      // else's, or the measure lost. The end of that write settles again
      // (`trackWrite`, `measureWriting`, `typeWriting` → `rearmSettle`), and
      // this goes against the Stand it left.
      if (writingRef.current !== null || otherWritesRef.current > 0) {
        settleDeferredRef.current = true;
        return;
      }
      // A released Vorgang: the first write asks first (UX-Gutachten Nr. 5) —
      // unless it would write nothing, which `persist` closes without a word.
      if (
        editabilityRef.current === "approved" &&
        !releaseSpentRef.current &&
        !(pendingOpsRef.current.length === 0 && sameJson(boardStateRef.current, lastSavedStateRef.current))
      ) {
        // Never a modal question under a pressed pointer (a drag paused
        // under a still hand — its next frame would land behind „Abbrechen“
        // and its release on an inert page) or over another question. Asked
        // again when the reason is gone (`rearmSettle`). A write on its way
        // held everything back above already (it may spend the release).
        if (pointerDownRef.current || modalDialogOpen()) {
          settleDeferredRef.current = true;
          return;
        }
        releaseQuestionRef.current = true;
        setReleaseQuestion({ subtitle: pendingSummariesRef.current.join(" · ") || "Planung geändert" });
        setSaveState({ kind: "unsaved", reason: "wartet auf deine Antwort" });
        return;
      }
      trackWrite(persistRef.current());
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [request, caseId, gestureTick, faseBlocked, trackWrite]);

  /**
   * „Trotzdem ändern“: the write goes out. The release is spent once it
   * lands (`persist`); until then a further gesture waits (`trackWrite`).
   */
  function spendRelease(): void {
    releaseQuestionRef.current = false;
    setReleaseQuestion(null);
    setSaveState({ kind: "saving" });
    trackWrite(persistRef.current());
  }

  /**
   * „Abbrechen“: the gesture is gone, not waiting — the board the server
   * holds goes back on, and nothing is written. A pick that still has its
   * thing stays; one that the gesture made (a placed element) goes with it.
   */
  function keepRelease(): void {
    releaseQuestionRef.current = false;
    setReleaseQuestion(null);
    pendingOpsRef.current = [];
    pendingSummariesRef.current = [];
    pendingLaidRef.current = null;
    gesturePendingRef.current = false;
    unwritableRef.current = null;
    unsentReasonRef.current = null;
    const written = writtenBoardRef.current;
    if (written !== null) {
      const now = store.getState();
      const back = written.history.at(-1) ?? EMPTY_DRAFT;
      store.setState({
        ...written,
        selected: back.placements.some((each) => each.id === now.selected) ? now.selected : null,
        selectedFeature: back.features.some((each) => each.id === now.selectedFeature)
          ? now.selectedFeature
          : null,
        selectedCutout: back.cutouts.some((each) => each.id === now.selectedCutout)
          ? now.selectedCutout
          : null,
      });
    }
    // What was pinned to the gesture's stack height goes with it (M-3), and
    // a laying still on its way is dropped when it answers (M-4).
    setRelaid(null);
    setNothingLaid(null);
    setBlankRefusal(null);
    setFeatureRefusal(null);
    layingTicketRef.current += 1;
    // And the plan's answer with the board (N2): the request it rebuilds is
    // one this board has asked before.
    restoreAnswerRef.current = written !== null;
    if (versionRef.current !== null) setSaveState({ kind: "saved", versionNo: versionRef.current });
    setAnnouncement(RELEASE_KEPT);
  }

  /** Where the focus goes once the question is gone, if the thing that had it went with the gesture. */
  function afterReleaseQuestion(): void {
    const active = document.activeElement;
    if (active !== null && active !== document.body && active.isConnected) return;
    document.querySelector<SVGElement>('[data-role="plan-board"]')?.focus();
  }

  // ── Editing ────────────────────────────────────────────────────────────

  const commit = useCallback(
    (next: Placement[], live = false) =>
      store.getState().push({ placements: next, corners, features, cutouts }, live),
    [store, corners, features, cutouts],
  );

  /**
   * Set or clear the mitre at one corner — the same undo stack as an element.
   *
   * Setting a mitre is a gesture, so Rückgängig has to undo it like any
   * other. That is the whole reason the draft carries both lists rather than
   * two stores keeping step by hand.
   */
  /**
   * Change what somebody set on one corner, without touching whether it is
   * mitred at all — that is `toggleCorner`, and the two are different
   * gestures. A step of its own on the undo stack, like every other.
   */
  const changeCorner = useCallback(
    (wallIndex: number, patch: Partial<CornerMark>) => {
      const next = corners.map((mark) =>
        mark.wallIndex === wallIndex ? { ...mark, ...patch } : mark,
      );
      const changed = next.find((mark) => mark.wallIndex === wallIndex);
      if (changed) announce("Ecke geändert", [setCornerOp(outline, changed)]);
      store.getState().push({ placements, corners: next, features, cutouts });
    },
    [store, corners, placements, features, cutouts, outline],
  );

  const [cornerMenu, setCornerMenu] = useState<CornerMenuRequest | null>(null);
  /** Why the last „Objekt einsetzen" did not happen — cleared the moment a
   * new menu opens or an insertion succeeds, so a stale refusal never
   * outlives the click that produced it. */
  const [insertFailure, setInsertFailure] = useState<string | null>(null);

  /** Hands focus back to the wall a just-closed menu was about — its own
   * `data-testid`/`role="button"` grip, always present and always
   * focusable regardless of how the wall is currently covered. Without
   * this, closing the menu (Escape, an away-click, or any menuitem) drops
   * focus to `document.body` once the menu unmounts (review round 1,
   * important 3) — a real focus loss, not merely an untidy one, for
   * anyone driving the board from the keyboard alone. */
  function focusWall(wallIndex: number): void {
    document.querySelector<HTMLElement>(`[data-testid="wall-${wallIndex}"]`)?.focus();
  }

  /** Closes the wall menu and returns focus to the wall it was about — the
   * one close path every menu action funnels through. */
  function closeCornerMenu(): void {
    const wallIndex = cornerMenu?.objectWallIndex ?? null;
    setCornerMenu(null);
    if (wallIndex !== null) focusWall(wallIndex);
  }

  /** Somebody asked what one bracket should build — a right-click on its
   * mark, or Shift+F10 once it holds focus (task 11). */
  const [consoleMenu, setConsoleMenu] = useState<ConsoleMenuRequest | null>(null);

  /** Closes the bracket chooser and returns focus to the mark it was about —
   * the same courtesy `closeCornerMenu` pays the wall grip above. */
  function closeConsoleMenu(): void {
    const index = consoleMenu?.index ?? null;
    setConsoleMenu(null);
    if (index !== null) {
      document
        .querySelector<HTMLElement>(
          `[data-testid="console-joint-${index}"], [data-testid="console-middle-${index}"]`,
        )
        ?.focus();
    }
  }

  /**
   * Cover the corner square with one element instead of dividing it.
   *
   * Replaces whatever the corner carried — a corner has one treatment, and
   * „fill this" on a mitred corner means the mitre goes.
   */
  const fillCorner = useCallback(
    (wallIndex: number, filledWallIndex: number) => {
      const mark = {
        wallIndex,
        treatment: "fuellung" as const,
        filledWallIndex,
        kindOverride: null,
        gainOverrideMm: null,
      };
      announce("Ecke aufgefüllt", [setCornerOp(outline, mark)]);
      store.getState().push({
        placements,
        corners: [...corners.filter((each) => each.wallIndex !== wallIndex), mark],
        features,
        cutouts,
      });
    },
    [store, corners, placements, features, cutouts, outline],
  );

  /** Take whatever a corner carries off it. The butted corner is the state
   * before any treatment, and it is what a reset returns to. */
  const clearCorner = useCallback(
    (wallIndex: number) => {
      announce("Ecke zurückgesetzt", [clearCornerOp(outline, wallIndex)]);
      store.getState().push({
        placements,
        corners: corners.filter((mark) => mark.wallIndex !== wallIndex),
        features,
        cutouts,
      });
    },
    [store, corners, placements, features, cutouts, outline],
  );

  const toggleCorner = useCallback(
    (wallIndex: number) => {
      const already = corners.some((mark) => mark.wallIndex === wallIndex);
      if (already) {
        announce("Gehrung entfernt", [clearCornerOp(outline, wallIndex)]);
      } else {
        // A press on the handle means a mitre; a filling is the other
        // gesture and names its own wall (§4).
        announce("Gehrung gesetzt", [
          setCornerOp(outline, {
            wallIndex,
            treatment: "gehrung" as const,
            filledWallIndex: null,
            kindOverride: null,
            gainOverrideMm: null,
          }),
        ]);
      }
      store.getState().push({
        placements,
        corners: already
          ? corners.filter((mark) => mark.wallIndex !== wallIndex)
          : [
              ...corners,
              {
                wallIndex,
                treatment: "gehrung" as const,
                filledWallIndex: null,
                kindOverride: null,
                gainOverrideMm: null,
              },
            ],
        features,
        cutouts,
      });
    },
    [store, corners, placements, features, cutouts, outline],
  );

  /**
   * Set a mitre on one corner directly, replacing whatever it carried —
   * the „Gehrung setzen" entry in the wall menu (task 14).
   *
   * Not `toggleCorner`: that one flips a mark on and off and needs a second
   * press to turn a filling into a mitre (by design — see its own doc
   * comment on the standalone handle). The menu offers three named choices
   * side by side, not a toggle, so „Gehrung setzen" has to *set* it in one
   * click even where the corner already carries a filling.
   */
  const setMitre = useCallback(
    (wallIndex: number) => {
      const mark = {
        wallIndex,
        treatment: "gehrung" as const,
        filledWallIndex: null,
        kindOverride: null,
        gainOverrideMm: null,
      };
      announce("Gehrung gesetzt", [setCornerOp(outline, mark)]);
      store.getState().push({
        placements,
        corners: [...corners.filter((each) => each.wallIndex !== wallIndex), mark],
        features,
        cutouts,
      });
    },
    [store, corners, placements, features, cutouts, outline],
  );

  function change(id: string, patch: Partial<Placement>, live = false): void {
    announce("Element geändert");
    commit(
      placements.map((each) => (each.id === id ? { ...each, ...patch } : each)),
      live,
    );
  }

  /** „Aufteilung“ on the element (20.10.2026): one press, one step, the first piece stays picked. */
  function splitAt(id: string, firstMm: number): void {
    const next = splitPlacement(placements, id, firstMm, newSplitId());
    if (next === null) return;
    announce("Element aufgeteilt");
    commit(next);
    store.getState().select(id);
  }

  function remove(id: string): void {
    announce("Element entfernt");
    commit(placements.filter((each) => each.id !== id));
    store.getState().select(null);
  }

  /**
   * The board hands over one finished object — the same one-gesture-one-step
   * contract `commit` keeps for a placement.
   *
   * **An upsert, keyed on `id`**, because the design's op is one op:
   * `SetFeature` „anlegen und ändern, geschlüsselt über `id`" (§2). Drawing
   * one, dragging it along its wall and pulling one of its ends are the same
   * statement about the same wall, and a second function for the second half
   * would be a second place for the rule to live.
   */
  function setFeature(
    feature: FacadeFeature,
    live = false,
    // The tool form's own tick for a drawn object; the menu passes the
    // type's default instead (`insertFeature`). Read only for a *new*
    // object — one that stands already has, or has not, a hole of its own.
    withCutout = featureWithCutout,
  ): void {
    const known = features.some((each) => each.id === feature.id);
    // A new covered object that asked for a hole brings the hole with it —
    // the object first, then the hole, in this order on the wire (§6.1):
    // `set_cutout` needs its object to exist.
    const hole =
      !known && feature.covered && withCutout
        ? linkedCutoutFor(feature, cutoutClearanceMm, nextCutoutId(cutouts))
        : null;
    announce(
      "Fassadenobjekt gesetzt",
      hole
        ? [setFeatureOp(outline, feature), setCutoutOp(outline, hole, [...features, feature])]
        : [setFeatureOp(outline, feature)],
    );
    // An object that loses its grating loses its linked holes in the
    // same step — „ohne Blech kein Loch", the cascade the server runs on
    // `covered = false`, mirrored here so the stateless plan never names
    // a hole on an uncovered object (design §5.1). No `remove_cutout`
    // goes out for them: `diffOps` knows the cascade (`cascaded`).
    const remaining = feature.covered ? cutouts : withoutLinkedCutouts(cutouts, feature.id);
    store.getState().push(
      {
        placements,
        corners,
        features: known
          ? features.map((each) => (each.id === feature.id ? feature : each))
          : [...features, feature],
        cutouts: hole ? [...remaining, hole] : remaining,
      },
      live,
    );
    forgetCutoutUnless(remaining);
    // A new downpipe opens its own panel with the diameter choice in hand
    // (A5, 23.09.2026): the one question every pipe raises, asked while the
    // hand is still on it. 120 is marked, Enter keeps it; a click anywhere
    // else keeps it too — nothing here is modal.
    if (!known && feature.featureType === "fallrohr") {
      store.getState().selectFeature(feature.id);
      setFreshPipe(feature.id);
    }
  }

  /**
   * A downpipe to a new diameter — the Schnellwahl or its own field (A5).
   *
   * The pipe keeps its middle ({@link pipeOffsetMm}) and goes through the
   * same `settleFeature` every other change of an object goes through; its
   * linked holes follow in the **same** step, pipe plus the case's
   * Montagespiel ({@link holeForPipe}). One gesture, one Rückgängig, and on
   * the wire the very `set_feature` and `set_cutout` the Länge field and the
   * size fields would have written by hand — no field of its own.
   */
  function setPipe(feature: FacadeFeature, pipeMm: number, live = false): void {
    const wallLength = outline ? wallLengthMm(outline, feature.wallIndex) : null;
    if (wallLength === null) return;
    const settled = settleFeature(features, feature, pipeOffsetMm(feature, pipeMm), pipeMm, wallLength);
    if (settled.outcome === "overlaps") {
      setFeatureRefusal({ wallIndex: feature.wallIndex, reason: "feature_overlaps", step: history.length });
      return;
    }
    setFeatureRefusal(null);
    const moved =
      settled.outcome === "settled"
        ? { ...feature, offsetMm: settled.offsetMm, lengthMm: settled.lengthMm }
        : feature;
    const nextFeatures = features.map((each) => (each.id === feature.id ? moved : each));
    const resized = cutouts.map((each) =>
      each.featureId === feature.id ? holeForPipe(each, moved.lengthMm, cutoutClearanceMm) : each,
    );
    const changedHoles = resized.filter((each, index) => !sameCutout(each, cutouts[index]!));
    if (moved === feature && changedHoles.length === 0) return;
    announce("Fallrohr geändert", [
      ...(moved === feature ? [] : [setFeatureOp(outline, moved)]),
      ...changedHoles.map((each) => setCutoutOp(outline, each, nextFeatures)),
    ]);
    store.getState().push({ placements, corners, features: nextFeatures, cutouts: resized }, live);
  }

  /** „Enter übernimmt": the marked diameter stays, the panel steps aside and
   * the tool's own form is back for the next object. */
  function keepFreshPipe(): void {
    setFreshPipe(null);
    store.getState().selectFeature(null);
    document.querySelector<HTMLElement>('[data-role="plan-board"]')?.focus();
  }

  /** Take one object off its wall. Its findings go with it — they were about
   * it — and so are the holes linked to it, in the same push: the server
   * removes them with the object (design §5.1), and a plan asked in between
   * must not name a hole whose object is gone. */
  function removeFeature(id: string): void {
    announce("Fassadenobjekt entfernt", [removeFeatureOp(id)]);
    const remaining = withoutLinkedCutouts(cutouts, id);
    store.getState().push({
      placements,
      corners,
      features: features.filter((each) => each.id !== id),
      cutouts: remaining,
    });
    store.getState().selectFeature(null);
    forgetCutoutUnless(remaining);
  }

  /** A selection names a thing in the draft: once the cascade has taken the
   * selected hole with its object, the panel must not stay open over it
   * (task 9 review). `selectFeature(null)` keeps the hole on purpose — the
   * two slots are independent — so this is said separately. */
  function forgetCutoutUnless(remaining: readonly Cutout[]): void {
    if (selectedCutout !== null && !remaining.some((each) => each.id === selectedCutout)) {
      store.getState().selectCutout(null);
    }
  }

  /** One hole on the board — the same one-gesture-one-step contract `setFeature` keeps. */
  function placeCutout(cutout: Cutout): void {
    announce(`Ausschnitt ${cutout.id} gesetzt`, [setCutoutOp(outline, cutout, features)]);
    store.getState().push({ placements, corners, features, cutouts: [...cutouts, cutout] });
    store.getState().selectCutout(cutout.id);
  }

  /**
   * A hole carried to `offsetMm`/`acrossMm` — a drag's frame, its release,
   * or one arrow key.
   *
   * **Every frame is announced**, live or not, and `announce` keeps one
   * `set_cutout` per hole — the latest — so a settle window that fires under
   * a still hand mid-drag posts the position the board shows and never an
   * older frame (fix round 1, task 11). The settling call repeats the last
   * frame's figures with `live=false`: it announces (and so starts the
   * window of its own that carries the final op) but pushes nothing — the
   * stack already holds these figures, and a second entry would be a second
   * Rückgängig step for one gesture.
   */
  function moveCutout(id: string, offsetMm: number, acrossMm: number, live: boolean): void {
    const current = cutouts.find((each) => each.id === id);
    if (!current) return;
    const moved = { ...current, offsetMm, acrossMm };
    announce(`Ausschnitt ${id} verschoben`, [setCutoutOp(outline, moved, features)]);
    if (current.offsetMm === offsetMm && current.acrossMm === acrossMm) return;
    store.getState().push(
      { placements, corners, features, cutouts: cutouts.map((each) => (each.id === id ? moved : each)) },
      live,
    );
  }

  /**
   * One field of one hole, from its panel — the same upsert `moveCutout`
   * makes for a drag, and announced on every keystroke for the same reason
   * every frame of a drag is: `announce` keeps one `set_cutout` per hole,
   * so a settle window that fires under a pause posts what the panel shows.
   * `live` folds the keystrokes of one edit into one step on the stack.
   */
  function changeCutout(id: string, patch: Partial<Cutout>, live = false): void {
    const current = cutouts.find((each) => each.id === id);
    if (!current) return;
    const next = { ...current, ...patch };
    announce(`Ausschnitt ${id} geändert`, [setCutoutOp(outline, next, features)]);
    store.getState().push(
      { placements, corners, features, cutouts: cutouts.map((each) => (each.id === id ? next : each)) },
      live,
    );
  }

  /**
   * „mit Ausschnitt" on an object that already stands (the object panel):
   * ticked, the linked hole is set at the object's middle (D-9, D-10);
   * unticked, every linked hole goes — **with** a `remove_cutout` each,
   * because the object keeps its grating and this is a deletion in its own
   * right, not the cascade.
   *
   * Not `placeCutout`: that one selects the new hole, which is right for
   * the tool and the menu (the hole is what was just made) and wrong here —
   * a tick in the object's own form must not close that form (fix round 1,
   * Important 1). The object stays picked; the hole is on the board.
   */
  function linkCutout(feature: FacadeFeature, on: boolean): void {
    if (on) {
      const hole = linkedCutoutFor(feature, cutoutClearanceMm, nextCutoutId(cutouts));
      announce(`Ausschnitt ${hole.id} gesetzt`, [setCutoutOp(outline, hole, features)]);
      store.getState().push({ placements, corners, features, cutouts: [...cutouts, hole] });
      return;
    }
    const linked = cutouts.filter((each) => each.featureId === feature.id);
    if (linked.length === 0) return;
    announce(
      `Ausschnitt ${linked.map((each) => each.id).join(", ")} entfernt`,
      linked.map((each) => removeCutoutOp(each.id)),
    );
    const remaining = cutouts.filter((each) => each.featureId !== feature.id);
    store.getState().push({ placements, corners, features, cutouts: remaining });
    forgetCutoutUnless(remaining);
  }

  function deleteCutout(id: string): void {
    announce(`Ausschnitt ${id} entfernt`, [removeCutoutOp(id)]);
    store.getState().push({ placements, corners, features, cutouts: cutouts.filter((each) => each.id !== id) });
    store.getState().selectCutout(null);
  }

  /**
   * A hole from the menu — square at the click, or round on request (§8).
   *
   * Along the click, snapped; across the middle of the band under it — the
   * element's own width where one lies there, the job's otherwise — exactly
   * as the board's own tool sets one (ruling 9).
   */
  function insertCutout(request: CornerMenuRequest, shape: CutoutShape): void {
    const wallIndex = request.objectWallIndex;
    const carrier = carrierAt(placements, wallIndex, request.offsetMm);
    const widthMm = bandWidthMm(carrier, specs, jobGratingType);
    const base = freeCutoutAt(wallIndex, snapCutout(request.offsetMm, 0).offsetMm, widthMm, nextCutoutId(cutouts));
    closeCornerMenu();
    placeCutout(
      shape === "rund"
        ? { ...base, shape: "rund", diameterMm: CUTOUT_DEFAULT_MM, sizeAlongMm: null, sizeAcrossMm: null }
        : base,
    );
  }

  /**
   * Put one element on the whole board — „Diesen Rost groß" (task 8, design
   * 2026-09-10).
   *
   * A viewpoint, not a second screen (design D-5): scale and centre, handed to
   * the same renderer as every other view. There is no second component that
   * draws grating, so there is no second place a drawing and its parts list
   * can drift apart.
   *
   * `boardWidth` comes from the corner-menu request, not a fallback constant
   * (fix round 1, 2026-09-10): only the board itself knows its real, measured
   * width, and the request already carries it — riding along on a channel
   * that exists rather than opening a second one. The first version of this
   * function used `BOARD_FALLBACK_WIDTH_PX` instead, which is correct in
   * every test (jsdom fires no `ResizeObserver`) and quietly wrong on a real
   * screen: at a true board width of ~800 px it produced a view scaled to
   * about two thirds of what the board could actually show, and above
   * ~1.100 px `MAX_SCALE` caps the real board's own zoom while the fallback's
   * scale does not move — the entry that exists to make one element fill the
   * board was instead filling about half of it.
   *
   * `outlineProjection(...).fitScale` and not a second, hand-built node list:
   * `lib/outline.ts` already turns `outline.nodes` into the `MmPoint[]`
   * `fitScale` wants (`points()`, private to that module) and exports exactly
   * this composition for a caller that only needs the number. A second
   * mapping here, with its own id scheme, is the second place the same
   * geometry could drift from itself that this codebase's own comments argue
   * against.
   *
   * `boardHeight` rides on the same request: 520 in place, the container's
   * own height once the board is maximized (A3, 23.09.2026) — so a rost on a
   * vertical wall fills the tall board, not a 520-px slice of it.
   */
  function zoomToPlacement(placementId: string, boardWidth: number, boardHeight: number): void {
    const placement = placements.find((each) => each.id === placementId);
    if (!placement || !outline) return;
    const box = placementBoxMm(outline, placement, specs, jobGratingType);
    if (!box) return;
    const size = { width: boardWidth, height: boardHeight };
    const fitted = outlineProjection(outline, { ...size, padding: VIEW_PADDING }).fitScale;
    store.getState().setView(viewForBox(box, size, fitted));
  }

  /**
   * „Objekt einsetzen" from the wall menu: centred on the click, at the
   * type's own catalogue length, shifted clear of whatever else already
   * stands on that wall.
   *
   * The one place this screen may **not** hand `addFeature` a zone that
   * overlaps another — `_set_feature` on the server refuses that outright
   * (`document.py`), and a menu click is an approximate gesture, not a typed
   * offset, so it is exactly the kind of click that would land on top of a
   * neighbour by accident. `freeFeatureOffsetMm` shifts it clear instead;
   * only where nothing of that length fits anywhere on the wall does this
   * give up and say so, rather than push the broken state to the server and
   * let the async conflict banner report it after the fact (task 14).
   */
  function insertFeature(request: CornerMenuRequest, type: FeatureType): void {
    const wallIndex = request.objectWallIndex;
    const wallLength = outline ? wallLengthMm(outline, wallIndex) : null;
    const defaults = FEATURE_DEFAULTS[type];
    if (wallLength === null) {
      setCornerMenu(null);
      focusWall(wallIndex);
      return;
    }
    const wantedMm = request.offsetMm - defaults.defaultLengthMm / 2;
    const offsetMm = freeFeatureOffsetMm(
      features,
      wallIndex,
      wallLength,
      wantedMm,
      defaults.defaultLengthMm,
    );
    if (offsetMm === null) {
      setInsertFailure(
        `${defaults.name} passt auf Wand ${wallIndex + 1} nirgends mehr frei — ` +
          `${formatMm(defaults.defaultLengthMm)} sind dort nicht mehr am Stück frei.`,
      );
      setCornerMenu(null);
      focusWall(wallIndex);
      return;
    }
    setInsertFailure(null);
    // The menu knows nothing of the tool form's tick — the type says whether
    // a hole belongs to it (a downpipe does).
    setFeature(
      {
        id: nextFeatureId(features),
        wallIndex,
        offsetMm,
        lengthMm: defaults.defaultLengthMm,
        featureType: type,
        covered: defaults.covered,
        widthOffsetMm: defaults.widthOffsetMm,
        splitsRun: defaults.splitsRun,
        label: null,
      },
      false,
      defaults.withCutout,
    );
    setCornerMenu(null);
    focusWall(wallIndex);
  }

  /** Change one field of one object — the panel's half of `setFeature`'s
   * upsert, which is why it goes through the same function.
   *
   * `live` folds the keystrokes of one edit into one step, the way the
   * placement panel's length field already does: „1200" is four keystrokes
   * and one gesture.
   */
  function changeFeature(id: string, patch: Partial<FacadeFeature>, live = false): void {
    const feature = features.find((each) => each.id === id);
    if (!feature) return;
    setFeature({ ...feature, ...patch }, live);
  }

  /**
   * Where the typed „Anfang"/„Länge" of the side panel put an object — through
   * **the same** `settleFeature` every gesture goes through.
   *
   * Round 2 of the walk (30.08.2026) found these two fields bypassing it
   * entirely: no overlap check and no grid snap, so a typed offset could
   * rebuild the very document `_set_feature` refuses — and `_plan_features`
   * does not check overlap either, so nothing downstream would have caught
   * it. One rule, one place, whichever hand asked.
   */
  function settleFeatureFields(
    feature: FacadeFeature,
    wantedOffsetMm: number,
    wantedLengthMm: number,
    live: boolean,
  ): void {
    const wallLength = wallLengthMm(outline!, feature.wallIndex);
    if (wallLength === null) return;
    const settled = settleFeature(
      features,
      feature,
      wantedOffsetMm,
      wantedLengthMm,
      wallLength,
    );
    if (settled.outcome === "overlaps") {
      setFeatureRefusal({
        wallIndex: feature.wallIndex,
        reason: "feature_overlaps",
        step: history.length,
      });
      return;
    }
    setFeatureRefusal(null);
    if (settled.outcome === "unchanged") return;
    setFeature(
      { ...feature, offsetMm: settled.offsetMm, lengthMm: settled.lengthMm },
      live,
    );
  }

  /**
   * The two messages that describe a state, shown only while that state is
   * what stands.
   *
   * Both are pinned to the step of the draft they were said at; the moment
   * the draft moves — a new gesture, or Rückgängig — they stop being true and
   * stop being shown. One rule in one place, rather than a
   * `setRelaid(null)`/`setFeatureRefusal(null)` at every call site that could
   * invalidate them and a missed one at the next (round 2 of the walk,
   * finding 4).
   */
  const standingRelaid = relaid && relaid.step === history.length ? relaid : null;
  const standingRefusal =
    featureRefusal && featureRefusal.step === history.length ? featureRefusal : null;
  const standingNothingLaid =
    nothingLaid && nothingLaid.step === history.length ? nothingLaid : null;
  const standingBlankRefusal =
    blankRefusal && blankRefusal.step === history.length ? blankRefusal : null;

  const picked = placements.find((each) => each.id === selected) ?? null;
  const pickedFeature = features.find((each) => each.id === selectedFeature) ?? null;
  const pickedCutout = cutouts.find((each) => each.id === selectedCutout) ?? null;
  /** The answer's row for the picked hole — its cut edge, the computed
   * surcharge and what a typed one displaced — or `null` where the answer
   * has not laid an element under it (yet). */
  const pickedCutoutAnswer: DemoElementCutout | null = pickedCutout
    ? (result?.runs
        .flatMap((run) => run.elements ?? [])
        .flatMap((each) => each.cutouts ?? [])
        .find((each) => each.id === pickedCutout.id) ?? null)
    : null;

  // ── What the answer says ───────────────────────────────────────────────

  /**
   * The answer's findings — with the two a replay alone makes about a figure
   * the sketch contradicts (E-64) taken from the last `GET …/plan` answer
   * until the next one: the board's own plans know nothing of that figure,
   * and the finding would vanish with the first of them.
   */
  const findings = withReplayFindings(result, initialResult);
  /** A figure the sketch contradicts — its own rows, with their own answers. */
  const implausibles = findings.filter((finding) => finding.rule === MEASURE_IMPLAUSIBLE);
  /**
   * Which elements a finding is about — `placementIds`, never `measurementIds`.
   *
   * The first version read `measurementIds`, which on this path is always
   * empty: a placement is not a reading and the backend keeps two lists on
   * purpose (`FindingOut`). Nothing was ever marked, „zeigen" never appeared,
   * and both test suites were green because each built its own fixture on the
   * side the endpoint does not fill. Four carriers for a violation collapsed
   * to one — the sentence in the bar (found in review, 16.08.2026).
   */
  const issueIds = findings
    .filter((finding) => !NOTICES.has(finding.rule))
    .flatMap((finding) => finding.placementIds ?? []);
  /** Split for the bar below: what is wrong, and what is merely worth saying. */
  const violations = findings.filter(
    (finding) => !NOTICES.has(finding.rule) && finding.rule !== MEASURE_IMPLAUSIBLE,
  );
  const notices = findings.filter((finding) => NOTICES.has(finding.rule));

  /**
   * Whether the division on the board still matches what stands on the walls.
   *
   * **Read off the answer, not off a stamp we keep.** The first sketch of this
   * compared a fingerprint of the objects taken at the last covering against
   * the current one — which is a second bookkeeping of a fact the endpoint
   * already states, and one that a reload throws away while the draft
   * survives it. `check_placements` walks every element against every object
   * on its wall and says so in three findings (`layout.py`); those three are
   * exactly the cases a re-lay resolves, so they are the signal.
   *
   * Not a gate on the button. A person may re-lay a wall that carries no
   * finding at all — after changing an element's length by hand, say, which
   * leaves a hole no object knows about.
   */
  const planStale = findings.some((finding) => RELAY_FIXES.has(finding.rule));
  /** Whether there is anything to re-lay at all. */
  const hasGrating = placements.some((each) => each.kind === "grating");
  // The footer's way on: red once there is something to price — unless the
  // refresh (a stale plan) or the restore (a cancelled Vorgang) is the red
  // main action of the screen right now; then ink (design 2026-10-24 §8.2).
  const footerWay = !hasGrating
    ? "secondary"
    : planStale || editability === "cancelled"
      ? "tinte"
      : "primary";
  /**
   * Which wall ends a run actually crosses — the corners that can take a
   * mitre.
   *
   * Read off the answer's own `corners`, which states which joints sit on a
   * wall boundary (§4.1); the wall is then the one element `joint` sits on.
   * The board is not asked to work either out.
   */
  const cornerFacts = useMemo(() => cornerFactsOf(result), [result]);

  // A chamfer's two corners are only ever butted (design 2026-10-18, call 7)
  // — a run crosses them, but there is nothing to treat.
  const treatableWalls = useMemo(
    () => [...cornerFacts.keys()].filter((wallIndex) => outline === null || !isFaseCorner(outline, wallIndex)),
    [cornerFacts, outline],
  );

  /**
   * How each element is cut and what it is cut *to*, by placement id.
   *
   * Straight from the answer, and one map rather than two: the board draws
   * the leans, the list and the CSV print the blank, and all three are
   * statements about the same element made in one place.
   */
  const cuts = useMemo(() => cutsOf(result), [result]);

  /**
   * The answer's own laid elements, by `placementId` — the parts list and
   * the CSV read a hole's figures off this, never off the draft (§11).
   */
  const elements = useMemo(() => elementsOf(result), [result]);

  const derivation = result?.totals.consolesDerivation ?? "";

  /**
   * Every bracket the answer addressed, by its own key — what the board's
   * marks are enriched against below and what the chooser looks a proposal
   * up in. Memoized on `result` alone, and not folded into the `consoles`
   * memo just below: the chooser needs the same map for a mark that is not
   * necessarily the one `consoles` last recomputed for, and a fresh `Map` on
   * every render would have defeated that memo's own point (it lists this
   * map in its dependencies).
   */
  const consoleMarksByKey = useMemo(
    () => indexConsoleMarks(result?.consoleMarks ?? []),
    [result],
  );

  /**
   * Console positions, projected from the runs.
   *
   * The answer states *which* joints there are and what each one means
   * (`corners`, `bridged`, `ring`); this turns them into millimetres. The
   * domain statement stays in the backend, the projection stays here — and
   * neither is made twice.
   *
   * Each mark is also addressed here (task 11) — the same key
   * `console_positions` computes on the backend, built the same way rather
   * than read off `result.consoleMarks` by position: the two lists are not
   * guaranteed to line up index for index (a mark the answer has nothing to
   * say about — an older answer, say — still has to sit somewhere on the
   * board), while the *key* is exact wherever both sides compute it.
   */
  const consoles: ConsoleMark[] = useMemo(
    () => consoleMarksOf(outline, result, consoleMarksByKey),
    [outline, result, consoleMarksByKey],
  );

  // ── Covering a wall, or the house ──────────────────────────────────────

  /**
   * The last plan answers, by the request they answer — so „Abbrechen“ can put
   * the stored board's answer back with the board (`restoreAnswerRef`,
   * UX-Zweitgutachten 23.10.2026, N2: overlap and 22 brackets stood on a
   * board that was back at 20 until a reload).
   */
  const answersRef = useRef(new Map<string, DemoRunResult>());
  /** Set by `keepRelease`: the next plan request takes its known answer first. */
  const restoreAnswerRef = useRef(false);

  /**
   * Moves on with every „Abbrechen“ under the release question (`keepRelease`):
   * a laying (`cover`, `splitOverlongBlank`) whose answer comes back after it
   * was computed from a board that has been put back, and is dropped
   * (review of d8516611, M-4).
   */
  const layingTicketRef = useRef(0);

  /**
   * A laying's answer, with the gratings held off a disputed wall put back
   * (E-64). The answer replaces the board wholesale, and it was computed from
   * `request` — which never carried them (`heldOff`): committed as it is, it
   * would delete them from the board and from the next Stand without a word.
   * Read off the store at commit time, so the board the answer lands on is
   * the one they are taken from.
   */
  function withHeldOff(answered: Placement[]): Placement[] {
    if (heldOff.size === 0) return answered;
    const answeredIds = new Set(answered.map((each) => each.id));
    const kept = store
      .getState()
      .current()
      .placements.filter((each) => heldOff.has(each.id) && !answeredIds.has(each.id));
    return [...answered, ...kept];
  }

  /**
   * „Wand belegen" (`replaceExisting: false`) and „Wand neu aufteilen"
   * (`true`) — the same endpoint, two different questions.
   *
   * The first covers what is free. The second throws the wall's grating away
   * and lays it again around whatever stands on it, which is the only answer
   * to the order of work the customer asked for on 30.08.2026: „Objekte
   * sollten prozessual vor den Rosten eingegeben werden. Aber generell müssen
   * beide Wege möglich sein, falls etwas vergessen wurde." Before this,
   * covering the house first and remembering the front door afterwards left
   * findings and no offered way out — `fill_wall` fills *free* stretches, and
   * a covered wall has none, so the menu entry was a silent no-op.
   */
  async function cover(
    wallIndex: number | "all",
    replaceExisting = false,
  ): Promise<boolean | null> {
    if (!request) return null;
    // Held (a measure or a change of type is being saved): refused before
    // anything is asked of the server, and said (B-2).
    if (usePlanning.getState().held) {
      sayAgain(WRITE_WAIT);
      return null;
    }
    const ticket = layingTicketRef.current;
    // What the request carried — the answer is measured against that, not
    // against the held-off gratings it was never sent.
    const before = placements.filter((each) => !heldOff.has(each.id)).length;
    setFilling(wallIndex);
    setFillFailure(null);
    setRelaid(null);
    try {
      const answer = await fillWall({
        ...request,
        replaceExisting,
        // One wall by index, the house by list. The endpoint skips unmeasured
        // walls only on the list — on a single wall somebody pointed, and the
        // answer there is a refusal (§3.4).
        ...(wallIndex === "all"
          ? { fillWallIndices: outline!.segments.map((_, index) => index) }
          : { fillWallIndex: wallIndex }),
      });
      // „Abbrechen“ under the release question since the press: the laying
      // was computed from a board that is gone, and must not bring it back.
      if (ticket !== layingTicketRef.current || store.getState().held) return null;
      const summary =
        wallIndex === "all"
          ? "Alle Wände belegt"
          : replaceExisting
            ? `Wand ${wallIndex + 1} neu aufgeteilt`
            : `Wand ${wallIndex + 1} belegt`;
      announce(summary);
      commit(withHeldOff(answer.placements as Placement[]));
      setResult(answer.result);
      // Named, never silent: „covered everything" and „covered everything it
      // was allowed to" are different answers.
      setSkippedWalls(answer.skippedWalls ?? []);
      // And so are „neu aufgeteilt" and „neu aufgeteilt, und fünf Elemente
      // sind dabei verschwunden". Rückgängig takes one step back over the
      // whole re-laying, which is what makes throwing the old elements away
      // an offer rather than a loss — but only if it is said.
      setRelaid(
        replaceExisting && (answer.replaced ?? 0) > 0
          ? {
              wallIndex: typeof wallIndex === "number" ? wallIndex : null,
              count: answer.replaced ?? 0,
              handTyped: answer.replacedHandTyped ?? 0,
              // Read after the commit above, not off the render's own
              // `history`: that one is a step behind by the time this runs.
              step: store.getState().history.length,
            }
          : null,
      );
      // Whether the drawing moved at all. `null` above and in the catch means
      // „the call did not happen", which is a different answer from „it
      // happened and laid nothing" and must not be worded as one.
      const moved = answer.placements.length !== before || (answer.replaced ?? 0) > 0;
      // The toast after a laying (Welle 1, idea 23), with its way back: on a
      // Vorgang once the Stand that writes it has landed (`persist` — a laying
      // that changed nothing writes nothing and says nothing), without one at
      // once. Read after the commit: the board this laying left.
      const laidDraft = store.getState().current();
      if (caseId !== null) pendingLaidRef.current = { text: summary, draft: laidDraft };
      else if (moved) sayLaid(summary, laidDraft);
      return moved;
    } catch (cause: unknown) {
      setFillFailure(cause instanceof Error ? cause.message : String(cause));
      return null;
    } finally {
      setFilling(null);
    }
  }

  /**
   * „So aufteilen" under a `free_stretch_below_minimum` finding.
   *
   * The same covering as the wall menu's, plus the sentence that was missing:
   * where the free stretch is shorter than the smallest element, `fill_wall`
   * skips it and there is nothing to lay. What belongs on such a stretch is
   * open with Chiara (design §10.1, offene Frage 4) — so this says what
   * happened and names the open question; it does not decide it.
   */
  async function splitAgain(wallIndex: number): Promise<void> {
    const laid = await cover(wallIndex);
    setNothingLaid(
      laid === false ? { wallIndex, step: store.getState().history.length } : null,
    );
  }

  /**
   * „Rohling aufteilen" under a `miter_blank_over_maximum` finding.
   *
   * One press, one step on the undo stack — and only where something was
   * actually divided. A refusal changes nothing, so it must not push a step
   * that Rückgängig would then have to walk back over for no effect.
   *
   * The refusal sentence comes from the endpoint and is shown as it arrives:
   * why a division does not hold is arithmetic done there (E-16 — the answer
   * is a question, not a guess), and wording it a second time here would be a
   * second answer that could drift.
   */
  async function splitOverlongBlank(placementId: string): Promise<void> {
    if (!request) return;
    const ticket = layingTicketRef.current;
    setFillFailure(null);
    try {
      const answer = await splitBlank({ ...request, placementId });
      // As `cover`: an answer for a board „Abbrechen“ put back is dropped.
      if (ticket !== layingTicketRef.current) return;
      if (answer.pieces > 0) {
        announce("Rohling aufgeteilt");
        commit(withHeldOff(answer.placements as Placement[]));
        setResult(answer.result);
        setBlankRefusal(null);
        return;
      }
      setResult(answer.result);
      setBlankRefusal({
        placementId,
        message:
          answer.refusal ??
          "Hier ließ sich nichts teilen. Der Befund bleibt bestehen.",
        // Read after `setResult`, not off the render's own `history`: no step
        // was pushed, so this is the step the refusal was said at.
        step: store.getState().history.length,
      });
    } catch (cause: unknown) {
      setFillFailure(cause instanceof Error ? cause.message : String(cause));
    }
  }

  function downloadQuantities(): void {
    const rows = [
      [
        "Bezeichnung",
        "Wand",
        "Lage in mm",
        "Wandstrecke in mm",
        // What is cut, and how — the workshop builds from these two, not from
        // the wall stretch. A mitre lies across the corner square, so an
        // element can be longer than the wall it sits on (E-47).
        "Rohling in mm",
        "Schnitt links",
        "Schnitt rechts",
        "Breite in mm",
        "Rosttyp",
        // The hole, in the artefact that actually leaves the building. A
        // grating that reaches the shop without it gets built solid, and
        // that is scrap rather than a re-render (§14, „Die Werkstatt muss
        // wissen, *welcher* Rost gelocht wird").
        "Ausschnitt",
        // The two figures a workshop cannot read off the sentence alone —
        // summed over every hole of the element, empty where there is none.
        "Schnittkante in mm",
        "Ausschnitt-Aufschlag netto",
      ],
      ...placements
        .filter((each) => each.kind === "grating")
        .map((each) => {
          const cut = cuts.get(each.id);
          const answerElement = elements.get(each.id);
          const totals = cutoutTotals(answerElement);
          return [
            each.label ?? "",
            String(each.wallIndex + 1),
            String(each.offsetMm),
            String(each.lengthMm),
            String(cut?.blankMm ?? each.lengthMm),
            CUT_NAMES[cut?.start ?? "gerade"],
            CUT_NAMES[cut?.end ?? "gerade"],
            String(effectiveWidthMm(each, specs, jobGratingType)),
            each.gratingType ?? jobGratingType ?? "",
            // The same sentence the element list prints — one wording, one
            // function, so the shop and the screen cannot drift apart.
            cutoutNote(answerElement) ?? "",
            String(totals.cutLengthMm ?? ""),
            // A decimal comma, as the amount reads on screen (walk B-7): the
            // file is split on `;` by a German Excel, which then reads „12.71"
            // as text or as a date and never as a sum. The wire's dot is
            // replaced here and nowhere else — the amount stays a string and
            // is never a `Number` on the way.
            totals.surchargeNet?.replace(".", ",") ?? "",
          ];
        }),
    ];
    // Semicolons, CRLF and a byte-order mark: this file is opened in a German
    // Excel, which reads a comma as a decimal point and a missing BOM as
    // Latin-1 — „Außenfläche" then arrives as mojibake. The escape rather than
    // the literal character because an invisible one in source is a thing
    // nobody can see to fix.
    const csv = rows.map((row) => row.join(";")).join("\r\n");
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "mengenliste.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Lets go of whatever is picked — wall, element, object, hole — so the
   * side column shows the step's own panel again. Escape and a press on the
   * bare board both end here. */
  const clearPick = useCallback(() => {
    const state = store.getState();
    state.select(null);
    state.selectWall(null);
    state.selectFeature(null);
    state.selectCutout(null);
  }, [store]);

  // „Zeichenfläche maximieren" (A3, Entscheid Denis 23.09.2026). Escape is
  // ordered here, not in the hook: see the handler below.
  const {
    maximized,
    toggle: toggleMaximized,
    exit: exitMaximized,
    buttonRef: maximizeRef,
  } = useMaximize({ exitOnEscape: false });

  // Escape belongs to the gesture while one runs — the board stops it there
  // (§5.3) — then to an open menu (which closes itself), then to the tool in
  // hand, and only then to the maximized view: one press, one thing undone.
  // A field somebody is typing in keeps its Escape out of the last step.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      // A dialog over the board owns its Escape — „Freigabe wird ungültig"
      // closes, the wall the question was about stays picked. This listener
      // was registered first and runs first, so the dialog's own
      // `preventDefault` comes too late to tell it (seen in the browser,
      // 15.10.2026: Escape on the question closed the whole wall panel).
      if (event.target instanceof Element && event.target.closest('[role="dialog"]')) return;
      // And where the focus fell out of it to the page — a click into the
      // drawing shown large (review T18-2): the dialog is still open, and
      // the board under it is none of this Escape's business.
      if (modalDialogOpen()) return;
      const hadTool =
        toolInHand(tool, stepInForce(pickedStep, hasGrating), jobGratingType === null) !== "select";
      store.getState().setTool("select");
      if (hadTool || cornerMenu !== null || consoleMenu !== null) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA")
      ) {
        return;
      }
      if (maximized) {
        exitMaximized();
        return;
      }
      // Not maximized, nothing in hand: the pick goes, and the side column
      // shows the step's own panel again — „Alle Wände belegen", the console
      // settings. Before, nothing but a reload let go of a picked wall
      // (Generalprobe 15.10.2026).
      clearPick();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [store, tool, pickedStep, hasGrating, jobGratingType, maximized, cornerMenu, consoleMenu, exitMaximized, clearPick]);

  // ── Rückgängig and Wiederholen ─────────────────────────────────────────
  //
  // Rückgängig is a gesture like any other and writes a version like any
  // other — but it is the one that does not know what it is about to change,
  // so its ops are read off the two drafts themselves (`diffOps`). Without
  // them the board walked back and the server did not: reloading brought the
  // undone mitre straight back (review, Critical: ruling 2). Wiederholen is
  // the same walk the other way, written the same way.
  //
  // One path for the buttons and the keys (⌘Z, ⇧⌘Z, Strg+Y): a key that did
  // anything the button does not would be a second undo model.
  //
  // Each one also keeps the keyboard's place (`keepingFocus`) — a key that
  // takes away the grating whose grip holds the focus, or a button that goes
  // grey under it, would otherwise drop it to <body> — and says what it did
  // in the status region: the keys change the board without moving the
  // focus, and a screen reader would hear nothing at all.
  const segmentCount = outline?.segments.length ?? 0;
  const undoButtonRef = useRef<HTMLButtonElement>(null);
  const redoButtonRef = useRef<HTMLButtonElement>(null);

  function focusAfterStep(lost: Element): HTMLElement | SVGElement | null {
    const other =
      lost === undoButtonRef.current ? redoButtonRef.current : lost === redoButtonRef.current ? undoButtonRef.current : null;
    if (other !== null && !other.disabled) return other;
    return document.querySelector<SVGElement>('[data-role="plan-board"]');
  }

  /** Emptied first and filled a moment later, so the same words twice in a
   * row — two elements moved alike — are read twice. */
  const stepSaidRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (stepSaidRef.current !== null) window.clearTimeout(stepSaidRef.current);
    },
    [],
  );
  function sayStep(sentence: string): void {
    if (stepSaidRef.current !== null) window.clearTimeout(stepSaidRef.current);
    setAnnouncement("");
    stepSaidRef.current = window.setTimeout(() => {
      stepSaidRef.current = null;
      setAnnouncement(sentence);
    }, 50);
  }

  // Without an outline there is no board and no button — and so no key: a
  // step taken where nothing shows it would be found only on the next board.
  const canTakeBack = outline !== null && !readOnly && store.getState().canUndo();
  const canPutBack = outline !== null && !readOnly && future.length > 0;

  // The one check behind every way a step is asked for — the button, the
  // keys and a laying's toast — asked again at the moment of the press. A
  // modal dialog over the board refuses it: its question is about the board
  // as it stands. The button sits in the inert page then and the keys hold
  // still on their own, but the toast host is portalled above the dialog's
  // backdrop on purpose, and its „Rückgängig" would otherwise write a Stand
  // under the question (wave 1 review: „Form ändern …" seeds /umriss from
  // the Stand it opened on).
  function takeBack(): void {
    if (!canTakeBack || modalDialogOpen()) return;
    keepingFocus(() => {
      const before = store.getState().current();
      store.getState().undo();
      const after = store.getState().current();
      announce("Rückgängig", diffOps(outline, before, after));
      if (after === before) return;
      const words = describeStep(after, before, segmentCount);
      sayStep(words === null ? "Rückgängig gemacht." : `Rückgängig gemacht: ${words}`);
    }, focusAfterStep);
  }

  function putBack(): void {
    if (!canPutBack || modalDialogOpen()) return;
    keepingFocus(() => {
      const before = store.getState().current();
      store.getState().redo();
      const after = store.getState().current();
      announce("Wiederholen", diffOps(outline, before, after));
      if (after === before) return;
      const words = describeStep(before, after, segmentCount);
      sayStep(words === null ? "Wiederholt." : `Wiederholt: ${words}`);
    }, focusAfterStep);
  }

  useUndoKeys({ canUndo: canTakeBack, canRedo: canPutBack, onUndo: takeBack, onRedo: putBack });

  /** What the two buttons would do, in words, for their tooltips. */
  const takeBackLabel = useMemo(
    () => (history.length > 0 ? describeStep(history.at(-2) ?? EMPTY_DRAFT, history.at(-1)!, segmentCount) : null),
    [history, segmentCount],
  );
  const putBackLabel = useMemo(
    () => (future.length > 0 ? describeStep(history.at(-1) ?? EMPTY_DRAFT, future.at(-1)!, segmentCount) : null),
    [history, future, segmentCount],
  );

  // A laying's toast (Welle 1, idea 23) offers the same walk back as the
  // button and the keys: one path and one check, its ops read off the
  // drafts, the focus kept and the step said. The toast calls it long after
  // it was shown — the check is asked then, not when the toast went up.
  useEffect(() => {
    undoRef.current = takeBack;
  });

  // An empty screen: the centred card with the mesh sign, not a notice strip
  // at the top of an empty page (Task 11, UX-Gutachten 1, B-15).
  if (!outline) {
    return (
      <div className={styles.screen}>
        <EmptyState
          title="Kein Umriss da."
          actions={
            <>
              <Button onClick={() => navigate("/umriss")}>Umriss zeichnen</Button>
              <Button variant="quiet" onClick={() => navigate("/")}>
                Zum Start
              </Button>
            </>
          }
        >
          Diese Planung setzt einen Umriss voraus. Zeichne ihn, oder lies eine Zeichnung ein.
        </EmptyState>
      </div>
    );
  }

  const wallCount = outline.segments.length;
  /**
   * Without a grating type nothing can be laid — and the screen has to say so
   * *before* the first click, not after.
   *
   * A width is a manufacturing figure and A-014 forbids inventing one (§3.2).
   * The endpoint refuses accordingly. Found by running it: the first version
   * let a person place an element and then answered 422, which is the rule
   * being kept in the only place nobody can see it.
   */
  const typeMissing = jobGratingType === null;

  /**
   * „Rosttyp“ in the head is a choice (spec 2026-10-23 R-7) on a Vorgang
   * that states one and whose facade may still change. Not on the register
   * demo's board (no Vorgang to write to), not from „Angebot raus“ on, not
   * on a cancelled Vorgang, not on a board a chamfer froze (`readOnly`).
   */
  const typeChoosable = caseId !== null && statedType !== null && !readOnly;
  /**
   * What the mails ask for that the board does not do as they say (Task 15a):
   * another type than the Vorgang's — `mailRequest.gratingType` names it even
   * where it is the Vorgang's own (Task 13e), so the board compares —, a
   * coating, a welded grating. On a Vorgang whose facade may still change;
   * nothing from „Angebot raus“ on, nothing on a cancelled one, nothing where
   * no mail was read (`mailRequest: null`).
   */
  const mailRequest = caseDetail.data?.mailRequest ?? null;
  const mailNotes: { key: string; text: string; take: GratingTypeWord | null }[] = [];
  if (caseId !== null && mailRequest !== null && editability !== "sent" && editability !== "cancelled") {
    const named = mailRequest.gratingType;
    if (named !== null && isGratingTypeWord(named) && named !== (typeArriving ?? statedType)) {
      mailNotes.push({
        key: "type",
        text: `Die Mail nennt ${gratingTypeLabel(named)}.`,
        take: typeChoosable ? named : null,
      });
    }
    if (mailRequest.weldedNamed) mailNotes.push({ key: "welded", text: WELDED_NOT_PRICED, take: null });
    const coating = coatingSentence(mailRequest);
    if (coating !== null) mailNotes.push({ key: "coating", text: coating, take: null });
  }
  /** „Barfußrost übernehmen“: the choice takes it, question and all — and keeps the focus, as at the Eingang. */
  function takeMailType(type: GratingTypeWord): void {
    document.getElementById("job-grating-type")?.focus();
    typeHandleRef.current?.choose(type);
  }
  /**
   * What a change of type does to the board, for its question (R-7, R-10) —
   * and what a person set by hand that stays through it (Ruling G-1): the
   * brackets with a hand-picked article, the typed blanks. `null` where
   * nothing lies.
   */
  const laidGratings: LaidGratings | null = laidGratingsOf(placements, consoleOverrides);

  /** The change is out („Speichert …“), or back without a Stand of its own to show yet. */
  function typeWriting(writing: boolean): void {
    countOtherWrite(writing);
    if (writing) {
      setSaveState({ kind: "saving" });
      return;
    }
    // Went through: „Speichert …“ stays until the plan with the new Stand
    // lands, and the hydration says „Gespeichert“. Refused or failed: the
    // board stands where it stood.
    if (awaitingHydrationRef.current) return;
    const standing = versionRef.current;
    if (standing === null) return;
    const settled = settledSaveState(standing);
    setSaveState((state) => (state?.kind === "saving" ? settled : state));
  }

  /**
   * The Vorgang has the new type, and the server wrote a Stand for it (R-1)
   * — the number its answer names, which the board's lock takes at once
   * (Ruling G-3): the plan read again may land before the Vorgang is, and a
   * gesture made in between posted against the Stand before the change and
   * came back 409. Nothing of the board's own goes out until the plan that
   * carries the change is back (`awaitingHydrationRef`, as after a 409), and
   * the head's select waits with it (`typeArriving`): the plan answer brings
   * the new type with its width, and the Vorgang its status — „In Planung“
   * again where it was released. The start page's row moves with it.
   */
  function typeChanged(saved: GratingTypeOut, next: GratingTypeWord): void {
    // Landed: the release went with it.
    if (editability === "approved") {
      releaseSpentRef.current = true;
      setReleaseSpent(true);
    }
    versionRef.current = saved.latestVersionNo;
    awaitingHydrationRef.current = true;
    typeAwaitingRef.current = true;
    setTypeChange({ type: saved.gratingType ?? next, hydratedAt: store.getState().hydratedAt });
    setTypeRefusal(null);
    reload();
    void queryClient.invalidateQueries({ queryKey: CASES_KEY });
    // What was set by hand stays — said again once it went through (Ruling G-1).
    const handSet = laidGratings !== null ? handSetSentence(laidGratings) : "";
    setAnnouncement(
      [`Rosttyp: ${gratingTypeLabel(next)}. Die Planung wird neu geladen.`, handSet].filter((part) => part !== "").join(" "),
    );
  }
  /**
   * A change of type refused with a 409 — the Vorgang was cancelled, or its
   * offer went out, under the board. Said above the board, where the
   * board's own 409s are said, and the Vorgang read again; nothing of the
   * board's goes out until its plan is back. **The Vorgang first, then the
   * plan:** the head gives way to the reading once the Vorgang's status is
   * in, and the select with the focus goes — the hydration that follows
   * hands the focus to this notice (`focusMessagesRef`, V-3). A plan that
   * landed first found the focus still on the select it was about to take
   * away.
   */
  function typeConflict(sentence: string): void {
    if (caseId === null) return;
    setTypeRefusal(sentence);
    setAnnouncement(sentence);
    // The board itself lost nothing: it stands where it stood, saved.
    const standing = versionRef.current;
    if (standing !== null) setSaveState({ kind: "saved", versionNo: standing });
    awaitingHydrationRef.current = true;
    focusMessagesRef.current = true;
    void queryClient.invalidateQueries({ queryKey: CASE_KEY(caseId), exact: true }).then(() => {
      store.getState().askHydration();
      void queryClient.invalidateQueries({ queryKey: ["case", caseId, "plan"] });
    });
  }
  /**
   * No wall carries a typed or read length (Denis, 19.10.2026). Since then
   * no Stand is written without one (`append_version`), but a Stand from
   * before — Vorgang 2026-0009 — opens here with nothing a grating could lie
   * against. Laying is held and the banner says how on: pick a wall and type
   * its „Maß"; the server takes that gesture, and only that one, first.
   */
  const unmeasured =
    outline !== null && outline.segments.length > 0 && outline.segments.every((segment) => segment.lengthMm === null);
  /**
   * The walls the release will stop at (UX-Gutachten 23.10.2026, Nr. 6) —
   * said on the board, with the way out, for as long as one stands; on a
   * Vorgang whose facade may still change. Not beside the banner above,
   * which holds the laying until a first wall is measured and says how. The
   * register demo has no release: there only what a laying left out is
   * said (`skippedWalls`).
   */
  const wallsWithoutMeasure =
    caseId !== null &&
    outline !== null &&
    editability !== "sent" &&
    editability !== "cancelled" &&
    !(unmeasured && !readOnly)
      ? unmeasuredWallsOf(outline.segments)
      : [];
  /** What the last laying left out — on a Vorgang, `wallsWithoutMeasure` says it already. */
  const skippedSaid = caseId === null ? skippedWalls : [];
  /**
   * Which step the screen is on (design 2026-09-22 §3).
   *
   * `null` in the store means „follow the board": no grating yet → objects,
   * grating laid → grating. „Konsolen" picked and then the last grating taken
   * off again (Rückgängig) falls back to „Roste" — the step opens with the
   * first grating and closes with the last, in both directions.
   */
  const effectiveStep: PlanningStep = stepInForce(pickedStep, hasGrating);
  const stepSpec = STEPS.find((each) => each.step === effectiveStep)!;
  /** The tools this step offers — „Auswählen" first, then the step's own. */
  const stepTools = TOOLS.filter(
    (each) => each.tool === "select" || stepSpec.tools.includes(each.tool),
  );
  /**
   * Which tool is actually in hand.
   *
   * The stored tool survives a reload; the grating type does not (it belongs
   * to the job, not to the draft). Coming back into a restored draft, the
   * toolbar therefore showed „Fassadenobjekt" ticked *and* greyed out, with
   * its whole „Art" form open under it and nothing that could be drawn (walk
   * finding 16). Without a type nothing may be laid, so without a type the
   * tool in hand is „Auswählen" — and the one somebody put down comes back
   * the moment they answer the question above.
   *
   * The same on a step change: a tool that belongs to another step is not in
   * hand here, and comes back on the way back into its step. The store keeps
   * it; this screen decides what it means.
   */
  const effectiveTool: Tool = toolInHand(tool, effectiveStep, typeMissing || readOnly);
  const activeTool = TOOLS.find((each) => each.tool === effectiveTool)!;
  /** What the status line says with nothing to do on the board but look. */
  const readOnlyStatus =
    editability === "cancelled"
      ? "Nur ansehen — der Vorgang ist storniert."
      : editability === "sent"
        ? "Nur ansehen — das Angebot ist raus."
        : "Nur ansehen — die Fase lässt sich an dieser Form nicht einzeichnen. Ein Maß lässt sich an der Wand korrigieren.";
  const gratingCount = placements.filter((each) => each.kind === "grating").length;
  const coveredWalls = new Set(
    placements.filter((each) => each.kind === "grating").map((each) => each.wallIndex),
  ).size;
  const consoleCount = result?.totals.consoles ?? null;
  /** One line per step, under its name: what stands, in the step's own words. */
  const stepState: Record<PlanningStep, string> = {
    objects:
      features.length === 0
        ? "noch keins gesetzt"
        : features.length === 1
          ? "1 Objekt gesetzt"
          : `${features.length} Objekte gesetzt`,
    grating: !hasGrating
      ? "noch keiner gesetzt"
      : `${gratingCount} auf ${coveredWalls === 1 ? "1 Wand" : `${coveredWalls} Wänden`}`,
    consoles: !hasGrating
      ? "nach den Rosten"
      : consoleCount === null
        ? "werden gerechnet"
        : consoleCount === 1
          ? "1 gerechnet"
          : `${consoleCount} gerechnet`,
  };
  const stepDone: Record<PlanningStep, boolean> = {
    objects: features.length > 0,
    grating: hasGrating,
    consoles: false,
  };

  /**
   * A change of step is shown, not jumped (idea 26): the side column, the
   * tools and the strip cross-fade, the board stays where it is under the
   * pointer. At once where the browser has no view transitions or the person
   * asked for less motion.
   */
  function goToStep(step: PlanningStep): void {
    withViewTransition(() => store.getState().setStep(step));
  }

  /**
   * „Alle Wände belegen“ from the hint after a form change: the same laying
   * as the step's own button, and then the hint has said what it had to — it
   * goes, and the focus with it to the heading, as after its ×. A laying that
   * failed leaves it standing; the failure is said where it always is.
   */
  async function layAfterFormChange(): Promise<void> {
    goToStep("grating");
    const laid = await cover("all");
    if (laid === null) return;
    setFormChanged(false);
    headingRef.current?.focus();
  }

  // ── Fall C: the splitting rule and what it would lay (design 2026-10-14) ──
  //
  // The catalogue and the variants come with every planning answer; the
  // priority is the store's. Nothing here re-lays a wall: the select is a
  // prefill, „Wand neu aufteilen" the one gesture that lays a variant.
  const splitRules: DemoSplitRule[] = result?.splitRules ?? [];
  const splitVariants: DemoWallSplitVariants[] = result?.splitVariants ?? [];
  const splitRule = splitRules.find((each) => each.priority === splitPriority) ?? null;
  /** „Roste nach „Standardlänge zuerst““ — what „Wand belegen" lays, in the
   * hints under both buttons. The old wording until the catalogue is there;
   * it is the default rule's own. */
  const layingWords = splitRule ? `Roste nach „${splitRule.label}“` : "1.000er-Elemente";

  function chooseSplitPriority(priority: SplitPriority): void {
    if (priority === splitPriority) return;
    // A Vorgang-level setting is a gesture — it travels on every version, so
    // it has to say so or the settle writes nothing (the bracket settings'
    // reason, fix round 1, item 11).
    announce("Aufteilung geändert");
    store.getState().setSplitPriority(priority);
  }

  const splitMismatch = splitRule
    ? mismatchSentence(splitVariants, splitPriority, splitRule.label)
    : null;

  // ── „Maß“ on the picked wall (spec 2026-10-15 §2) ────────────────────
  //
  // Only on a Vorgang (there is nothing to post to on the register demo) and
  // only on a wall the stored document names — an op under an invented id
  // would brick the Vorgang at its next replay ({@link wallIdFor}).
  const measureWall = selectedWall !== null ? (outline.segments[selectedWall] ?? null) : null;
  const measureWallId = selectedWall !== null ? wallIdFor(outline, selectedWall) : null;
  const measureWalls = directedWalls(outline);
  const measureAxis = selectedWall !== null ? axisOf(directionOf(outline, selectedWall)) : null;
  // A remainder only means something on a ring: an open outline need not
  // come back to where it started (E-14).
  const measureGap =
    measureAxis !== null && outline.connected && outline.closureEvaluable !== false
      ? axisGap(measureWalls, measureAxis)
      : null;

  /**
   * The Stand a measure gesture states (final review I-1). On an open board
   * it is the board as it stands (`boardState`). Where the chamfer cannot be
   * drawn (`faseBlocked`) the board shows the server's answer on the uncut
   * ring, and its placements count those walls — the record does not: it is
   * the replay's `planningState` **as loaded**, in the cut ring's numbering,
   * and a measure written there states exactly that, so the next cut finds
   * every grating where it was laid. `null` where a blocked answer carries no
   * stored state to send (a fixture): then there is no field.
   */
  const measureState: PlanningStateWire | null = faseBlocked
    ? ((initialResult?.planningState ?? null) as PlanningStateWire | null)
    : boardState;
  /**
   * The one control a blocked board keeps (final review I-1): „Maß“ on a
   * picked wall. A gap above the tolerance stays visible — the geometry is
   * the error detector (E-13) — and correcting the figure that opened it is
   * the way back to a drawable chamfer without „Form ändern …“ dropping the
   * planning. Only for the chamfer: a cancelled or sent Vorgang stays shut.
   */
  const measureOnlyOpen =
    faseBlocked && editability !== "cancelled" && editability !== "sent" && measureState !== null;
  function measureSaved(nextVersionNo: number, wallIndex: number): void {
    // The measure asked about the release itself; the server took it.
    if (editability === "approved") {
      releaseSpentRef.current = true;
      setReleaseSpent(true);
    }
    // As `persist` does on its own writes: the number moves on at once, and
    // the cached Vorgang with it — only ever forward.
    versionRef.current = nextVersionNo;
    queryClient.setQueryData<CaseDetailOut>(CASE_KEY(caseId), (old) =>
      old && old.latestVersionNo < nextVersionNo
        ? { ...old, latestVersionNo: nextVersionNo }
        : old,
    );
    lastSavedStateRef.current = measureState;
    // The plan the field now asks for comes back through a hydration, which
    // would let go of this wall — and close the panel the ring's sentence is
    // about to appear in. This one hydration keeps it.
    store.getState().holdPick({ wallIndex, step: pickedStep });
    if (faseBlocked) heldWallIdRef.current = measureWallId;
    setSaveState(settledSaveState(nextVersionNo));
    // Seen, not heard: the field's own live line says „Maß gesetzt." for
    // this gesture, and a second sentence on top of it would be one too many.
    sayStand({ text: savedSentence(nextVersionNo), announce: false });
  }

  /** The field's write is on its way, or has ended without a Stand (refused, failed). */
  function measureWriting(writing: boolean): void {
    countOtherWrite(writing);
    if (writing) setSaveState({ kind: "saving" });
    else {
      // Not optimistic: a measure that did not land changed nothing on the
      // board, so the board stands where it stood — saved, unless a gesture
      // of its own still waits (`settledSaveState`). A conflict keeps its word.
      const standing = versionRef.current;
      if (standing === null) return;
      const settled = settledSaveState(standing);
      setSaveState((state) => (state?.kind === "saving" ? settled : state));
    }
  }

  /**
   * A figure the sketch contradicts, answered from its row (E-64) — written
   * the way „Maß“ writes: the same Stand, lock, settle and 409. `null` where
   * nothing may be written; the row then says its sentence alone.
   */
  const implausibleTarget: ImplausibleWriteTarget | null =
    caseId !== null &&
    versionNo !== null &&
    measureState !== null &&
    editability !== "sent" &&
    editability !== "cancelled"
      ? {
          caseId,
          expectedVersionNo: versionNo,
          planningState: measureState,
          // Spent here already, no second question (M-2).
          editability: editability === "approved" && !releaseStands ? "open" : editability,
          approval,
          settle: settleWrites,
          onSaved: implausibleSaved,
          onWriting: measureWriting,
          onStale: acceptStale,
        }
      : null;

  function implausibleSaved(nextVersionNo: number, wallIndex: number): void {
    measureSaved(nextVersionNo, wallIndex);
    // `measureSaved` follows the picked wall's id; this one is the row's.
    if (faseBlocked) heldWallIdRef.current = wallIdFor(outline, wallIndex);
    // Heard as well as seen: no field speaks for this gesture, and the row
    // goes with its finding once the replay answers.
    sayStand({ text: savedSentence(nextVersionNo) });
    // The focus would go with that row — it goes to the wall just decided.
    focusMeasureWallRef.current = wallIndex;
    store.getState().selectWall(wallIndex);
  }

  /** „Maß eintippen“: the wall picked, its „Maß“ in focus once its panel is there (M-8). */
  function typeImplausible(wallIndex: number): void {
    if (store.getState().selectedWall === wallIndex) {
      focusMeasureField();
      return;
    }
    focusMeasureWallRef.current = wallIndex;
    store.getState().selectWall(wallIndex);
  }

  const measureField: ReactNode =
    caseId !== null &&
    versionNo !== null &&
    selectedWall !== null &&
    measureWall !== null &&
    measureWallId !== null &&
    measureState !== null ? (
      isFase(measureWall) ? (
        // The slant's measure is the chamfer's, worked out from the legs a
        // person typed in the editor — no field here, and where it changes
        // is said (design 2026-10-18 §6.2, call 2).
        <div className={styles.field} data-role="fase-measure">
          <div className={styles.reading}>
            <span className={`label ${styles.fieldLabel}`}>Länge</span>
            <span className={`num ${styles.readingValue}`}>{formatMm(measureWall.lengthMm)}</span>
          </div>
          <p className={styles.hint}>{FASE_MEASURE}</p>
        </div>
      ) : (
        <WallMeasureField
          // A fresh field per wall: one wall's half-typed figure must not
          // stand in the next wall's field.
          key={measureWallId}
          caseId={caseId}
          wallId={measureWallId}
          wallName={`Wand ${selectedWall + 1}`}
          // On a wall a chamfer shortened, the customer's measure up to the
          // corner point — what they stated and what „Maß ändern“ writes into
          // the document under this same wall id (design §5.1); the usable
          // stretch follows from it and stands beside the field.
          lengthMm={measureWall.cornerPointLengthMm ?? measureWall.lengthMm}
          usable={shortenedWallLabel(measureWall)}
          lengthSource={measureWall.lengthSource}
          displacedReadingMm={measureWall.displacedReadingMm ?? null}
          // A figure the sketch contradicts (E-64) is what „Maß eintippen“
          // lands on — the field says it as the board does, not „ohne Maß“.
          disputedMm={measureWall.lengthMm === null ? (measureWall.implausibleLengthMm ?? null) : null}
          expectedVersionNo={versionNo}
          planningState={measureState}
          // Spent here already, no second question (M-2).
          editability={editability === "approved" && !releaseStands ? "open" : editability}
          approval={approval}
          closure={closureSentence(outline)}
          axisGapMm={measureGap}
          settle={settleWrites}
          onSaved={(next) => measureSaved(next, selectedWall)}
          onWriting={measureWriting}
          onStale={acceptStale}
        />
      )
    ) : null;

  /**
   * „Maß“ open on a blocked board (`measureOnlyOpen`) — and only while the
   * wall's own panel is the one shown: an element, a hole or an object picked
   * keeps its panel held exactly as before.
   */
  const measureOnly =
    measureOnlyOpen &&
    measureField !== null &&
    picked === null &&
    pickedCutout === null &&
    pickedFeature === null &&
    effectiveTool !== "feature" &&
    selectedWall !== null;

  // The board's own strong action, red — unless the Vorgang is cancelled:
  // then „Wiederherstellen …“ in the notice above the board is the one red
  // action (design 2026-10-24 §8.2), and the held buttons here stand in ink
  // rather than as faded reds beside it.
  const boardMain = editability === "cancelled" ? "tinte" : "primary";

  const refreshButton = hasGrating ? (
    /* The refresh the process needs, in the one place that is always on
       screen: covering a wall and *then* moving the sill is the ordinary way
       round, not a mistake, and before this the way back was a right-click
       on a band nobody had reason to find (Denis, 03.09.2026 — „eine Art
       Aktualisierungs-Button"). It re-lays every measured wall around
       whatever stands on it, in one step on the undo stack. */
    <Button
      variant={planStale ? boardMain : "secondary"}
      data-role="refresh-plan"
      data-stale={planStale}
      onClick={() => void cover("all", true)}
      disabled={readOnly || typeMissing || filling !== null}
    >
      {filling === "all"
        ? "Planung wird aktualisiert …"
        : planStale
          ? "Planung aktualisieren •"
          : "Planung aktualisieren"}
    </Button>
  ) : null;

  const cutoutDefaults = (
    /* The cutout half of the Vorgang's settings (design §8, D-10): the
       clearance a linked downpipe hole is prefilled with. The two fields of
       the per-case surcharge rate (D-6) are gone since 15.10.2026 — the cut
       price comes from the supplier's rules in Max and stands in the
       calculation (E-51/D-8); one sentence says so where the fields stood.
       Folded away: a field nobody touches twice a month must not stand
       between a person and the board. */
    <details className={styles.defaults}>
      <summary className={styles.defaultsSummary}>
        Vorgaben für Ausschnitte
        <span className={`num ${styles.defaultsValues}`}>{formatMm(cutoutClearanceMm)} Spiel</span>
      </summary>
      <div className={styles.defaultsBody}>
        <NumberField
          id="cutout-clearance"
          label="Montagespiel"
          value={cutoutClearanceMm}
          min={0}
          hint="mm Spiel um ein Fallrohr — Lochseite = Rohrbreite + Spiel"
          onChange={(mm) => {
            announce("Montagespiel geändert");
            store.getState().setCutoutClearanceMm(mm);
          }}
        />
        <p className={styles.hint} data-role="cutout-price-source">
          Der Aufschlag kommt aus der Preisliste des Lieferanten und steht in der Kalkulation.
        </p>
      </div>
    </details>
  );

  /** What the side column shows while nothing is picked — the step's own
   * empty state, which is an instruction and never a mood (CI §5). */
  const idlePanel =
    effectiveStep === "objects" ? (
      <>
        <p className={`label ${styles.panelHead}`}>Objekte an der Fassade</p>
        <p className={styles.idle}>
          Türen, Fensterbänke, Fallrohre und Stufen mit „Fassadenobjekt" auf die Wand setzen.
          Die Roste teilen sich später daran. „Außenfläche" schraffiert Kies und Umgebung.
        </p>
        {!hasGrating ? (
          <>
            <p className={styles.idleTitle}>Noch kein Rost gesetzt.</p>
            {/* The whole house, without picking a wall first: the single-wall
                button lives in the wall panel, which only exists once a wall
                is picked — this one must not hide behind a pick (plan Task
                2). From here it is also the way into step 2: the objects
                stand, the grating comes next. */}
            <Button
              variant={boardMain}
              onClick={() => {
                goToStep("grating");
                void cover("all");
              }}
              disabled={typeMissing || unmeasured || filling !== null}
            >
              {filling === "all" ? "Alle Wände werden belegt …" : "Alle Wände belegen"}
            </Button>
            <span className={styles.hint}>
              Objekte stehen? Das legt {layingWords} über jede bemaßte Wand — bündig an
              jedem Objekt, das die Rostbreite ändert — und geht weiter zu den Rosten.
            </span>
          </>
        ) : (
          <Button variant="secondary" onClick={() => goToStep("grating")}>
            Weiter zu den Rosten
          </Button>
        )}
      </>
    ) : effectiveStep === "grating" ? (
      <>
        <p className={`label ${styles.panelHead}`}>Roste</p>
        {hasGrating ? (
          <p className={styles.idle}>
            Wand anklicken, um sie einzeln zu belegen. Element anklicken zum Ändern.
          </p>
        ) : (
          <p className={styles.idleTitle}>Noch kein Rost gesetzt.</p>
        )}
        {/* Before the button it decides: choose, then lay. */}
        <SplitPriorityField
          id="split-priority"
          rules={splitRules}
          value={splitPriority}
          onChange={chooseSplitPriority}
          // No wall is picked here, so a changed priority changes nothing
          // anyone can see — this line says which walls it leaves standing.
          status={splitMismatch ?? ""}
        />
        <Button
          variant={hasGrating ? "secondary" : boardMain}
          onClick={() => void cover("all")}
          disabled={typeMissing || unmeasured || filling !== null}
        >
          {filling === "all" ? "Alle Wände werden belegt …" : "Alle Wände belegen"}
        </Button>
        <span className={styles.hint}>
          Legt {layingWords} über jede bemaßte Wand — bündig an jedem Objekt, das die
          Rostbreite ändert. Wände ohne Maß bleiben frei und werden genannt.
        </span>
        {hasGrating ? (
          <Button variant="secondary" onClick={() => goToStep("consoles")}>
            Weiter zu den Konsolen
          </Button>
        ) : null}
        {cutoutDefaults}
      </>
    ) : (
      <>
        <p className={`label ${styles.panelHead}`}>Konsolen</p>
        {consoleCount !== null ? (
          <div className={styles.reading}>
            <span className={`label ${styles.fieldLabel}`}>Gerechnet</span>
            <span className={`num ${styles.readingValue}`}>
              {consoleCount} {consoleCount === 1 ? "Konsole" : "Konsolen"}
            </span>
            {/* Not the `label` class: that one sets small caps, and this is a
                sentence — „10 KONSOLEN — 1 LAUF, N+1" is unreadable and reads
                as a heading rather than as the reasoning it is (measured in
                the browser, 16.08.2026). */}
            {derivation ? <span className={styles.derivation}>{derivation}</span> : null}
          </div>
        ) : (
          <p className={styles.idle}>Die Konsolen werden gerechnet …</p>
        )}
        {/* The two Vorgang-level settings task 11 puts in the person's hand —
            here and not in the header: they are about the brackets, and the
            brackets come after the grating. Both travel on every planning
            request from here on (§`request` above). */}
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="console-adjustable-first">
            Konsolentyp
          </label>
          <Select
            id="console-adjustable-first"
            className={styles.input}
            value={consoleAdjustableFirst ? "vario" : "solid"}
            onChange={(event) => {
              // A Vorgang-level setting is a gesture too — it travels on
              // every plan request and on every version, so it has to say so
              // or the settle below writes nothing at all (fix round 1,
              // item 11).
              announce("Konsolentyp geändert");
              store.getState().setConsoleAdjustableFirst(event.target.value === "vario");
            }}
          >
            <option value="vario">verstellbar (Vario)</option>
            <option value="solid">fest (Solid)</option>
          </Select>
        </div>
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="console-series">
            Konsolenserie
          </label>
          <Select
            id="console-series"
            className={styles.input}
            value={consoleSeries}
            onChange={(event) => {
              announce("Konsolenserie geändert");
              store.getState().setConsoleSeries(event.target.value as ConsoleSeries);
            }}
          >
            <option value="solid_1">Solid 1.0 (von oben verschraubt)</option>
            <option value="solid_2">Solid 2.0 (seitlich verschraubt)</option>
          </Select>
          {/* The two series carry the same load and differ only in how they
              reach the Kerb — one screw from above, two from the side. That
              is exactly why this is a setting nobody derives (task 11
              brief). */}
          <span className={styles.hint}>
            Wirkt nur, wo die Zuordnung auf eine feste Konsole fällt — für Vario ändert sich
            nichts.
          </span>
        </div>
        <span className={styles.hint}>
          Rechtsklick auf eine Konsole wählt den Artikel. „Konsole extra" setzt eine
          zusätzliche zu den gerechneten.
        </span>
      </>
    );

  return (
    <div className={styles.screen}>

      {mailNotes.length > 0 ? (
        /* What the mails ask for (Task 15a, UX-Zweitgutachten Nr. 8/9) —
           under the head, before a grating is laid: a quiet block, no status
           colour, the words carry it. A type of its own one press away, the
           way the choice above takes it. */
        <section className={styles.mailNotes} aria-label="Aus der Mail">
          <p className={`label ${styles.mailNotesHead}`}>Aus der Mail</p>
          <ul className={styles.mailNotesList}>
            {mailNotes.map((note) => (
              <li key={note.key} className={styles.mailNote}>
                <span>{note.text}</span>
                {note.take !== null ? (
                  <Button onClick={() => takeMailType(note.take!)}>
                    {gratingTypeLabel(note.take)} übernehmen
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p role="status" className="visually-hidden">
        {announcement}
      </p>
      {/* Above everything the board says (V-1, V-5): why nothing on it moves,
          and on a cancelled Vorgang the way back — the record's own notice. */}
      {caseId !== null && editability === "cancelled" ? (
        <CancelledNotice
          cancellation={caseDetail.data?.cancellation ?? null}
          action={
            <Button
              variant="primary"
              aria-describedby="case-cancelled"
              onClick={() => navigate(restorePath(caseId))}
            >
              Wiederherstellen …
            </Button>
          }
        />
      ) : caseId !== null && editability === "sent" ? (
        <StateMessage title={FACADE_SENT} live={false} />
      ) : null}
      {arrived !== null && arrivedShown ? (
        <StateMessage
          tone="success"
          title={arrived}
          live={false}
          onDismiss={() => {
            setArrivedShown(false);
            // The × is gone with its notice: the page's heading takes the focus.
            headingRef.current?.focus();
          }}
        />
      ) : null}
      {unmeasured && !readOnly ? (
        <StateMessage id="needs-measure" strong title={NEEDS_MEASURE} live={false}>
          Ohne ein getipptes oder gelesenes Maß kann kein Rost liegen. Wand anklicken und unter „Maß“
          ihre Länge eintragen — danach lassen sich die Wände belegen.
        </StateMessage>
      ) : null}
      {formChanged && !hasGrating && !unmeasured ? (
        <StateMessage
          id="form-changed"
          strong
          title={FORM_CHANGED}
          live={false}
          action={
            // Linie, not ink: step 1's own panel holds the same gesture as
            // its one ink button, and two of them side by side would
            // compete for one decision.
            <Button
              variant="secondary"
              onClick={() => void layAfterFormChange()}
              disabled={typeMissing || readOnly}
              busy={filling !== null}
            >
              {filling === "all" ? "Alle Wände werden belegt …" : "Alle Wände belegen"}
            </Button>
          }
          onDismiss={() => {
            setFormChanged(false);
            headingRef.current?.focus();
          }}
        >
          Die neue Form ist gespeichert, alles von vorher auf der Fassade ist entfernt. Setz zuerst die
          Objekte, wenn sich die Roste an ihnen teilen sollen.
        </StateMessage>
      ) : null}

      {/* Maximized, this section is the overlay: strip, tools, board, panel
          and findings over the whole window (fixed, not the Fullscreen API —
          the tools have to stay in reach). */}
      <section className={styles.work} aria-label="Belegung" data-maximized={maximized}>
        {/* The work's head (UX-Gutachten 2, B-6): the board began at 57 %
            of a 900-px window, under a head of title, lede, save state and
            facts. The screen's title, the steps and where the board stands
            with the server now share one band at the top of the work; the
            lede is gone, the job's facts and „Form ändern …" stand in the
            tool row. A `header`, so the save state is still the head's. */}
        <header className={styles.strip}>
          <PageTitle headingRef={headingRef} tabIndex={-1} className={styles.title}>
            {PLANNING_TITLE}
          </PageTitle>
          {/* The order the work is done in, as the thing that steers the screen
              and not as a sentence above it (design 2026-09-22 §2). A stage
              path with words — no ring, no percentage (CI §6). The third step
              says „nach den Rosten" and opens with the first grating: the
              brackets are the answer to a laid grating, never something laid
              before it. „Planungsschritte", a name of its own: two navigations
              of one name on this page could not be told apart (round-4 screen
              check, R4-2 — then beside the rail's landmark, now beside the
              masthead's „Hauptnavigation" and the frame's „Vorgang"). */}
          <nav className={styles.steps} aria-label="Planungsschritte">
            <ol className={styles.stepList}>
              {STEPS.map((spec, index) => {
                const locked = spec.step === "consoles" && !hasGrating;
                const current = spec.step === effectiveStep;
                return (
                  <li key={spec.step} className={styles.stepItem}>
                    <button
                      type="button"
                      className={`${styles.step} ${current ? styles.stepCurrent : ""} ${
                        locked ? styles.stepLocked : ""
                      } ${stepDone[spec.step] && !current ? styles.stepDone : ""}`}
                      aria-current={current ? "step" : undefined}
                      aria-disabled={locked || undefined}
                      data-step={spec.step}
                      onClick={() => {
                        if (!locked) goToStep(spec.step);
                      }}
                    >
                      <span className={`num ${styles.stepNo}`} aria-hidden="true">
                        {index + 1}
                      </span>
                      <span className={styles.stepText}>
                        <span className={styles.stepName}>{spec.name}</span>{" "}
                        <span className={styles.stepState}>{stepState[spec.step]}</span>
                      </span>
                      {stepDone[spec.step] && !current ? (
                        <span className={styles.stepMark} aria-hidden="true">
                          <StepCheck />
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
          {/* Where the board stands with the server (Welle 1, idea 23) — only
              on a Vorgang; the register demo writes nothing. Plain text, not a
              live region: the toast says a save, the messages above the board
              say a failure, and this line is where a person looks it up. At
              the strip's right end (UX-Gutachten 2, B-6). */}
          {caseId !== null && saveState !== null ? (
            <p className={styles.saveState} data-role="save-state" data-state={saveState.kind}>
              {saveState.kind === "saved" ? (
                <>
                  <CheckIcon size={14} className={styles.saveMark} />
                  <span>
                    Gespeichert · Stand <span className="num">{saveState.versionNo}</span>
                  </span>
                </>
              ) : saveState.kind === "saving" ? (
                <span>Speichert …</span>
              ) : (
                <>
                  <AlertIcon size={14} className={styles.saveMark} />
                  <span>Nicht gespeichert — {saveState.reason}</span>
                </>
              )}
            </p>
          ) : null}
        </header>

        {/* Asked before the first click, not refused after it. Without a type
            there is no width, and a width is a manufacturing figure (A-014).
            The tools stay out of reach until it is answered — a toolbar that
            works and then fails silently is worse than one that waits. */}
        {typeMissing ? (
          <div className={styles.gate}>
            <label className={`label ${styles.fieldLabel}`} htmlFor="job-type">
              Rosttyp
            </label>
            <Select
              id="job-type"
              className={styles.input}
              value=""
              onChange={(event) => setPickedType(event.target.value || null)}
            >
              <option value="">bitte wählen</option>
              {(specs.length > 0 ? specs.map((each) => each.type) : Object.keys(GRATING_TYPE_NAMES)).map(
                (type) => (
                  <option key={type} value={type}>
                    {GRATING_TYPE_NAMES[type] ?? type}
                  </option>
                ),
              )}
            </Select>
            <span className={styles.hint}>
              Der Vorgang nennt keinen. Ohne Rosttyp gibt es keine Breite — und eine Breite ist ein
              Fertigungsmaß, das sich niemand ausdenken darf.
            </span>
          </div>
        ) : null}


        <div className={styles.toolRow}>
          {/* A radio group, and a radio group is steered with the arrow keys.
              The first version had the roving tabindex without the handler —
              which is the half that makes the other half unusable: Tab reaches
              exactly the one tool that is already active, and nothing moves
              from there. Without a mouse the tool could not be changed at all
              (found in review, 16.08.2026).

              „Rückgängig" and „Wiederholen" sit outside the group, not inside
              it: they are not tools, and a screen reader counting „Werkzeug,
              4 von 4" would say they are. */}
          <div className={styles.toolbar} role="radiogroup" aria-label="Werkzeug">
            {stepTools.map((each) => (
              <button
                key={each.tool}
                type="button"
                role="radio"
                aria-checked={effectiveTool === each.tool}
                tabIndex={effectiveTool === each.tool ? 0 : -1}
                disabled={(typeMissing || readOnly) && each.tool !== "select"}
                className={`${styles.tool} ${effectiveTool === each.tool ? styles.toolActive : ""}`}
                onClick={() => store.getState().setTool(each.tool)}
                onKeyDown={(event) => {
                  const step =
                    event.key === "ArrowRight" || event.key === "ArrowDown"
                      ? 1
                      : event.key === "ArrowLeft" || event.key === "ArrowUp"
                        ? -1
                        : 0;
                  const jump = event.key === "Home" ? "first" : event.key === "End" ? "last" : null;
                  if (step === 0 && jump === null) return;
                  event.preventDefault();
                  // Wrapping, and skipping what a missing grating type has put
                  // out of reach — arrowing onto a disabled tool would look
                  // like the keyboard is broken.
                  const reachable = stepTools.filter(
                    (candidate) => !((typeMissing || readOnly) && candidate.tool !== "select"),
                  );
                  const here = reachable.findIndex((candidate) => candidate.tool === effectiveTool);
                  const next =
                    jump === "first"
                      ? reachable[0]!
                      : jump === "last"
                        ? reachable.at(-1)!
                        : reachable[
                            (((here + step) % reachable.length) + reachable.length) %
                              reachable.length
                          ]!;
                  store.getState().setTool(next.tool);
                  // The roving tabindex moves with the selection, so the newly
                  // active button is the one that can hold focus.
                  const buttons = event.currentTarget.parentElement?.querySelectorAll("button");
                  buttons?.[stepTools.findIndex((c) => c.tool === next.tool)]?.focus();
                }}
              >
                <ToolGlyph tool={each.tool} />
                {each.name}
              </button>
            ))}
          </div>
          {/* What this job is, and the way to change its form (UX-Gutachten 2,
              B-6): in the tool row, beside the tools that work on it — they
              stood in a head of their own over the strip. */}
          <div className={styles.jobFacts}>
            {/* What this job is, in the figures a person checks first. The
                type is the Vorgang's own and changes here (spec 2026-10-23 R-7)
                — on a Vorgang whose facade may still change; elsewhere, and on
                the register demo's board, it is the reading it was. A Vorgang
                that states no type at all (data from before §15.14) is asked
                below, where the question blocks the tools. */}
            <dl className={styles.facts}>
              <div className={styles.fact}>
                <dt className={`label ${styles.fieldLabel}`}>
                  {typeChoosable ? (
                    <label htmlFor="job-grating-type">
                      {/* Read as „Rosttyp des Vorgangs“: a picked grating's panel
                          has its own „Rosttyp“, and the two must not sound alike. */}
                      Rosttyp <span className="visually-hidden">des Vorgangs</span>
                    </label>
                  ) : (
                    "Rosttyp"
                  )}
                </dt>
                <dd className={styles.factValue}>
                  {typeChoosable ? (
                    <JobGratingType
                      handle={typeHandleRef}
                      id="job-grating-type"
                      caseId={caseId}
                      type={statedType}
                      arriving={typeArriving}
                      specs={specs}
                      approved={releaseStands}
                      approval={approval}
                      laid={laidGratings}
                      settle={settleWrites}
                      onWriting={typeWriting}
                      onChanged={typeChanged}
                      onConflict={typeConflict}
                    />
                  ) : jobGratingType ? (
                    <>
                      {gratingTypeLabel(jobGratingType)}
                      {jobSpec ? (
                        <span className={`num ${styles.factFigure}`}> {formatMm(jobSpec.widthMm)}</span>
                      ) : null}
                    </>
                  ) : (
                    "offen"
                  )}
                </dd>
              </div>
              <div className={styles.fact}>
                <dt className={`label ${styles.fieldLabel}`}>Wände</dt>
                <dd className={`num ${styles.factValue}`}>{wallCount}</dd>
              </div>
            </dl>
            {/* Beside the walls it changes (spec 2026-10-15 §3) — only on a
                Vorgang, and only while its facade may still change. */}
            {caseId !== null ? <ChangeFormButton caseId={caseId} /> : null}
          </div>
          <div className={styles.actions}>
            {refreshButton}
            {/* The tooltip names what the press takes back („Rückgängig:
                Gehrung an Ecke Wand 3 zu Wand 4 gesetzt") and the keys that
                do the same; the button keeps its one trained word. A
                laying's toast offers the same walk back (`takeBack`). */}
            <Tooltip
              text={takeBackLabel !== null && canTakeBack ? `Rückgängig: ${takeBackLabel}` : "Rückgängig"}
              keys={undoKeys().undo}
            >
              <Button ref={undoButtonRef} variant="quiet" onClick={takeBack} disabled={!canTakeBack}>
                Rückgängig
              </Button>
            </Tooltip>
            <Tooltip
              text={putBackLabel !== null && canPutBack ? `Wiederholen: ${putBackLabel}` : "Wiederholen"}
              keys={undoKeys().redo}
            >
              <Button ref={redoButtonRef} variant="quiet" onClick={putBack} disabled={!canPutBack}>
                Wiederholen
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* What the tool does, and why nothing moves (read-only) or that the
            plan is stale: a line of its own over the board at the text's size
            (fix round 1 of Task 15, I-1). Laid over the board's corner it hid
            a drawn measure — the geometry is this product's error detector —
            and shrank these sentences to 12 px. Compact: 4 px above and
            below. */}
        <p className={styles.status} data-role="tool-status">
          <span className={styles.statusText}>
            {gesturesHeld ? WRITE_WAIT : readOnly ? readOnlyStatus : activeTool.status}
            {planStale
              ? ' Ein Objekt passt nicht zur Belegung — „Planung aktualisieren" teilt die Wände neu auf.'
              : ""}
          </span>
          {effectiveTool === "select" ? null : (
            <span className={styles.statusKey}>
              <kbd className={styles.kbd}>Esc</kbd> beendet das Werkzeug
            </span>
          )}
          {hover ? (
            <span className={styles.statusRefusal}>
              {hover.reason === "wall_without_measure"
                ? `Wand ${hover.wallIndex + 1} trägt kein Maß.`
                : `Wand ${hover.wallIndex + 1} — ${formatMm(
                    Math.max(
                      0,
                      ...freeStretches(outline, placements, hover.wallIndex).map(
                        (stretch) => stretch.lengthMm,
                      ),
                      0,
                    ),
                  )} frei. Das kleinste Element misst ${formatMm(jobSpec?.minLengthMm ?? 400)}.`}
            </span>
          ) : null}
        </p>

        {/* Every message about a write or a plan, right above the board
            (screen check 15.10.2026, V-3): at the foot of the page a 409
            „Stand veraltet" stood 1.000 px below the field it was about, and
            a person typing there saw only their figure vanish. Inside the
            work area, so the maximized board carries them too — behind the
            overlay they would be said to nobody. Focusable for the one case
            a reload takes the focus away with the panel it closes. */}
        <div ref={messagesRef} tabIndex={-1} className={styles.messages}>
          {failure ? (
            <StateMessage tone="conflict" title="Der Plan wurde nicht gerechnet.">
              {failure}
            </StateMessage>
          ) : null}
          {fillFailure ? (
            <StateMessage tone="conflict" title="Die Wand wurde nicht belegt.">
              {fillFailure} Die Zeichnung bleibt, wie sie ist.
            </StateMessage>
          ) : null}
          {insertFailure ? (
            <StateMessage tone="conflict" title="Objekt nicht eingesetzt.">
              {insertFailure}
            </StateMessage>
          ) : null}
          {/* Said out loud, and in a sentence. The endpoint refuses two objects
              that overlap on one wall; the screen used to let the gesture through
              without a word, so a person had two sills inside one another and no
              way to tell that anything had gone wrong (walk finding 2). */}
          {standingRefusal?.reason === "fase_wall" ? (
            // The slant takes grating and nothing else (design 2026-10-18
            // §6.3) — the server would refuse it, the board says so first.
            <StateMessage tone="conflict" title={`Wand ${standingRefusal.wallIndex + 1} ist eine Fase.`}>
              {FASE_NO_OBJECTS}
            </StateMessage>
          ) : standingRefusal ? (
            <StateMessage tone="conflict" title="Zwei Objekte an derselben Stelle.">
              Auf Wand {standingRefusal.wallIndex + 1} steht dort schon ein Objekt. Zwei Objekte
              dürfen sich nicht überlappen — setz es daneben, oder ändere das
              vorhandene.
            </StateMessage>
          ) : null}
          {/* A chamfer the form can no longer cut (Task 4 ruling 1, I-1): the
              board stands still on the replay, and the one way on is the
              form. The server's own sentence, and the button beside it. */}
          {notDrawable !== null ? (
            <StateMessage
              tone="conflict"
              title="Die Fase lässt sich nicht einzeichnen."
              live={false}
              action={caseId !== null ? <ChangeFormButton caseId={caseId} /> : undefined}
            >
              {notDrawable.message}
            </StateMessage>
          ) : null}
          {/* The 409 (design §9/§10): somebody else's gesture landed first. Said
              out loud and reloaded, never merged and never silently overwritten
              — `persist` has already taken the server's own number as the new
              expectation by the time this shows. */}
          {staleMessage ? (
            <StateMessage tone="conflict" title="Stand veraltet." live={false}>
              {staleMessage.text}
              {staleMessage.lost !== null ? (
                <>
                  {" "}
                  <LostMeasureSentence lost={staleMessage.lost} />
                </>
              ) : null}
            </StateMessage>
          ) : null}
          {/* A change of type refused under the board (409): its select is
              gone with the head's reading, so the sentence stands here. */}
          {typeRefusal ? <StateMessage tone="conflict" title={typeRefusal} live={false} /> : null}
          {/* The server's own verdict (S-8): dropped and reloaded already, so the
              transport branch's promise of a retry below would be false here —
              a 409 on a sent Vorgang, for one, is never sent again. */}
          {refusedMessage ? (
            <StateMessage tone="conflict" title="Nicht gespeichert.">
              {refusedMessage} Die Geste wurde verworfen, der Vorgang neu geladen.
            </StateMessage>
          ) : null}
          {lostGesture ? (
            <StateMessage tone="conflict" title={LOST_GESTURE} live={false} />
          ) : null}
          {persistFailure ? (
            <StateMessage tone="conflict" title="Nicht gespeichert.">
              {persistFailure} Die Zeichnung bleibt, wie sie ist — der nächste Versuch schreibt den
              Stand nach.
            </StateMessage>
          ) : null}
          {/* Not a failure to retry, and deliberately worded so nobody waits for
              one: the wall carries no Kennung in the stored Umriss, so there is
              nothing for the Geste to be keyed by — now or later. Posting it under
              a made-up key would be accepted and would then break every further
              Aufruf des Vorgangs (fix round 1, item 5). */}
          {unwritable ? (
            <StateMessage tone="conflict" title="Geste nicht in den Vorgang geschrieben.">
              {unwritable} Bitte den Umriss erneut übernehmen — danach trägt jede Wand wieder eine
              Kennung.
            </StateMessage>
          ) : null}
        </div>

        {/* `data-step` lets the stylesheet dim the bracket marks before the
            brackets are the step: they are the server's answer to a laid
            grating and stay visible, but they are not what a person is
            doing on „Objekte" or „Roste" (design 2026-09-22 §2). */}
        <div className={styles.workspace} data-step={effectiveStep}>
          <PlanningBoard
            outline={outline}
            placements={placements}
            features={features}
            specs={specs}
            jobGratingType={jobGratingType}
            newLengthMm={newLengthMm}
            minGapMm={minGapMm}
            tool={effectiveTool}
            featureTemplate={{
              featureType,
              covered: featureCovered,
              widthOffsetMm: featureWidthOffsetMm,
              splitsRun: featureSplitsRun,
              label: featureLabel.trim() === "" ? null : featureLabel,
            }}
            selected={selected}
            selectedWall={selectedWall}
            selectedFeature={selectedFeature}
            issueIds={issueIds}
            consoles={consoles}
            corners={corners}
            treatableWalls={treatableWalls}
            cuts={cuts}
            // Read-only (V-1): every gesture the board hands up goes nowhere —
            // picking, hovering and the view stay. The menus offer gestures,
            // so they do not open.
            onToggleCorner={readOnly ? ignoreGesture : toggleCorner}
            onCornerMenu={
              readOnly
                ? ignoreGesture
                : (request) => {
                    setInsertFailure(null);
                    setCornerMenu(request);
                  }
            }
            onConsoleMenu={readOnly ? undefined : (request) => setConsoleMenu(request)}
            // The board hands over a finished placement, label and all. The
            // first version numbered it a second time here and overwrote what
            // came in — two implementations of one rule, and the losing one
            // was the one that knew „only grating is numbered". An
            // interruption then arrived carrying `W1-01`, so the element count
            // skipped a number that had never been used on an element (found
            // in review, 16.08.2026).
            onPlace={
              readOnly
                ? ignoreGesture
                : (placement) => {
                    announce("Element gesetzt");
                    commit([...placements, placement]);
                  }
            }
            onMove={readOnly ? ignoreGesture : (id, offsetMm, live) => change(id, { offsetMm }, live)}
            onDelete={readOnly ? ignoreGesture : remove}
            onSelect={(id) => store.getState().select(id)}
            onSelectWall={(index) => store.getState().selectWall(index)}
            onClearPick={clearPick}
            onHoverWall={(index, reason) =>
              setHover(index !== null && reason ? { wallIndex: index, reason } : null)
            }
            setFeature={readOnly ? ignoreGesture : setFeature}
            onSelectFeature={(id) => store.getState().selectFeature(id)}
            onDeleteFeature={readOnly ? ignoreGesture : removeFeature}
            onFeatureRefused={(refusal) =>
              setFeatureRefusal(refusal && { ...refusal, step: history.length })
            }
            cutouts={cutouts}
            selectedCutout={selectedCutout}
            onSelectCutout={(id) => store.getState().selectCutout(id)}
            onPlaceCutout={readOnly ? ignoreGesture : placeCutout}
            onMoveCutout={readOnly ? ignoreGesture : moveCutout}
            onDeleteCutout={readOnly ? ignoreGesture : deleteCutout}
            view={view}
            onView={(next) => store.getState().setView(next)}
            maximized={maximized}
            onMaximize={toggleMaximized}
            maximizeRef={maximizeRef}
          />
          <div className={styles.side} data-role="side-panel">
            {/* The customer's drawing beside the plan (Task 18): at the top of
                this column, so the board keeps its width and the shape is
                compared at a glance (E-13); large on a press. Outside the
                fieldset — looking is never held. */}
            {caseId !== null && caseDocuments.length > 0 ? (
              <SketchPane caseId={caseId} documents={caseDocuments} />
            ) : null}
            {/* One switch for every control the panels hold (V-1): a disabled
                fieldset reaches each button, field and select inside without
                a prop per panel, and the figures stay there to read. The one
                exception is „Maß“ on a wall while a chamfer cannot be drawn
                (final review I-1): the wall's panel then holds everything
                below the field itself (`lockedBelowMeasure`). */}
            <fieldset className={styles.panel} disabled={readOnly && !measureOnly}>
              {picked ? (
                <PlacementPanel
                  placement={picked}
                  specs={specs}
                  jobGratingType={jobGratingType}
                  lengthText={lengthText?.id === picked.id ? lengthText.text : null}
                  cut={cuts.get(picked.id)}
                  onLengthText={(text) => setLengthText({ id: picked.id, text })}
                  onLengthDone={() => setLengthText(null)}
                  onChange={(patch, live) => change(picked.id, patch, live)}
                  onSplit={(firstMm) => splitAt(picked.id, firstMm)}
                  onRemove={() => remove(picked.id)}
                />
              ) : pickedCutout ? (
                <CutoutPanel
                  // A fresh panel per hole: the mid-edit state of one hole's
                  // field must not fold the next hole's keystrokes into its step.
                  key={pickedCutout.id}
                  cutout={pickedCutout}
                  features={features}
                  placements={placements}
                  wallLengthMm={outline ? wallLengthMm(outline, pickedCutout.wallIndex) : null}
                  answer={pickedCutoutAnswer}
                  surcharge={cutoutSurcharges[pickedCutout.id] ?? null}
                  clearanceMm={cutoutClearanceMm}
                  onPipe={(pipe, mm, live) => setPipe(pipe, mm, live)}
                  onChange={(patch, live) => changeCutout(pickedCutout.id, patch, live)}
                  onSurcharge={(typed, displaced) => {
                    announce("Aufschlag geändert");
                    store.getState().setCutoutSurcharge(pickedCutout.id, typed, displaced);
                  }}
                  onRemove={() => deleteCutout(pickedCutout.id)}
                />
              ) : pickedFeature ? (
                <FeaturePanel
                  feature={pickedFeature}
                  typeWidthMm={jobSpec?.widthMm ?? null}
                  withCutout={cutouts.some((each) => each.featureId === pickedFeature.id)}
                  holes={cutouts.filter((each) => each.featureId === pickedFeature.id)}
                  clearanceMm={cutoutClearanceMm}
                  fresh={freshPipe === pickedFeature.id}
                  onPipe={(mm, live) => setPipe(pickedFeature, mm, live)}
                  onKeepPipe={keepFreshPipe}
                  onChange={(patch, live) => changeFeature(pickedFeature.id, patch, live)}
                  onSettle={(offsetMm, lengthMm, live) =>
                    settleFeatureFields(pickedFeature, offsetMm, lengthMm, live)
                  }
                  onWithCutout={(on) => linkCutout(pickedFeature, on)}
                  onRemove={() => removeFeature(pickedFeature.id)}
                />
              ) : effectiveTool === "feature" ? (
                /* The tool's own little form — what a new object is built
                   with, before anything is drawn. Not the placement panel:
                   nothing exists yet to select (design, Task 12,
                   30.08.2026). In the side column since 22.09.2026, where a
                   person's eye already is: the board on the left, what is
                   about to be put on it on the right. */
                <div className={styles.toolForm} data-role="feature-tool-panel">
                  <p className={`label ${styles.panelHead}`}>Nächstes Objekt</p>
                  <FeatureFields
                    idPrefix="feature"
                    featureType={featureType}
                    covered={featureCovered}
                    widthOffsetText={featureWidthOffsetText}
                    splitsRun={featureSplitsRun}
                    withCutout={featureWithCutout}
                    label={featureLabel}
                    onType={chooseFeatureType}
                    onCovered={chooseFeatureCovered}
                    onWidthOffsetText={typeFeatureWidthOffset}
                    onWidthOffsetBlur={finishFeatureWidthOffset}
                    onSplitsRun={setFeatureSplitsRun}
                    onWithCutout={setFeatureWithCutout}
                    onLabel={setFeatureLabel}
                    widthRefusal={featureWidthRefusal}
                  />
                  <span className={styles.hint}>Dann auf die Wand klicken, wo es steht.</span>
                </div>
              ) : selectedWall !== null ? (
                <WallPanel
                  outline={outline}
                  placements={placements}
                  wallIndex={selectedWall}
                  busy={filling === selectedWall}
                  corner={cornerFacts.get(selectedWall) ?? null}
                  miter={corners.find((each) => each.wallIndex === selectedWall) ?? null}
                  onCornerChange={(patch) => changeCorner(selectedWall, patch)}
                  onCover={() => void cover(selectedWall)}
                  measure={measureField}
                  lockedBelowMeasure={measureOnly}
                  layingWords={layingWords}
                  split={
                    effectiveStep === "grating"
                      ? {
                          wall:
                            splitVariants.find((each) => each.wallIndex === selectedWall) ?? null,
                          rules: splitRules,
                          priority: splitPriority,
                          onPriority: chooseSplitPriority,
                          onRelay: () => void cover(selectedWall, true),
                        }
                      : null
                  }
                />
              ) : (
                idlePanel
              )}
            </fieldset>

            {effectiveStep === "objects" && features.length > 0 ? (
              <div className={styles.listBlock}>
                <p className={`label ${styles.listHead}`}>
                  Objekte <span className="num">{features.length}</span>
                </p>
                <FeatureList
                  features={features}
                  wallCount={wallCount}
                  selected={selectedFeature}
                  onSelect={(id) => store.getState().selectFeature(id)}
                />
              </div>
            ) : null}
            {placements.length > 0 ? (
              <div className={styles.listBlock}>
                <p className={`label ${styles.listHead}`}>
                  Elemente <span className="num">{placements.length}</span>
                </p>
                <ElementList
                  placements={placements}
                  elements={elements}
                  wallCount={wallCount}
                  selected={selected}
                  cuts={cuts}
                  onSelect={(id) => store.getState().select(id)}
                />
              </div>
            ) : null}
          </div>
        </div>

        {implausibles.length > 0 ||
        notices.length > 0 ||
        skippedSaid.length > 0 ||
        wallsWithoutMeasure.length > 0 ||
        standingRelaid !== null ? (
          <div className={styles.notices} role="region" aria-label="Zur Kenntnis">
            <p className={`label ${styles.noticesHead}`}>
              Zur Kenntnis (
              {implausibles.length +
                notices.length +
                (skippedSaid.length > 0 ? 1 : 0) +
                (wallsWithoutMeasure.length > 0 ? 1 : 0) +
                (standingRelaid ? 1 : 0)}
              )
            </p>
            <ul className={styles.findingList}>
              {/* First: the one thing here that waits for a person's decision
                  — and holds the release until it is made (E-64). */}
              {implausibles.map((finding) => (
                <ImplausibleMeasure
                  key={finding.message}
                  finding={finding}
                  wall={disputedWallOf(finding, outline)}
                  target={implausibleTarget}
                  onType={typeImplausible}
                />
              ))}
              {standingRelaid ? (
                <li className={styles.finding}>
                  {/* One sentence for one element, one for several — the same
                      care `skippedWalls` takes one row down, and it had been
                      taken there and not here: „1 Elemente ersetzt … stellt
                      sie wieder her" (round 2 of the walk, finding 3). */}
                  <span className={styles.findingText}>{relaidText(standingRelaid)}</span>
                </li>
              ) : null}
              {wallsWithoutMeasure.length > 0 ? (
                <li className={styles.finding} data-role="unmeasured-walls">
                  {/* What it does and the two ways out (UX-Gutachten Nr. 6) —
                      and the first such wall one press away, its „Maß“ field
                      the first of the two. */}
                  <span className={styles.findingText}>
                    {unmeasuredWallsSentence(wallsWithoutMeasure)}
                  </span>
                  <Button
                    variant="quiet"
                    onClick={() => {
                      // Into the wall's „Maß“ — the first way out — once its
                      // panel is there, not left on this button (M-8); at
                      // once where that wall is the one picked already.
                      const target = wallsWithoutMeasure[0]!;
                      if (store.getState().selectedWall === target) {
                        focusMeasureField();
                        return;
                      }
                      focusMeasureWallRef.current = target;
                      store.getState().selectWall(target);
                    }}
                  >
                    Wand {wallsWithoutMeasure[0]! + 1} zeigen
                  </Button>
                </li>
              ) : null}
              {skippedSaid.length > 0 ? (
                <li className={styles.finding}>
                  {/* One sentence for one wall, one for several — a list of
                      wall numbers is a thing a person reads out loud. */}
                  <span className={styles.findingText}>{skippedWallsSentence(skippedSaid)}</span>
                </li>
              ) : null}
              {notices.map((finding, index) => (
                <li key={index} className={styles.finding}>
                  <span className={styles.findingText}>{finding.message}</span>
                  {finding.placementIds?.[0] ? (
                    <Button
                      variant="quiet"
                      onClick={() => store.getState().select(finding.placementIds![0]!)}
                    >
                      zeigen
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {violations.length > 0 ? (
          <div className={styles.findings}>
            <p className={`label ${styles.findingsHead}`}>Passt nicht ({violations.length})</p>
            <ul className={styles.findingList}>
              {violations.map((finding, index) => (
                <li key={index} className={styles.finding}>
                  <span className={styles.findingText}>{finding.message}</span>
                  {finding.rule === SPLIT_RULE && finding.wallIndex !== undefined ? (
                    <Button
                      variant="quiet"
                      disabled={readOnly}
                      onClick={() => void splitAgain(finding.wallIndex!)}
                    >
                      So aufteilen
                    </Button>
                  ) : finding.rule === BLANK_RULE && finding.placementIds?.[0] ? (
                    /* The offered way out E-47 deliberately did not build,
                       added on the feedback it named as the thing that would
                       correct it (Entwurf 2026-08-29 §13). Worded as what it
                       does — it divides the *blank*, which is the figure the
                       finding is about, and not the wall. */
                    <Button
                      variant="quiet"
                      disabled={readOnly}
                      onClick={() => void splitOverlongBlank(finding.placementIds![0]!)}
                    >
                      Rohling aufteilen
                    </Button>
                  ) : finding.placementIds?.[0] ? (
                    <Button
                      variant="quiet"
                      onClick={() => store.getState().select(finding.placementIds![0]!)}
                    >
                      zeigen
                    </Button>
                  ) : null}
                  {/* The rule as well as the wall. `check_placements` emits
                      about ten further rules that carry a `wall_index`, so a
                      gate on the wall alone hung this answer under an
                      unrelated finding — and under each of them, where one
                      wall carried several (re-review, 30.08.2026). It belongs
                      to the button, and the button belongs to this rule. */}
                  {finding.rule === SPLIT_RULE &&
                  finding.wallIndex !== undefined &&
                  standingNothingLaid?.wallIndex === finding.wallIndex ? (
                    <span className={styles.findingNote}>
                      Auf Wand {finding.wallIndex + 1} ließ sich nichts anlegen — die freie
                      Strecke ist kürzer als das kleinste Element. Was auf eine solche
                      Reststrecke gehört, ist im Regelwerk noch nicht festgelegt; die Frage
                      liegt bei Chiara. Bis dahin: Nachbarelemente ändern oder das Objekt
                      davor verschieben.
                    </span>
                  ) : null}
                  {/* Why „Rohling aufteilen" divided nothing — under the
                      finding it was pressed on, and under no other. Gated on
                      the element as well as the rule, the way the note above
                      is gated on the wall as well as the rule: one wall can
                      carry several overlong blanks. */}
                  {finding.rule === BLANK_RULE &&
                  standingBlankRefusal !== null &&
                  finding.placementIds?.[0] === standingBlankRefusal.placementId ? (
                    <span className={styles.findingNote}>
                      {standingBlankRefusal.message}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      {/* The bought parts, on the step they belong to. Empty and not absent
          where nothing was laid: an empty table with a heading under it is
          worse than no panel at all. */}
      {effectiveStep === "consoles" && result && result.consoleLines && result.consoleLines.length > 0 ? (
        <ConsoleLinesPanel
          lines={result.consoleLines}
          // Whole-branch review, 05.09.2026 (Important 6): with no catalogue
          // configured at all, `_console_catalogue` returns none and every
          // bracket on every plan reads "kein Konsolenartikel im Katalog" —
          // one row per width, naming a per-width gap that is not what is
          // actually wrong. `consoleCatalogue` already carries active
          // articles only, so an empty list here means exactly that.
          catalogueConfigured={(result.consoleCatalogue?.length ?? 0) > 0}
        />
      ) : null}

      <div className={styles.footer}>
        {/* On a Vorgang the way out is the calculation (design 2026-09-13 §9,
            D-22): it opens the prices for this Stand and starts nothing by
            itself. The register demo's stateless board has no Stand on the
            server to price, so it keeps the check as its primary way out. */}
        {caseId !== null ? (
          // Primary once there is something to price. Before the first
          // grating the way forward is on the board, and a black button
          // that leads away from it would say otherwise. Red is one per
          // screen (design 2026-10-24 §8.2): while the plan waits for
          // „Neu belegen" or the Vorgang for „Wiederherstellen", those are
          // red and this one is ink (`footerWay`).
          <Button
            variant={footerWay}
            onClick={() => navigate(`/vorgang/${caseId}/kalkulation`)}
          >
            Weiter zur Kalkulation
          </Button>
        ) : (
          // The check is the register demo's own stateless `/schema`. From a
          // Vorgang it opened example figures under „Gespeichert ist nichts“
          // (Welle 0, S1, 18.10.2026) — the calculation above is the way on.
          <Button variant={footerWay} onClick={() => navigate("/schema")}>
            Weiter zur Prüfung
          </Button>
        )}
        <Button onClick={downloadQuantities} disabled={placements.length === 0}>
          Mengenliste als CSV
        </Button>
        {/* The stored Stand as documents (design 2026-10-12) — only on a
            Vorgang: the register demo's board has nothing stored to print. */}
        {caseId !== null ? <DocumentsButton caseId={caseId} versionNo={versionNo ?? 0} /> : null}
        <p className={styles.footerNote}>
          {caseId !== null
            ? "Weiter zur Kalkulation bewertet diese Stückliste mit den Preislisten — die erste Kalkulation startest du dort. Offene Regelverstöße gehen mit."
            : "Weiter zur Prüfung rechnet Konsolen und Mengenliste. Offene Regelverstöße gehen mit und stehen dort ganz oben."}
        </p>
      </div>

      {cornerMenu ? (
        <CornerMenu
          // Remounts on every genuinely new click — this is how the „Objekt
          // einsetzen" submenu forgets it was open on a wall three metres
          // away, without an effect calling setState to reset it on the way
          // in (react-hooks/set-state-in-effect).
          key={`${cornerMenu.objectWallIndex}-${cornerMenu.offsetMm}-${cornerMenu.x}-${cornerMenu.y}`}
          request={cornerMenu}
          faseCorner={isFaseCorner(outline, cornerMenu.wallIndex)}
          faseWall={isFase(outline.segments[cornerMenu.objectWallIndex])}
          placement={placements.find((each) => each.id === cornerMenu.placementId) ?? null}
          fact={cornerFacts.get(cornerMenu.wallIndex) ?? null}
          mark={corners.find((each) => each.wallIndex === cornerMenu.wallIndex) ?? null}
          widthMm={(() => {
            const element = placements.find((each) => each.id === cornerMenu.placementId);
            return element ? effectiveWidthMm(element, specs, jobGratingType) : null;
          })()}
          maxLengthMm={jobSpec?.maxLengthMm ?? 1500}
          onFill={(filledWallIndex) => {
            fillCorner(cornerMenu.wallIndex, filledWallIndex);
            closeCornerMenu();
          }}
          onSetMitre={() => {
            setMitre(cornerMenu.wallIndex);
            closeCornerMenu();
          }}
          onReset={() => {
            clearCorner(cornerMenu.wallIndex);
            closeCornerMenu();
          }}
          onZoomToPlacement={() => {
            if (cornerMenu.placementId !== null) {
              zoomToPlacement(cornerMenu.placementId, cornerMenu.boardWidth, cornerMenu.boardHeight);
            }
            closeCornerMenu();
          }}
          onRemovePlacement={() => {
            if (cornerMenu.placementId !== null) remove(cornerMenu.placementId);
            closeCornerMenu();
          }}
          onSplitWall={() => {
            // „Neu aufteilen" and not „belegen": the entry says what it
            // does, and on a covered wall — the normal case — topping up the
            // free stretches is nothing at all (walk finding 5).
            void cover(cornerMenu.objectWallIndex, true);
            closeCornerMenu();
          }}
          onInsertFeature={(type) => insertFeature(cornerMenu, type)}
          onInsertCutout={(shape) => insertCutout(cornerMenu, shape)}
          feature={features.find((each) => each.id === cornerMenu.featureId) ?? null}
          onSwitchHinge={(door) => {
            changeFeature(door.id, { hinge: hingeOf(door) === "left" ? "right" : "left" });
            closeCornerMenu();
          }}
          onRemoveCutout={
            cornerMenu.cutoutId !== null
              ? () => {
                  if (cornerMenu.cutoutId !== null) deleteCutout(cornerMenu.cutoutId);
                  closeCornerMenu();
                }
              : null
          }
          onClose={closeCornerMenu}
        />
      ) : null}

      {releaseQuestion !== null ? (
        <ConfirmReleaseLoss
          by={approval?.by ?? null}
          at={approval?.at ?? null}
          subtitle={releaseQuestion.subtitle}
          onCancel={keepRelease}
          onConfirm={spendRelease}
          onClosed={afterReleaseQuestion}
        />
      ) : null}

      {consoleMenu ? (
        <ConsoleMenu
          // Remounts on every genuinely new click, the same reason
          // `CornerMenu` does above.
          key={`${consoleMenu.index}-${consoleMenu.x}-${consoleMenu.y}`}
          request={consoleMenu}
          mark={consoleMenu.key ? (consoleMarksByKey.get(consoleMenu.key) ?? null) : null}
          options={consoleArticleOptions(result?.consoleCatalogue ?? [])}
          onChoose={(articleNo) => {
            if (consoleMenu.key) {
              announce("Konsole von Hand gesetzt");
              store.getState().setConsoleOverride(consoleMenu.key, articleNo);
            }
            closeConsoleMenu();
          }}
          onRestore={() => {
            if (consoleMenu.key) {
              announce("Konsolenvorschlag wiederhergestellt");
              store.getState().setConsoleOverride(consoleMenu.key, null);
            }
            closeConsoleMenu();
          }}
          onClose={closeConsoleMenu}
        />
      ) : null}
    </div>
  );
}

/**
 * What a stale Stand cost a measure gesture (V-3) — the wall and the figure,
 * the figure in the mono face like every measure (CI §4), and what to do: the
 * reload closed the field the figure was typed into.
 */
function LostMeasureSentence({ lost }: { lost: LostMeasure }) {
  return lost.mm !== null ? (
    <>
      Nicht gespeichert: {lost.wallName} auf <span className="num">{formatMm(lost.mm)}</span> — prüf die
      Wand im neuen Stand und setz das Maß noch einmal.
    </>
  ) : (
    <>Nicht gespeichert: Maß an {lost.wallName} entfernen — prüf die Wand im neuen Stand.</>
  );
}

/**
 * „Wand 1 neu aufgeteilt: 5 Elemente ersetzt …" — and the singular of it.
 *
 * Written out rather than assembled from a plural flag, because German does
 * not decline in one place: the count, the noun, the „davon"-clause and the
 * pronoun at the end all move together, and a sentence stitched from four
 * conditionals is unreadable in the source and wrong in the output.
 */
function relaidText(relaid: {
  wallIndex: number | null;
  count: number;
  handTyped: number;
}): string {
  const one = relaid.count === 1;
  const where =
    relaid.wallIndex === null ? "Neu aufgeteilt" : `Wand ${relaid.wallIndex + 1} neu aufgeteilt`;
  const what = one ? "1 Element ersetzt" : `${relaid.count} Elemente ersetzt`;
  const typed =
    relaid.handTyped === 0
      ? ""
      : relaid.handTyped === 1
        ? ", davon eines mit einem von Hand getippten Maß"
        : `, davon ${relaid.handTyped} mit von Hand getippten Maßen`;
  const back = one ? "Rückgängig stellt es wieder her." : "Rückgängig stellt sie wieder her.";
  return `${where}: ${what}${typed}. ${back}`;
}


/**
 * The bought-parts half of the parts list: brackets, one row per article.
 *
 * `PartsList.tsx`'s own header comment explains why gratings carry no price
 * on screen — no price list exists for them, and an invented figure in front
 * of the management is out of the question (spec §1). **Brackets are the
 * exception since 05.09.2026**: they are bought parts with a maintained sales
 * price in JTL, and that figure is the quotation price with no margin
 * calculation applied. Two price rules stand on one screen and must not read
 * as the same one — hence the panel's own title and its place beside, not
 * inside, the section that still has none.
 *
 * Grouped by article already on the backend (`pricing/consoles.py:console_lines`)
 * — this renders one row per entry and never explodes `count` back into rows
 * of one. Prices arrive as decimal strings and are parsed only for display
 * (`formatMoney`); nothing here adds or re-totals them — the sums are the
 * backend's own.
 */
function ConsoleLinesPanel({
  lines,
  /**
   * Whether the server holds any active catalogue article at all —
   * `(result.consoleCatalogue ?? []).length > 0`. `false` means
   * `CONSOLE_CATALOGUE_CSV` is unset, empty, or has nothing active
   * (`_console_catalogue`'s own docstring: unconfigured is not malformed,
   * and the ordinary state before ``WP-E1-1``).
   *
   * Read once here rather than per row: with no catalogue configured, every
   * unpriced row's own reason is technically true and systemically
   * misleading — twenty rows each naming a different width's gap read as
   * twenty separate problems, when there is exactly one (whole-branch
   * review, 05.09.2026, Important 6).
   */
  catalogueConfigured,
}: {
  lines: DemoConsoleLine[];
  catalogueConfigured: boolean;
}) {
  return (
    <Panel title="Winkelkonsolen" hint="Gekaufte Teile, gruppiert nach Artikel.">
      {!catalogueConfigured ? (
        <p className={styles.consoleNote}>
          Kein Konsolenkatalog hinterlegt — deshalb steht neben keiner Konsole ein Artikel.
        </p>
      ) : null}
      <div className={styles.consoleTableWrap}>
        <table className={styles.consoleTable}>
          <caption className="visually-hidden">
            Winkelkonsolen der Planung, gruppiert nach Artikel
          </caption>
          <thead>
            <tr>
              <th scope="col" className="label">
                Art.-Nr.
              </th>
              <th scope="col" className="label">
                Bezeichnung
              </th>
              <th scope="col" className="label">
                Menge
              </th>
              <th scope="col" className="label">
                Einzelpreis
              </th>
              <th scope="col" className="label">
                Summe
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              // A row with no article stays visible in the quantity while
              // disappearing from the sum — it keeps its count and loses its
              // price, it does not invent either (`ConsoleLineOut`).
              const priced = line.articleNo !== null;
              return (
                <tr key={line.articleNo ?? `offen-${index}`}>
                  <th scope="row" className="num">
                    {line.articleNo ?? "—"}
                  </th>
                  <td>
                    {line.label}
                    {line.overridden > 0 ? (
                      <span className={styles.consoleOverridden}>
                        {line.overridden} von Hand
                      </span>
                    ) : null}
                    {/* A note beside a price is not an alternative to it — both
                        show. Only the unpriced row (below) puts the note where
                        the price would have been. */}
                    {priced && line.note ? (
                      <span className={styles.consoleNote}>{line.note}</span>
                    ) : null}
                  </td>
                  <td className="num">{line.count}</td>
                  {priced ? (
                    <>
                      <td className={`num ${styles.consoleMoney}`}>
                        {formatMoney(line.unitPriceNet)}
                      </td>
                      <td className={`num ${styles.consoleMoney}`}>
                        {formatMoney(line.totalNet)}
                      </td>
                    </>
                  ) : (
                    <td className={styles.consoleReason} colSpan={2}>
                      {/* The panel-level note above already says the one
                          thing that is actually true — no catalogue is
                          configured — so a row's own per-width reason would
                          only repeat a symptom as if it were twenty separate
                          causes. */}
                      {catalogueConfigured ? line.note : "—"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/**
 * Every element, grouped by wall.
 *
 * Not decoration: on a 24-metre facade the elements are twenty to fifty pixels
 * wide and past six per wall the board prints one figure for the whole wall
 * (§5.5.1). A covering is checked by reading a list top to bottom, not by
 * looking at a picture.
 */
/**
 * What one grating row says: the wall stretch, and the blank where a mitre
 * has made the two differ.
 *
 * Only where they differ. „1.000 mm · Rohling 1.000 mm" on every row of every
 * ordinary job is a column of noise that trains people to stop reading it.
 */
/**
 * What the field under the blank says: what it would be, or what it displaced.
 *
 * Never overwriting, always standing beside — the rule `displaced_reading_mm`
 * keeps one level up (E-46). Where a typed blank was stamped against a figure
 * the shape has since moved away from, both are named: a production length
 * that no longer matches what it was typed against is not something to
 * discover in the workshop.
 */
function blankHint(placement: Placement, cut: ElementCut): string {
  // `== null` throughout: a draft stored before these two fields existed
  // carries neither, and `undefined` there means what `null` means.
  if (placement.blankLengthMm == null) {
    if (placement.lengthMm === cut.blankMm) {
      return "in Millimetern — leer heißt: die Wandstrecke";
    }
    // Which of the two grew it. A filling is cut straight at both ends and
    // grows all the same, so the cut words are what tell them apart — the
    // same test `check_miters` makes on the other side.
    const from =
      cut.start === "gerade" && cut.end === "gerade" ? "der Auffüllung" : "der Gehrung";
    return `in Millimetern — leer heißt: ${formatMm(cut.blankMm)} aus ${from}`;
  }
  const snapshot = placement.blankSnapshotMm;
  if (snapshot != null && snapshot !== cut.blankMm) {
    return `von Hand · abgeleitet war ${formatMm(snapshot)} beim Tippen, jetzt ${formatMm(cut.blankMm)}`;
  }
  return `von Hand · abgeleitet wären ${formatMm(cut.blankMm)}`;
}

/**
 * The key that does the same on a focused element, beside a menu item's
 * words (Welle 1, idea 16) — from the registry the „?" overview lists.
 * Hidden from screen readers: the item keeps its own name, and the overview
 * says the key in words.
 */
function MenuKey({ id }: { id: ShortcutId }) {
  return (
    <span className={styles.cornerMenuKey} aria-hidden="true">
      {firstChordCaps(id, isMac()).map((cap) => (
        <Kbd key={cap}>{cap}</Kbd>
      ))}
    </span>
  );
}

/**
 * A menu opened on the board stays inside the window (UX-Gutachten
 * 23.10.2026, Nr. 8): below and to the right of the pointer while it fits
 * there, upwards where the window has no room below, pulled in from an edge
 * otherwise — `placeFloating`, the rule every floating layer of the app
 * keeps. A list taller than the window scrolls in itself (`.cornerMenu`'s
 * `max-block-size`), so its last entry is reachable with the mouse too; the
 * page under an open menu does not scroll.
 *
 * Measured before the first paint, and again when what the menu holds
 * changes (`contentKey` — „Objekt einsetzen“ opening its seven types).
 */
function useKeptInView(
  ref: RefObject<HTMLElement | null>,
  x: number,
  y: number,
  contentKey: unknown = null,
): void {
  useLayoutEffect(() => {
    const layer = ref.current;
    if (layer === null) return;
    const placed = placeFloating(
      { x, y },
      { width: layer.offsetWidth, height: layer.offsetHeight },
      {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight,
      },
      { side: "below", align: "start" },
    );
    layer.style.left = `${placed.left}px`;
    layer.style.top = `${placed.top}px`;
  }, [ref, x, y, contentKey]);
}

/**
 * What can be done with one wall — the right-click menu.
 *
 * The only context menu in this application until task 11 added a second,
 * `ConsoleMenu`, at a bracket mark — this one started life about one
 * corner only (27.08.2026, E-47). Task 14 widened it to the whole wall: a
 * second menu confined to the bare wall line would be unreachable the moment
 * the wall is fully covered, which is the normal case, so instead there is
 * one menu, opened from wherever the click landed — its band, an
 * interruption, or the bare grip — with its content depending on where that
 * was. Opened by the `contextmenu` event rather than by a mouse button, so
 * **Shift+F10** on the focused wall opens it too (`wallKeyDown` answers the
 * key directly, since jsdom does not turn it into a `contextmenu` event the
 * way a real browser does) — the keyboard route costs nothing and is the
 * difference between „mouse only" and „mouse first".
 *
 * Two things are always offered — inserting an object, and re-splitting the
 * wall — because they apply to the wall wherever the click landed on it. The
 * corner treatments (Ecke auffüllen · Gehrung · stumpf) apply to a corner,
 * so they show only where `request.nearCorner` says the click was actually
 * about one, and then only once the corner also has a grating band to reason
 * from (`placement`, guarded below) — the fill amount and the mitre both
 * grow *that* element.
 *
 * The length rule for a filling is checked *before* the gesture, not
 * reported after it: a filling that would push the element past the
 * maximum is offered greyed out with the arithmetic spelled out, and the
 * way forward — splitting the wall afresh — is the same „Wand neu
 * aufteilen" entry that is on the menu regardless (formerly a second, only
 * conditionally visible copy of the same button; task 14 folded the two).
 */
function CornerMenu({
  request,
  faseCorner,
  faseWall,
  placement,
  fact,
  mark,
  widthMm,
  maxLengthMm,
  onFill,
  onSetMitre,
  onReset,
  onZoomToPlacement,
  onRemovePlacement,
  onSplitWall,
  onInsertFeature,
  onInsertCutout,
  onRemoveCutout,
  feature,
  onSwitchHinge,
  onClose,
}: {
  request: CornerMenuRequest;
  /** The corner the click is about is one of a chamfer's (design 2026-10-18):
   * only butted, so neither a mitre nor a filling is offered — and why not
   * is said. */
  faseCorner: boolean;
  /** The wall under the click is a chamfer's slant: it takes grating, but no
   * object and no hole. */
  faseWall: boolean;
  placement: Placement | null;
  fact: CornerFact | null;
  /** What the corner carries now, or `null` where it is butted. */
  mark: CornerMark | null;
  widthMm: number | null;
  maxLengthMm: number;
  onFill: (filledWallIndex: number) => void;
  onSetMitre: () => void;
  onReset: () => void;
  /** „Diesen Rost groß" — only ever called where `request.placementId` is
   * not `null` (task 8, design 2026-09-10); the button below does not render
   * otherwise. */
  onZoomToPlacement: () => void;
  /** „Element entfernen" — rendered under the same condition. */
  onRemovePlacement: () => void;
  onSplitWall: () => void;
  onInsertFeature: (type: FeatureType) => void;
  /** A free hole at the click, square or round (design §8). */
  onInsertCutout: (shape: CutoutShape) => void;
  /** „Ausschnitt entfernen" — only where the click landed on a hole
   * (`request.cutoutId`); the entry does not render otherwise. */
  onRemoveCutout: (() => void) | null;
  /** The facade object the click landed on (`request.featureId`), or `null`. */
  feature: FacadeFeature | null;
  /** „Anschlag wechseln" — offered only where `feature` is a door. */
  onSwitchHinge: (door: FacadeFeature) => void;
  onClose: () => void;
}) {
  const [insertOpen, setInsertOpen] = useState(false);
  const door = feature && SWING_TYPES.has(feature.featureType) ? feature : null;
  const containerRef = useRef<HTMLDivElement>(null);
  useKeptInView(containerRef, request.x, request.y, insertOpen);

  // Move focus *into* the menu the moment it opens (review round 1,
  // important 3) — Shift+F10 and a right-click both land the pointer or the
  // keyboard focus somewhere outside it (the wall grip, or nowhere at all),
  // and a `role="menu"` nobody can Tab or Arrow into is not reachable from
  // the keyboard, whatever its markup says. Runs once per mount, and the
  // menu remounts on every genuinely new click (see the `key` at the call
  // site), so this never has to re-fire mid-menu.
  useEffect(() => {
    containerRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, []);

  useEffect(() => {
    function away(event: Event): void {
      if (!(event.target as HTMLElement | null)?.closest?.("[data-role='corner-menu']")) {
        onClose();
      }
    }
    function key(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  /**
   * Roving focus among the menuitems — Arrow keys move within the menu the
   * same way they already do in the tool radiogroup (`TOOLS.map` above),
   * Home/End jump to either end. Read fresh off the DOM on every keypress
   * rather than kept in state: which buttons exist changes as „Objekt
   * einsetzen" opens and closes its seven types, and a stale index would
   * either skip the newly shown ones or land on one that just disappeared.
   */
  function menuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    const jump = event.key === "Home" ? "first" : event.key === "End" ? "last" : null;
    if (step === 0 && jump === null) return;
    event.preventDefault();
    const items = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );
    if (items.length === 0) return;
    const here = items.indexOf(document.activeElement as HTMLElement);
    const next =
      jump === "first"
        ? items[0]!
        : jump === "last"
          ? items.at(-1)!
          : items[(((here === -1 ? 0 : here) + step) % items.length + items.length) % items.length]!;
    next.focus();
  }

  // The grating that would grow at this corner, if any — narrowed once so
  // the rest of the component never has to repeat the three-part check that
  // decides it. Only meaningful near a corner: an interruption or a bare
  // stretch offers the same two general entries as anywhere else on the
  // wall, and nothing about a fill or a mitre.
  const gratingAtCorner =
    request.nearCorner && placement && placement.kind === "grating" ? placement : null;

  // A corner no run crosses has nothing to fill: the square is not between
  // two gratings, it is simply open.
  const crossed = fact !== null;
  // **Only an outer corner grows anything** (E-47). There the square gapes
  // open and the covering grating reaches into it; at a re-entrant corner the
  // same square is under both bands already, so covering it takes nothing on
  // — the *other* grating gives it up instead, and this element measures
  // exactly what it measured before. The menu said „+288 mm" and locked
  // itself against 1.500 mm at inner corners too, against a figure that must
  // never arise there (walk of 30.08.2026, finding 1).
  //
  // Asked as „is it convex", not „is it not re-entrant": `shape` is also
  // `null` where the walk does not turn at all, and `_cut_run` grows nothing
  // there either — the negative form put that corner in the growing branch
  // (found in review, 30.08.2026).
  const grows = fact?.shape === "aussen";
  const grown =
    !grows || widthMm === null || !gratingAtCorner
      ? null
      : gratingAtCorner.lengthMm + widthMm;
  const overlong = grown !== null && grown > maxLengthMm;

  // What the corner carries now decides what is worth offering: filling it
  // again from the grating that already fills it would do nothing, a mitre
  // it already has needs removing rather than setting again, and a reset on
  // a corner with nothing on it would say there is something.
  const filled = mark?.treatment === "fuellung";
  const filledByThis =
    filled && gratingAtCorner !== null && mark?.filledWallIndex === gratingAtCorner.wallIndex;
  const mitred = mark?.treatment === "gehrung";
  const state = filled ? "aufgefüllt" : mark ? "auf Gehrung" : "";

  return (
    <div
      ref={containerRef}
      data-role="corner-menu"
      className={styles.cornerMenu}
      role="menu"
      aria-label={`Wand ${request.objectWallIndex + 1}`}
      onKeyDown={menuKeyDown}
    >
      <p className={`label ${styles.cornerMenuHead}`}>
        Wand {request.objectWallIndex + 1}
        {placement?.label ? ` · ${placement.label}` : ""}
        {door
          ? ` · ${door.label ?? FEATURE_DEFAULTS[door.featureType].name}, Anschlag ${
              HINGE_WORDS[hingeOf(door)]
            }`
          : ""}
        {/* `cornerInReach`, not `nearCorner`: the entries below reach along a
            whole band on purpose, but the heading is a statement of fact, and
            naming a corner eight metres from the click is simply untrue
            (round 2 of the walk, finding 5). */}
        {request.cornerInReach ? ` · Ecke nach Wand ${request.wallIndex + 1}` : ""}
        {state ? ` · ${state}` : ""}
      </p>

      {/* Navigation before editing, and first for it: a bare stretch of wall
          has no element to zoom to, so this only ever appears where
          `placement` does too (task 8, design 2026-09-10). */}
      {request.placementId !== null ? (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.cornerMenuItem}
          onClick={onZoomToPlacement}
        >
          Diesen Rost groß
        </button>
      ) : null}
      {/* The way to take one element off, where the pointer already is
          (Denis, 22.09.2026: „individuelle Gitterroste löschen"). The same
          gesture the panel's „Element entfernen" and the Delete key make —
          one more door to it, not a second implementation. */}
      {request.placementId !== null ? (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.cornerMenuItem}
          onClick={onRemovePlacement}
        >
          Element entfernen
          <MenuKey id="remove" />
        </button>
      ) : null}

      {/* The door's own entry, first where the click landed on a door —
          the object under the pointer is what the person is thinking about
          (Denis, 24.09.2026). The heading above says which side it is now. */}
      {door ? (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.cornerMenuItem}
          onClick={() => onSwitchHinge(door)}
        >
          Anschlag wechseln
        </button>
      ) : null}
      {faseWall ? (
        // Said in place of the two entries it removes — an entry that is
        // simply missing reads as an oversight (design 2026-10-18 §6.5).
        <p className={styles.cornerMenuNote} data-role="fase-wall-note">
          {FASE_NO_OBJECTS}
        </p>
      ) : (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.cornerMenuItem}
          aria-haspopup="true"
          aria-expanded={insertOpen}
          onClick={() => setInsertOpen((open) => !open)}
        >
          Objekt einsetzen
        </button>
      )}
      {insertOpen && !faseWall ? (
        <div className={styles.cornerMenuSubmenu}>
          {FEATURE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.cornerMenuItem}
              onClick={() => onInsertFeature(type)}
            >
              {FEATURE_DEFAULTS[type].name}
            </button>
          ))}
        </div>
      ) : null}
      {/* The three meanings of this menu — Blick („Diesen Rost groß"),
          Einsetzen (Objekt, Ausschnitt eckig/rund, entfernen), Ecke — stand
          in this order; Justin's grouping (§15.6) is open, and until it comes
          this is the order that moves no existing entry (ruling 14). */}
      {faseWall ? null : (
        <>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={styles.cornerMenuItem}
            onClick={() => onInsertCutout("eckig")}
          >
            Ausschnitt eckig einsetzen
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={styles.cornerMenuItem}
            onClick={() => onInsertCutout("rund")}
          >
            Ausschnitt rund einsetzen
          </button>
        </>
      )}
      {onRemoveCutout ? (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className={styles.cornerMenuItem}
          onClick={onRemoveCutout}
        >
          Ausschnitt entfernen
          <MenuKey id="remove" />
        </button>
      ) : null}
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        className={styles.cornerMenuItem}
        onClick={onSplitWall}
      >
        Wand neu aufteilen
      </button>

      {request.nearCorner && faseCorner ? (
        // A chamfer's corner is only ever butted (design 2026-10-18, call 7):
        // nothing to set or fill there, and the reason in place of the
        // entries.
        <p className={styles.cornerMenuNote} data-role="fase-corner-note">
          {FASE_BUTT_ONLY}
        </p>
      ) : request.nearCorner ? (
        <>
          {/* A mitre is a mark on the corner itself, not on a specific pair
              of gratings — the same standing the corner already has via the
              standalone handle (`treatableWalls`), which this entry mirrors
              rather than gates behind a run the answer happens to have
              confirmed crosses here. Setting one on a corner nothing meets
              has no effect; it is not a state anything downstream refuses. */}
          {mitred ? (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.cornerMenuItem}
              onClick={onReset}
            >
              Gehrung entfernen
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={styles.cornerMenuItem}
              onClick={onSetMitre}
            >
              Gehrung setzen
            </button>
          )}
          {!crossed ? (
            // Scoped to auffüllen only — a mitre is offered right above this
            // regardless of a confirmed crossing (Gehrung setzen/entfernen),
            // and the note used to read as if it excused both (review round
            // 1, task 14, minor 7).
            <p className={styles.cornerMenuNote}>
              An dieser Ecke trifft kein zweiter Rost — hier lässt sich nichts auffüllen.
            </p>
          ) : gratingAtCorner ? (
            <>
              {filledByThis ? null : (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={styles.cornerMenuItem}
                  disabled={overlong}
                  onClick={() => onFill(gratingAtCorner.wallIndex)}
                >
                  Ecke auffüllen{grows && widthMm !== null ? ` (+${formatMm(widthMm)})` : ""}
                </button>
              )}
              {filled ? (
                <button
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className={styles.cornerMenuItem}
                  onClick={onReset}
                >
                  Auffüllung zurücksetzen
                </button>
              ) : null}
              {overlong ? (
                <p className={styles.cornerMenuNote}>
                  {formatMm(gratingAtCorner.lengthMm)} + {formatMm(widthMm ?? 0)} ={" "}
                  {formatMm(grown ?? 0)} — über der Maximallänge {formatMm(maxLengthMm)}. „Wand
                  neu aufteilen" oben teilt die Wand stattdessen neu auf.
                </p>
              ) : filledByThis ? (
                <p className={styles.cornerMenuNote}>
                  Dieser Rost deckt die Ecke. Zurücksetzen macht den Stoß wieder stumpf.
                </p>
              ) : (
                <p className={styles.cornerMenuNote}>
                  {filled
                    ? "Der andere Rost deckt die Ecke. Auffüllen übernimmt sie hierher."
                    : grows
                      ? "Statt einer Gehrung: dieser Rost läuft durch, der andere stößt an."
                      : fact?.shape === "innen"
                        ? // An Innenecke: the square is under both bands
                          // already. Which grating *keeps* it is the whole
                          // question, and no blank grows. „seine Rostbreite"
                          // and not a figure — it is the other element's
                          // width, and the menu is holding this one's.
                          "Innenecke: dieser Rost behält die Ecke, der andere gibt sie ab und wird um seine Rostbreite kürzer."
                        : // No turn, no corner square — and nothing for a
                          // filling to hand to anybody.
                          "Hier laufen zwei Wände in einer Linie — es gibt kein Eckquadrat, das ein Rost übernehmen könnte."}
                </p>
              )}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Every entry of the bracket chooser the keys walk, in document order — the articles and the way back. */
const CONSOLE_MENU_ITEMS = '[role="menuitemradio"]:not(:disabled), [role="menuitem"]:not(:disabled)';

/**
 * What one bracket should build — the right-click menu at a console mark
 * (task 11), built after `CornerMenu`'s own pattern rather than a second one:
 * opened by `contextmenu`/Shift+F10, closed by Escape or a press elsewhere,
 * roving Arrow/Home/End among its items, focus moved in on mount.
 *
 * The proposal heads the list, marked as one; the rest of the catalogue
 * follows; „Vorschlag wiederherstellen" always closes it, whether or not this
 * bracket carries an override to clear — pressing it on one that carries none
 * is simply a no-op, not a state to guard against.
 *
 * What does not carry this bracket's width (`mark.fittingArticleNos`, the
 * server's answer — spec 2026-10-23, Task 9) stands behind a rule, after
 * everything that does, each saying so in words („trägt 288 mm nicht") on
 * the screen and in its accessible name, never by colour alone (CI §6). It
 * stays selectable — the board takes a person's choice (A-014) — but the
 * calculation does not let it through: `console_width_mismatch` is a blocker
 * since `dc19aa13` (Task 13a), and the release waits until a fitting article
 * or the proposal stands again. The arrow keys walk the
 * items in DOM order, so they follow this order without being told.
 *
 * The articles are a choice of one (UX-Gutachten 23.10.2026, Nr. 8): each
 * a `menuitemradio`, the one the bracket carries now (`mark.articleNo` —
 * set by hand, or the proposal where nobody did) checked, and marked with a
 * tick in a column of its own — a shape, not a colour (CI §7.4). „Vorschlag
 * wiederherstellen“ is no article and stays a `menuitem`.
 */
function ConsoleMenu({
  request,
  mark,
  options,
  onChoose,
  onRestore,
  onClose,
}: {
  request: ConsoleMenuRequest;
  /** The bracket's own answer, or `null` where it carries none — an answer
   * that predates this field, or a mark the backend has not addressed. The
   * menu still opens; it simply has no proposal to head itself with. */
  mark: DemoConsoleMark | null;
  options: ConsoleArticleOption[];
  onChoose: (articleNo: string) => void;
  onRestore: () => void;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useKeptInView(containerRef, request.x, request.y);

  useEffect(() => {
    containerRef.current?.querySelector<HTMLElement>(CONSOLE_MENU_ITEMS)?.focus();
  }, []);

  useEffect(() => {
    function away(event: Event): void {
      if (!(event.target as HTMLElement | null)?.closest?.("[data-role='console-menu']")) {
        onClose();
      }
    }
    function key(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  function menuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    const jump = event.key === "Home" ? "first" : event.key === "End" ? "last" : null;
    if (step === 0 && jump === null) return;
    event.preventDefault();
    const items = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(CONSOLE_MENU_ITEMS) ?? [],
    );
    if (items.length === 0) return;
    const here = items.indexOf(document.activeElement as HTMLElement);
    const next =
      jump === "first"
        ? items[0]!
        : jump === "last"
          ? items.at(-1)!
          : items[(((here === -1 ? 0 : here) + step) % items.length + items.length) % items.length]!;
    next.focus();
  }

  const proposedNo = mark?.proposedArticleNo ?? null;
  /** What the bracket carries now — the one checked. */
  const currentNo = mark?.articleNo ?? null;
  // The server's own answer to „what carries this width" — never `fits`
  // rebuilt here. `null` where the answer predates the field: then every
  // article counts as carrying it, which is the list as it always was.
  const fitting = mark?.fittingArticleNos === undefined ? null : new Set(mark.fittingArticleNos);
  const carries = (option: ConsoleArticleOption) => fitting === null || fitting.has(option.articleNo);
  const proposalFirst = (group: ConsoleArticleOption[]) => [
    ...group.filter((option) => option.articleNo === proposedNo),
    ...group.filter((option) => option.articleNo !== proposedNo),
  ];
  const carrying = proposalFirst(options.filter(carries));
  const notCarrying = proposalFirst(options.filter((option) => !carries(option)));
  const misfitNote = mark ? `trägt ${formatMm(mark.widthMm)} nicht` : null;

  function item(option: ConsoleArticleOption, misfit: boolean) {
    const checked = option.articleNo === currentNo;
    return (
      <button
        key={option.articleNo}
        type="button"
        role="menuitemradio"
        aria-checked={checked}
        tabIndex={-1}
        className={`${styles.cornerMenuItem} ${styles.cornerMenuChoice}`}
        onClick={() => onChoose(option.articleNo)}
      >
        <span className={styles.cornerMenuTick} aria-hidden="true">
          {checked ? <CheckIcon size={12} /> : null}
        </span>
        <span>
          {option.label}
          {option.articleNo === proposedNo ? " (Vorschlag)" : ""}
          {misfit && misfitNote ? (
            <>
              {/* The comma only for the accessible name; the eye has the line break. */}
              <span className="visually-hidden">, </span>
              <span className={styles.cornerMenuItemNote}>{misfitNote}</span>
            </>
          ) : null}
        </span>
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      data-role="console-menu"
      className={styles.cornerMenu}
      role="menu"
      aria-label={mark ? `Winkelkonsole, ${formatMm(mark.widthMm)}` : "Winkelkonsole"}
      onKeyDown={menuKeyDown}
    >
      <p className={`label ${styles.cornerMenuHead}`}>
        Winkelkonsole{mark ? ` · ${formatMm(mark.widthMm)}` : ""}
      </p>
      {carrying.map((option) => item(option, false))}
      {carrying.length > 0 && notCarrying.length > 0 ? (
        <div role="separator" className={styles.cornerMenuRule} />
      ) : null}
      {notCarrying.map((option) => item(option, true))}
      {options.length === 0 ? (
        <p className={styles.cornerMenuNote}>
          Diese Zeichnung nennt keinen Artikel — die Mengenliste unten sagt, was in Frage kommt.
        </p>
      ) : null}
      <button
        type="button"
        role="menuitem"
        tabIndex={-1}
        // In the articles' name column, its tick column empty for good: it is
        // no article, and the names above line up with it.
        className={`${styles.cornerMenuItem} ${styles.cornerMenuChoice}`}
        onClick={onRestore}
      >
        <span className={styles.cornerMenuTick} aria-hidden="true" />
        <span>Vorschlag wiederherstellen</span>
      </button>
    </div>
  );
}

/**
 * What an object *is* — the three effect fields, the type and the label.
 *
 * **One form, two places.** The tool holds it before anything is drawn (there
 * is no object yet to select) and the side panel holds it afterwards, for the
 * object somebody picked. A second copy of these five controls would be a
 * second place for „a width offset only means anything on a covered object"
 * to be stated, and the two would drift — which is the whole argument
 * `setFeature` makes one level down about being one op.
 *
 * `idPrefix` keeps the two instances' `htmlFor` apart. They cannot be on
 * screen at once — picking up a tool clears the selection and selecting an
 * object needs „Auswählen" — but two labels pointing at one id is a defect
 * whether or not anybody has managed to see it.
 */
function FeatureFields({
  idPrefix,
  featureType,
  covered,
  widthOffsetText,
  splitsRun,
  withCutout,
  label,
  onType,
  onCovered,
  onWidthOffsetText,
  onWidthOffsetBlur,
  onSplitsRun,
  onWithCutout,
  onLabel,
  widthRefusal,
}: {
  idPrefix: string;
  featureType: FeatureType;
  covered: boolean;
  /** What the field shows, kept apart from the committed number: „-" is not
   * a number yet, and a field bound straight to the committed value would
   * erase the sign the moment somebody typed it. */
  widthOffsetText: string;
  splitsRun: boolean;
  /**
   * Whether the object brings a linked hole (D-1). On the tool form this is
   * the wish for one, read when the object is set; on the object panel it
   * is a fact — „a linked hole exists" — and the tick creates or removes it.
   */
  withCutout: boolean;
  label: string;
  onType: (type: FeatureType) => void;
  onCovered: (covered: boolean) => void;
  onWidthOffsetText: (raw: string) => void;
  onWidthOffsetBlur: () => void;
  onSplitsRun: (splitsRun: boolean) => void;
  onWithCutout: (withCutout: boolean) => void;
  onLabel: (label: string) => void;
  /** Why the typed offset was refused, or `null`. Refused at the field and
   * not behind the first server call (walk finding 7). */
  widthRefusal: string | null;
}) {
  return (
    <>
      <label className={`label ${styles.fieldLabel}`} htmlFor={`${idPrefix}-type`}>
        Art
      </label>
      <Select
        id={`${idPrefix}-type`}
        className={styles.input}
        value={featureType}
        onChange={(event) => onType(event.target.value as FeatureType)}
      >
        {FEATURE_TYPES.map((type) => (
          <option key={type} value={type}>
            {FEATURE_DEFAULTS[type].name}
          </option>
        ))}
      </Select>

      <label className={styles.checkboxField}>
        <input
          type="checkbox"
          checked={covered}
          onChange={(event) => onCovered(event.target.checked)}
        />
        Rost liegt darauf
      </label>

      {covered ? (
        <>
          <label className={`label ${styles.fieldLabel}`} htmlFor={`${idPrefix}-width-offset`}>
            Breite ändert sich um … mm
          </label>
          <input
            id={`${idPrefix}-width-offset`}
            className={`${styles.input} ${styles.inputNum} ${
              widthRefusal ? styles.inputBad : ""
            }`}
            type="number"
            inputMode="numeric"
            aria-invalid={widthRefusal !== null}
            value={widthOffsetText}
            onChange={(event) => onWidthOffsetText(event.target.value)}
            onBlur={onWidthOffsetBlur}
          />
          <span className={`${styles.hint} ${widthRefusal ? styles.hintBad : ""}`}>
            {widthRefusal ??
              "Negativ: der Rost weicht zurück. Positiv: er reicht darüber hinaus."}
          </span>

          {/* The third effect (design §14): the band runs through and is
              drilled. Only where grating lies — without steel there is no
              hole, and the document refuses to carry one. Since 12.09.2026
              (D-1) the hole is a `Cutout` of its own; this is the wish for
              one, and the hole comes prefilled at pipe width plus clearance
              (D-9, D-10). */}
          <label className={styles.checkboxField}>
            <input
              type="checkbox"
              checked={withCutout}
              onChange={(event) => onWithCutout(event.target.checked)}
            />
            mit Ausschnitt
          </label>
        </>
      ) : (
        <label className={styles.checkboxField}>
          <input
            type="checkbox"
            checked={splitsRun}
            onChange={(event) => onSplitsRun(event.target.checked)}
          />
          Trennt den Lauf
        </label>
      )}

      <label className={`label ${styles.fieldLabel}`} htmlFor={`${idPrefix}-label`}>
        Beschriftung
      </label>
      <input
        id={`${idPrefix}-label`}
        className={styles.input}
        type="text"
        value={label}
        placeholder={FEATURE_DEFAULTS[featureType].name}
        onChange={(event) => onLabel(event.target.value)}
      />
    </>
  );
}

/**
 * The object somebody picked, and what can be done to it (Entwurf §6
 * „Ändern").
 *
 * The same five controls the tool carries, plus the two figures a gesture
 * would otherwise be the only way to state: where it begins and how long it
 * is. Both are typed rather than only dragged, because a person who knows
 * „die Haustür ist 1.010 breit" should not have to hit it with a mouse — and
 * because a keyboard has no drag.
 */
function FeaturePanel({
  feature,
  typeWidthMm,
  withCutout,
  holes,
  clearanceMm,
  fresh,
  onPipe,
  onKeepPipe,
  onChange,
  onSettle,
  onWithCutout,
  onRemove,
}: {
  feature: FacadeFeature;
  /** The job's own grating width — what the offset is taken off. `null` where
   * no type is chosen, and then there is nothing to subtract from. */
  typeWidthMm: number | null;
  /** Whether a linked hole exists for this object — on one that stands, the
   * tick is a fact about the draft, not a wish (D-1). */
  withCutout: boolean;
  /** The holes linked to this object — the sum line of a downpipe reads the first. */
  holes: readonly Cutout[];
  /** The case's Montagespiel, added to a pipe for its hole (D-10). */
  clearanceMm: number;
  /** Just set: the diameter choice takes the focus, Enter keeps it (A5). */
  fresh: boolean;
  onPipe: (pipeMm: number, live: boolean) => void;
  onKeepPipe: () => void;
  onChange: (patch: Partial<FacadeFeature>, live?: boolean) => void;
  /** Where the object should come to rest — the screen puts it through the
   * same `settleFeature` every gesture goes through, and words the refusal. */
  onSettle: (offsetMm: number, lengthMm: number, live: boolean) => void;
  /** Create the linked hole, or take every linked hole off — the screen's
   * `linkCutout`. */
  onWithCutout: (on: boolean) => void;
  onRemove: () => void;
}) {
  const [widthOffsetText, setWidthOffsetText] = useState(String(feature.widthOffsetMm));
  const [widthRefusal, setWidthRefusal] = useState<string | null>(null);
  /**
   * Which field is mid-edit, so „1200" is one step on the undo stack and not
   * four.
   *
   * The **first** keystroke of an edit opens the step; every one after it
   * overwrites it. Marking the first one live too would overwrite the state
   * *before* the edit, and Rückgängig would then undo the object instead of
   * the typing — the same reasoning `lengthText` carries for a placement.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const typing = (field: string) => {
    const already = editing === field;
    if (!already) setEditing(field);
    return already;
  };
  const done = () => setEditing(null);
  // The field belongs to the object that is open. Picking a second object
  // while the first one's text sat in state showed the first one's number
  // over the second one's values.
  const [openId, setOpenId] = useState(feature.id);
  if (openId !== feature.id) {
    setOpenId(feature.id);
    setWidthOffsetText(String(feature.widthOffsetMm));
    setWidthRefusal(null);
    setEditing(null);
  }

  function typeWidthOffset(raw: string): void {
    setWidthOffsetText(raw);
    if (raw.trim() === "" || raw.trim() === "-") {
      setWidthRefusal(null);
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next)) return;
    const refusal = widthOffsetRefusal(typeWidthMm, next);
    setWidthRefusal(refusal);
    if (refusal === null) onChange({ widthOffsetMm: next }, typing("width"));
  }

  return (
    <>
      <p className={`label ${styles.panelHead}`}>
        <span className="num">{feature.id}</span> FASSADENOBJEKT · Wand{" "}
        {feature.wallIndex + 1}
      </p>

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="fp-offset">
          Anfang
        </label>
        <input
          id="fp-offset"
          className={`${styles.input} ${styles.inputNum}`}
          type="number"
          inputMode="numeric"
          value={String(feature.offsetMm)}
          onChange={(event) => {
            const raw = event.target.value.trim();
            if (!/^\d+$/.test(raw)) return;
            onSettle(Number(raw), feature.lengthMm, typing("offset"));
          }}
          onBlur={done}
          onKeyDown={(event) => {
            // Enter must close the typed step exactly like blur does — one
            // commit, not a step left open until something else ends it
            // (I-1). `blur()` also fires the native blur below, which calls
            // `done()` a second time; that is a no-op, `editing` is already
            // `null`.
            if (event.key === "Enter") {
              done();
              event.currentTarget.blur();
            }
          }}
        />
        <span className={styles.hint}>mm vom Wandanfang</span>
      </div>

      {feature.featureType === "fallrohr" ? (
        // A downpipe's length on the wall *is* its diameter: the choice of
        // the common three replaces the bare length field (A5).
        <PipeFields
          pipeMm={feature.lengthMm}
          hole={holes[0] ?? null}
          clearanceMm={clearanceMm}
          fresh={fresh}
          onPipe={onPipe}
          onKeep={onKeepPipe}
        />
      ) : (
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="fp-length">
            Länge
          </label>
          <input
            id="fp-length"
            className={`${styles.input} ${styles.inputNum}`}
            type="number"
            inputMode="numeric"
            value={String(feature.lengthMm)}
            onChange={(event) => {
              const raw = event.target.value.trim();
              if (!/^\d+$/.test(raw) || Number(raw) < 1) return;
              onSettle(feature.offsetMm, Number(raw), typing("length"));
            }}
            onBlur={done}
            onKeyDown={(event) => {
              // Same contract as „Anfang" above: Enter closes the step (I-1).
              if (event.key === "Enter") {
                done();
                event.currentTarget.blur();
              }
            }}
          />
          <span className={styles.hint}>mm auf der Wand</span>
        </div>
      )}

      <div className={styles.field}>
        <FeatureFields
          idPrefix="fp"
          featureType={feature.featureType}
          covered={feature.covered}
          widthOffsetText={widthOffsetText}
          splitsRun={feature.splitsRun}
          withCutout={withCutout}
          label={feature.label ?? ""}
          onType={(type) => {
            // Only the three effect fields are re-prefilled, never `name` or
            // `defaultLengthMm` — they are not fields of a `FacadeFeature`,
            // and the extent this object was drawn to is the person's, not
            // the type's. Nor the hole: on an object that stands, a type
            // change neither makes nor takes one — that is its own gesture.
            const defaults = FEATURE_DEFAULTS[type];
            setWidthOffsetText(String(defaults.widthOffsetMm));
            setWidthRefusal(null);
            onChange({
              featureType: type,
              covered: defaults.covered,
              widthOffsetMm: defaults.widthOffsetMm,
              splitsRun: defaults.splitsRun,
            });
          }}
          onCovered={(covered) => {
            // The same rule the tool's own form keeps, on an object that
            // already stands: without grating there is no width to change,
            // and the document refuses to carry one (`document.py`). The old
            // figure does not come back on re-ticking — see
            // `chooseFeatureCovered` for why not.
            if (!covered) {
              setWidthOffsetText("0");
              setWidthRefusal(null);
            }
            // The linked hole goes with the grating too — „ohne Blech kein
            // Loch" — and `setFeature` takes it out of the draft in the same
            // step, as the server does to the document.
            onChange(covered ? { covered } : { covered, widthOffsetMm: 0 });
          }}
          onWidthOffsetText={typeWidthOffset}
          onWidthOffsetBlur={() => {
            setWidthOffsetText(String(feature.widthOffsetMm));
            setWidthRefusal(null);
            done();
          }}
          onSplitsRun={(splitsRun) => onChange({ splitsRun })}
          onWithCutout={onWithCutout}
          onLabel={(label) => onChange({ label: label === "" ? null : label }, typing("label"))}
          widthRefusal={widthRefusal}
        />
      </div>

      {SWING_TYPES.has(feature.featureType) ? (
        <HingeChoice value={hingeOf(feature)} onPick={(hinge) => onChange({ hinge })} />
      ) : null}

      <Button onClick={onRemove}>Objekt entfernen</Button>
    </>
  );
}

/**
 * Which side a door hangs on, as two pressed-or-not buttons (24.09.2026).
 *
 * Two buttons and not a checkbox: „links" and „rechts" are two answers of
 * equal standing, and a tick box would make one of them the absence of the
 * other. `aria-pressed` carries the state, the ink segment shows it — and
 * the swing on the board shows it a third time, where it matters. The hint
 * names the viewpoint once, because „left" is only a fact from somewhere.
 */
function HingeChoice({
  value,
  onPick,
}: {
  value: DoorHinge;
  onPick: (hinge: DoorHinge) => void;
}) {
  return (
    <div className={styles.field}>
      <span className={`label ${styles.fieldLabel}`} id="fp-hinge">
        Anschlag
      </span>
      <div className={styles.hingeGroup} role="group" aria-labelledby="fp-hinge">
        {(["left", "right"] as const).map((side) => (
          <button
            key={side}
            type="button"
            className={styles.hingeSegment}
            aria-pressed={value === side}
            onClick={() => onPick(side)}
          >
            {HINGE_WORDS[side]}
          </button>
        ))}
      </div>
      <span className={styles.hint}>von innen gesehen, zur Öffnungsseite (DIN 107)</span>
    </div>
  );
}

/**
 * The diameter of a downpipe, picked or typed, and what it makes of the hole
 * (A5, 23.09.2026) — on the pipe's own panel and on its linked hole's.
 *
 * 100 · 120 · 150 are **pipe** diameters; the hole is the pipe plus the
 * case's Montagespiel, and the sum line says so in figures, so nobody has to
 * wonder whether „150" was the pipe or the hole. „Eigenes Maß" opens a field
 * for any other pipe and puts the cursor in it.
 */
function PipeFields({
  pipeMm,
  hole,
  clearanceMm,
  fresh = false,
  onPipe,
  onKeep,
}: {
  pipeMm: number;
  /** The linked hole, or `null` where the pipe asks for none. */
  hole: Cutout | null;
  clearanceMm: number;
  fresh?: boolean;
  onPipe: (pipeMm: number, live: boolean) => void;
  onKeep?: () => void;
}) {
  const [customOpen, setCustomOpen] = useState(false);
  /** What is typed in the own-measure field while it is being typed — a
   * figure under one grid step is not a pipe yet and goes nowhere. */
  const [draft, setDraft] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);
  const [focusField, setFocusField] = useState(0);
  useEffect(() => {
    if (focusField > 0) fieldRef.current?.focus();
  }, [focusField]);

  const custom = customOpen || !(PIPE_PRESETS_MM as readonly number[]).includes(pipeMm);
  const extent = hole ? cutoutExtentMm(hole) : null;

  return (
    <div className={styles.field}>
      <span className={`label ${styles.fieldLabel}`} aria-hidden="true">
        Rohr
      </span>
      <PresetChoice
        label="Rohr"
        presets={PIPE_PRESETS_MM}
        value={pipeMm}
        customActive={custom}
        autoFocus={fresh}
        onPick={(mm) => {
          setCustomOpen(false);
          setDraft(null);
          onPipe(mm, false);
        }}
        onCustom={() => {
          setCustomOpen(true);
          setFocusField((count) => count + 1);
        }}
        onConfirm={onKeep}
      />
      {custom ? (
        <>
          <label className={`label ${styles.fieldLabel}`} htmlFor="pipe-custom">
            Rohr in mm
          </label>
          <input
            ref={fieldRef}
            id="pipe-custom"
            className={`${styles.input} ${styles.inputNum}`}
            type="number"
            inputMode="numeric"
            min={GRID_MM}
            value={draft ?? String(pipeMm)}
            onChange={(event) => {
              const raw = event.target.value.trim();
              setDraft(raw);
              if (!/^\d+$/.test(raw) || Number(raw) < GRID_MM) return;
              onPipe(Number(raw), editing);
              setEditing(true);
            }}
            onBlur={() => {
              setDraft(null);
              setEditing(false);
            }}
          />
        </>
      ) : null}
      {fresh ? (
        <span className={styles.hint}>Enter übernimmt · 1, 2, 3 wählen</span>
      ) : null}
      {hole && extent ? (
        <p className={`num ${styles.pipeSum}`}>
          {/* No-break spaces inside the result, so a narrow column breaks
              the line before „Ausschnitt"'s figure, never inside it. */}
          {hole.shape === "rund"
            ? `Rohr ${pipeMm} + ${clearanceMm} Spiel = Ausschnitt Ø\u00a0${extent.alongMm}\u00a0mm`
            : `Rohr ${pipeMm} + ${clearanceMm} Spiel = Ausschnitt ${extent.alongMm}\u00a0×\u00a0${extent.acrossMm}\u00a0mm`}
        </p>
      ) : (
        <span className={styles.hint}>ohne Ausschnitt — „mit Ausschnitt“ setzt Rohr + Spiel</span>
      )}
    </div>
  );
}

/**
 * A labelled `<input type="number">` with an optional hint under it — the
 * one shape the cutout panel and the head block's clearance field share.
 *
 * Only a whole non-negative number commits, and one under `min` does not:
 * what is refused stays on screen as typed, nothing is built with it, and
 * the field shows the committed figure again the moment the value re-renders
 * — the reasoning `FeaturePanel`'s „Anfang" keeps.
 */
function NumberField({
  id,
  label,
  value,
  min,
  disabled = false,
  hint,
  onChange,
  onBlur,
}: {
  id: string;
  label: string;
  value: number;
  min?: number;
  disabled?: boolean;
  hint?: string | null;
  onChange: (value: number) => void;
  onBlur?: () => void;
}) {
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`${styles.input} ${styles.inputNum}`}
        type="number"
        inputMode="numeric"
        min={min}
        disabled={disabled}
        value={String(value)}
        onChange={(event) => {
          const raw = event.target.value.trim();
          if (!/^\d+$/.test(raw)) return;
          const next = Number(raw);
          if (min !== undefined && next < min) return;
          onChange(next);
        }}
        onBlur={onBlur}
      />
      {hint ? <span className={styles.hint}>{hint}</span> : null}
    </div>
  );
}

/**
 * What a money field accepts: digits, at most two decimals behind a dot or
 * a comma, no sign (Schlussreview I-2). `checked_amount` on the server reads
 * the same rule — a negative amount is a discount nobody meant, a third
 * decimal an amount the total never forms exactly.
 */
const AMOUNT = /^\d+([.,]\d{1,2})?$/;
/** A figure still being typed — „12," — is not a refusal yet, and not a value either. */
const AMOUNT_UNFINISHED = /^\d+[.,]$/;

/**
 * A money field: text, decimal keyboard, checked **at the field**.
 *
 * Until 12.09.2026 the string went on verbatim and the server refused
 * „12,50" — and the refusal came back as „Nicht gespeichert" with a reload,
 * on a figure every German keyboard produces first. Now the comma becomes a
 * dot before the store, and what does not read as an amount at all is
 * refused here, the way `featureWidthRefusal` refuses a width: the field
 * shows what was typed, marks it, and hands nothing on — no store change, no
 * `announce`, no version. Empty is a value (`null` at the caller), not an
 * error.
 *
 * The field keeps its own draft while somebody types, so „12," stays on
 * screen as typed and a refused „abc" stays visible under its mark; a draft
 * that reads is dropped on blur and the stored figure shows again.
 */
function MoneyField({
  id,
  label,
  value,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint?: string | null;
  /** Called with the normalised figure — dot, never comma — or `""`. */
  onChange: (text: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`${styles.input} ${styles.inputNum} ${refusal ? styles.inputBad : ""}`}
        type="text"
        inputMode="decimal"
        aria-invalid={refusal !== null}
        value={draft ?? value}
        onChange={(event) => {
          const raw = event.target.value.trim();
          setDraft(raw);
          if (raw === "" || AMOUNT.test(raw)) {
            setRefusal(null);
            onChange(raw.replace(",", "."));
            return;
          }
          if (AMOUNT_UNFINISHED.test(raw)) {
            setRefusal(null);
            return;
          }
          setRefusal(`„${raw}“ ist kein Betrag — Ziffern, höchstens zwei Nachkommastellen, kein Minus.`);
        }}
        onBlur={() => {
          if (refusal === null) setDraft(null);
        }}
      />
      {refusal ? (
        <span className={`${styles.hint} ${styles.hintBad}`}>{refusal}</span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

/** The value of the „Bezug" select: `wall_start` · `wall_end` · `feature:<id>:<side>`. */
function referenceValue(reference: CutoutReference): string {
  return reference.kind === "feature"
    ? `feature:${reference.featureId}:${reference.side}`
    : reference.kind;
}

function referenceFromValue(value: string): CutoutReference {
  if (value === "wall_start" || value === "wall_end") {
    return { kind: value, featureId: null, side: null };
  }
  const [, featureId, side] = value.split(":");
  return { kind: "feature", featureId: featureId ?? null, side: side === "before" ? "before" : "after" };
}

/** „F2 (Fenstertür)" — the object as the reference options and the linked
 * sentence name it: its id, then its label or its type's name. */
function featureName(feature: FacadeFeature): string {
  return `${feature.id} (${feature.label ?? FEATURE_DEFAULTS[feature.featureType].name})`;
}

/**
 * The hole somebody picked, and what can be done to it (design §8).
 *
 * The reference is display and input help (D-11): a distance is typed in
 * the reference's own terms — from the wall's start or end, or from an
 * object's edge — and converted into the stored `offsetMm`; the wall-start
 * figure stands beside it, so both readings are always on screen. A typed
 * figure is asserted (`LengthSource.MANUAL`), never re-snapped. A linked
 * hole has no position of its own along the wall — it says whose middle it
 * sits in, and only its size, its across figure, its confirmation and its
 * surcharge are open.
 */
function CutoutPanel({
  cutout,
  features,
  placements,
  wallLengthMm: wallLength,
  answer,
  surcharge,
  clearanceMm,
  onPipe,
  onChange,
  onSurcharge,
  onRemove,
}: {
  cutout: Cutout;
  features: readonly FacadeFeature[];
  placements: readonly Placement[];
  /** The wall's typed length, or `null` where it has none — then there is
   * no wall end to measure from, and „Wandende" is not offered. */
  wallLengthMm: number | null;
  /** The answer's row for this hole — cut edge, computed surcharge, what a
   * typed one displaced — or `null` before the answer has laid an element
   * under it. */
  answer: DemoElementCutout | null;
  /** What somebody typed for this hole, or `null` where the computation stands. */
  surcharge: CutoutSurcharge | null;
  /** The case's Montagespiel — the sum line of a downpipe's hole (A5). */
  clearanceMm: number;
  /** A downpipe to a new diameter, its hole with it — the screen's `setPipe`. */
  onPipe: (pipe: FacadeFeature, pipeMm: number, live: boolean) => void;
  onChange: (patch: Partial<Cutout>, live?: boolean) => void;
  onSurcharge: (typed: string | null, displaced: string | null) => void;
  onRemove: () => void;
}) {
  // Which field is mid-edit, so „1200" is one step on the stack and not
  // four — the same first-keystroke-opens-the-step rule `FeaturePanel` keeps.
  const [editing, setEditing] = useState<string | null>(null);
  const typing = (field: string) => {
    const already = editing === field;
    if (!already) setEditing(field);
    return already;
  };
  const done = () => setEditing(null);
  // A typed distance that lands before the wall start — „ab F2, davor" with
  // more than F2's own offset — is floored at 0 and said so (Schlussreview
  // I-3): the document refuses a negative middle, and a refusal after the
  // fact came back as „Nicht gespeichert" plus a reload. The hint stays
  // until the next figure lands inside the wall.
  const [beforeStart, setBeforeStart] = useState(false);

  const linked = cutout.featureId !== null;
  const anchor = linked ? (features.find((each) => each.id === cutout.featureId) ?? null) : null;
  const pipe = anchor?.featureType === "fallrohr" ? anchor : null;
  const alongMm = cutoutAlongMm(cutout, features);
  const onWall = features.filter(
    (each) => each.wallIndex === cutout.wallIndex && each.id !== cutout.featureId,
  );
  // The reference as shown: it falls back to the wall start when its object
  // is gone (D-11) — the stored `reference` is untouched, so the object
  // coming back (Rückgängig) restores the wording too. A wall end on a wall
  // without a length is the same case: nothing to measure from, so the
  // figure is shown from the start and the stored reference stays.
  const shown: CutoutReference =
    (cutout.reference.kind === "feature" &&
      !features.some((each) => each.id === cutout.reference.featureId)) ||
    (cutout.reference.kind === "wall_end" && wallLength === null)
      ? WALL_START
      : cutout.reference;
  const distanceMm = distanceToReference(shown, alongMm, wallLength ?? 0, features) ?? alongMm;
  // The element under the hole's middle — the one the workshop drills, and
  // the one „Maße bestätigt" is given for (§8, A-014).
  const carrier = carrierAt(placements, cutout.wallIndex, alongMm);
  const carrierName = carrier ? (carrier.label ?? carrier.id) : null;
  const extent = cutoutExtentMm(cutout);
  const moved =
    cutout.confirmed && cutout.confirmedOn !== null && carrierName !== null && carrierName !== cutout.confirmedOn;

  return (
    <>
      <p className={`label ${styles.panelHead}`}>
        <span className="num">{cutout.id}</span> AUSSCHNITT · Wand {cutout.wallIndex + 1}
      </p>

      {/* One of two, so a radio pair and not a checkbox (D-9: square first).
          Switching keeps one size — the square's along figure becomes the
          diameter and back — so nothing has to be typed twice. */}
      <fieldset className={styles.choice}>
        <legend className={`label ${styles.fieldLabel}`}>Form</legend>
        {(["eckig", "rund"] as const).map((shape) => (
          <label key={shape} className={styles.checkboxField}>
            <input
              type="radio"
              name="cutout-shape"
              value={shape}
              checked={cutout.shape === shape}
              onChange={() =>
                onChange(
                  shape === "rund"
                    ? { shape, diameterMm: extent.alongMm, sizeAlongMm: null, sizeAcrossMm: null }
                    : {
                        shape,
                        diameterMm: null,
                        sizeAlongMm: extent.alongMm,
                        sizeAcrossMm: extent.acrossMm || extent.alongMm,
                      },
                )
              }
            />
            {shape}
          </label>
        ))}
      </fieldset>

      {/* The one-click sizes (A5). On a downpipe's own hole they are pipe
          diameters and move the pipe with them — the hole is the pipe plus
          Spiel, and saying so in two places would let them disagree. On any
          other hole they are the hole itself: a „150er Ausschnitt" is one
          click, square as standard (D-9), the diameter where it is round. */}
      {pipe ? (
        <PipeFields
          pipeMm={pipe.lengthMm}
          hole={cutout}
          clearanceMm={clearanceMm}
          onPipe={(mm, live) => onPipe(pipe, mm, live)}
        />
      ) : (
        <div className={styles.field}>
          <span className={`label ${styles.fieldLabel}`} aria-hidden="true">
            Größe
          </span>
          <PresetChoice
            label="Größe"
            presets={PIPE_PRESETS_MM}
            value={
              cutout.shape === "rund"
                ? cutout.diameterMm
                : cutout.sizeAlongMm === cutout.sizeAcrossMm
                  ? cutout.sizeAlongMm
                  : null
            }
            customActive={
              !(PIPE_PRESETS_MM as readonly number[]).includes(
                (cutout.shape === "rund"
                  ? cutout.diameterMm
                  : cutout.sizeAlongMm === cutout.sizeAcrossMm
                    ? cutout.sizeAlongMm
                    : null) ?? -1,
              )
            }
            onPick={(mm) =>
              onChange(
                cutout.shape === "rund" ? { diameterMm: mm } : { sizeAlongMm: mm, sizeAcrossMm: mm },
              )
            }
            onCustom={() =>
              document
                .getElementById(cutout.shape === "rund" ? "cp-diameter" : "cp-along")
                ?.focus()
            }
          />
        </div>
      )}

      {cutout.shape === "rund" ? (
        <NumberField
          id="cp-diameter"
          label="Ø in mm"
          value={cutout.diameterMm ?? 0}
          min={1}
          onChange={(diameterMm) => onChange({ diameterMm }, typing("diameter"))}
          onBlur={done}
        />
      ) : (
        <>
          <NumberField
            id="cp-along"
            label="längs in mm"
            value={cutout.sizeAlongMm ?? 0}
            min={1}
            onChange={(sizeAlongMm) => onChange({ sizeAlongMm }, typing("along"))}
            onBlur={done}
          />
          <NumberField
            id="cp-across-size"
            label="quer in mm"
            value={cutout.sizeAcrossMm ?? 0}
            min={1}
            onChange={(sizeAcrossMm) => onChange({ sizeAcrossMm }, typing("across-size"))}
            onBlur={done}
          />
        </>
      )}

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="cp-reference">
          Bezug
        </label>
        <Select
          id="cp-reference"
          className={styles.input}
          value={referenceValue(shown)}
          disabled={linked}
          onChange={(event) => onChange({ reference: referenceFromValue(event.target.value) })}
        >
          <option value="wall_start">Wandanfang</option>
          {wallLength !== null ? <option value="wall_end">Wandende</option> : null}
          {onWall.flatMap((feature) => [
            <option key={`${feature.id}:before`} value={`feature:${feature.id}:before`}>
              ab {featureName(feature)}, davor
            </option>,
            <option key={`${feature.id}:after`} value={`feature:${feature.id}:after`}>
              ab {featureName(feature)}, dahinter
            </option>,
          ])}
        </Select>
      </div>

      <NumberField
        id="cp-distance"
        label="Abstand"
        value={distanceMm}
        disabled={linked}
        onChange={(typed) => {
          const offsetMm = offsetFromReference(shown, typed, wallLength ?? 0, features);
          if (offsetMm === null) return;
          setBeforeStart(offsetMm < 0);
          const floored = Math.max(offsetMm, 0);
          // A figure that moves the hole nowhere — a second one past the
          // start while it already sits at 0 — reports nothing, as the
          // arrow keys do; a `set_cutout` for an unmoved hole is a version
          // with nothing in it.
          if (floored === cutout.offsetMm) return;
          onChange({ offsetMm: floored }, typing("distance"));
        }}
        onBlur={done}
        hint={
          beforeStart
            ? "liegt vor dem Wandanfang"
            : linked && anchor
              ? `liegt in der Mitte von ${featureName(anchor)}`
              : shown.kind === "wall_start"
                ? "mm ab Wandanfang"
                : `= ${formatMm(alongMm)} ab Wandanfang`
        }
      />

      <NumberField
        id="cp-across"
        label="quer ab Rostkante"
        value={cutout.acrossMm}
        min={0}
        hint="mm von der fassadenseitigen Rostkante bis zur Lochmitte"
        onChange={(acrossMm) => onChange({ acrossMm }, typing("across"))}
        onBlur={done}
      />

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="cp-obstacle">
          Hindernis
        </label>
        <Select
          id="cp-obstacle"
          className={styles.input}
          value={cutout.obstacleType}
          onChange={(event) => onChange({ obstacleType: event.target.value as FeatureType })}
        >
          {FEATURE_TYPES.map((type) => (
            <option key={type} value={type}>
              {FEATURE_DEFAULTS[type].name}
            </option>
          ))}
        </Select>
      </div>

      {/* The release is given for the element the hole lies on, and the
          element's name is carried, never recomputed (§8, A-014): when the
          hole has since come to lie on another sheet, the panel says so
          rather than quietly re-attributing the confirmation. */}
      <label className={styles.checkboxField}>
        <input
          type="checkbox"
          checked={cutout.confirmed}
          aria-label="Maße bestätigt"
          onChange={(event) =>
            onChange({
              confirmed: event.target.checked,
              confirmedOn: event.target.checked ? carrierName : null,
            })
          }
        />
        Maße bestätigt
      </label>
      {moved ? (
        <span className={`${styles.hint} ${styles.hintBad}`}>
          bestätigt für {cutout.confirmedOn}, liegt jetzt auf {carrierName}
        </span>
      ) : null}

      {answer ? (
        <>
          <span className={styles.hint}>Schnittkante {formatMm(answer.cutLengthMm)}</span>
          <MoneyField
            id="cp-surcharge"
            label="Aufschlag netto"
            value={surcharge?.typed ?? answer.surchargeNet ?? ""}
            // No per-case rate any more (D-8): an empty field means the
            // supplier's price list prices the hole in the calculation; a
            // typed amount overrides it there as its own line.
            hint={
              surcharge?.typed
                ? answer.displacedNet
                  ? `gerechnet: ${formatMoney(answer.displacedNet)}`
                  : "von Hand — ersetzt die Preisliste in der Kalkulation"
                : answer.surchargeNet === null
                  ? "leer: Preis aus der Preisliste des Lieferanten, steht in der Kalkulation"
                  : "gerechnet"
            }
            // What a typed amount displaces is the computed one — read off
            // the answer *before* it started reporting the typed amount, so
            // the second keystroke does not displace the first.
            onChange={(text) =>
              onSurcharge(
                text === "" ? null : text,
                answer.surchargeTyped ? answer.displacedNet : answer.surchargeNet,
              )
            }
          />
          {surcharge?.typed ? (
            <Button variant="quiet" onClick={() => onSurcharge(null, null)}>
              Betrag entfernen
            </Button>
          ) : null}
        </>
      ) : null}

      <Button onClick={onRemove}>Ausschnitt entfernen</Button>
    </>
  );
}

function blankNote(placement: Placement, cut: ElementCut | undefined): string {
  const stretch = formatMm(placement.lengthMm);
  if (!cut || cut.blankMm === placement.lengthMm) return stretch;
  return `${stretch} · Rohling ${formatMm(cut.blankMm)}`;
}

/**
 * The parts-list cell for one element's holes — **one function for the screen
 * and the CSV** (§11), so the shop and the screen cannot drift apart.
 *
 * Every figure comes off the answer: `fromBlankStartMm` is the workshop's
 * zero (a named conversion over `growsAtStartMm`, done in `planning`),
 * `acrossMm` is verbatim (D-8), the reference is the customer's own wording
 * (D-11). „Mitte" and „unbestätigt" stand in the cell in as many words. The
 * wall-start figure beside a reference is `element.offsetMm +
 * fromBlankStartMm − growsAtStartMm` — the same conversion run backwards,
 * and the one arithmetic this function does.
 *
 * `undefined` where the answer has not laid this element (yet) — no hole
 * sentence without an answer to read it off.
 */
function cutoutNote(element: DemoPlacedElement | undefined): string | null {
  if (!element) return null;
  const holes = element.cutouts ?? [];
  if (holes.length === 0) return null;
  const grows = element.growsAtStartMm ?? 0;
  const label = element.label ?? element.placementId;
  return holes
    .map((hole) => {
      const shape =
        hole.shape === "rund"
          ? `rund Ø ${formatMm(hole.diameterMm ?? 0)}`
          : `eckig ${plain(hole.sizeAlongMm ?? 0)} × ${formatMm(hole.sizeAcrossMm ?? 0)}`;
      const parts = [shape];
      if (hole.referenceKind !== "wall_start" && hole.referenceLabel !== null && hole.fromReferenceMm !== null) {
        const fromWallStart = element.offsetMm + hole.fromBlankStartMm - grows;
        parts.push(
          `Mitte ${formatMm(hole.fromReferenceMm)} ab ${hole.referenceLabel} = ${formatMm(fromWallStart)} ab Wandanfang`,
        );
      }
      parts.push(`Mitte ${formatMm(hole.fromBlankStartMm)} ab Rohlinganfang, ${formatMm(hole.acrossMm)} ab Rostkante`);
      parts.push(FEATURE_DEFAULTS[hole.obstacleType as FeatureType]?.name ?? hole.obstacleType);
      parts.push(
        !hole.confirmed
          ? "unbestätigt"
          : hole.confirmedOn !== null && hole.confirmedOn !== label
            ? `bestätigt für ${hole.confirmedOn}, liegt jetzt auf ${label}`
            : "bestätigt",
      );
      return parts.join(" · ");
    })
    .join(" · ");
}

/** The two figures beside the cell — summed over the element's holes. */
function cutoutTotals(
  element: DemoPlacedElement | undefined,
): { cutLengthMm: number | null; surchargeNet: string | null } {
  const holes = element?.cutouts ?? [];
  if (holes.length === 0) return { cutLengthMm: null, surchargeNet: null };
  const cutLengthMm = holes.reduce((sum, hole) => sum + hole.cutLengthMm, 0);
  const amounts = holes.map((hole) => hole.surchargeNet).filter((each): each is string => each !== null);
  // Summed as integer cents — never as floats, never as a `Number` of a
  // decimal string (house rule, `ConsoleLineOut`).
  const cents = amounts.reduce((sum, each) => {
    const [whole, fraction = ""] = each.split(".");
    return sum + Number(whole) * 100 + Number((fraction + "00").slice(0, 2));
  }, 0);
  const surchargeNet = amounts.length === 0 ? null : `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
  return { cutLengthMm, surchargeNet };
}

/**
 * One glyph per tool, drawn in the board's own sign language — the band with
 * its cross bars, the diamond of the extra bracket, the square hole — so the
 * toolbar and the legend say the same thing with the same shapes. Sixteen
 * pixels, `currentColor`, hidden from the accessibility tree: the word beside
 * it is the name.
 */
function ToolGlyph({ tool }: { tool: Tool }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    "aria-hidden": true as const,
    focusable: "false" as const,
    className: styles.toolGlyph,
  };
  switch (tool) {
    case "select":
      return (
        <svg {...common}>
          <path d="M3 2l10 6-4.2 1.2L7.5 14z" fill="currentColor" />
        </svg>
      );
    case "feature":
      return (
        <svg {...common}>
          <rect x="3.5" y="2.5" width="9" height="12" fill="none" stroke="currentColor" />
          <circle cx="10" cy="9" r="0.9" fill="currentColor" />
        </svg>
      );
    case "area":
      return (
        <svg {...common}>
          <rect x="2.5" y="2.5" width="11" height="11" fill="none" stroke="currentColor" />
          <path d="M2.5 9l6.5-6.5M2.5 13.5L13.5 2.5M7 13.5l6.5-6.5" stroke="currentColor" />
        </svg>
      );
    case "grating":
      return (
        <svg {...common}>
          <path d="M1.5 5h13M1.5 11h13" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5 5v6M8 5v6M11 5v6" stroke="currentColor" />
        </svg>
      );
    case "cutout":
      return (
        <svg {...common}>
          <path d="M1.5 5h13M1.5 11h13" stroke="currentColor" strokeWidth="1.5" />
          <rect x="6" y="6" width="4" height="4" fill="none" stroke="currentColor" />
        </svg>
      );
    case "bracket":
      return (
        <svg {...common}>
          <path d="M8 2.5l5.5 5.5L8 13.5 2.5 8z" fill="none" stroke="currentColor" />
          <path d="M8 5.5v5M5.5 8h5" stroke="currentColor" />
        </svg>
      );
  }
}

/** The tick on a step that has something in it — a shape, never a colour. */
function StepCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path d="M2.5 7.5l3 3 6-6.5" fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

/**
 * Every facade object, grouped by wall — the first step's list, the way the
 * element list is the second's. Read top to bottom like a takeoff: „Wand 2 —
 * Haustür 1.010, Fallrohr 100".
 */
function FeatureList({
  features,
  wallCount,
  selected,
  onSelect,
}: {
  features: FacadeFeature[];
  wallCount: number;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const walls = Array.from({ length: wallCount }, (_, index) => index).filter((index) =>
    features.some((each) => each.wallIndex === index),
  );

  return (
    <ul className={styles.elements} aria-label="Objekte">
      {walls.map((wallIndex) => (
        <li key={wallIndex} className={styles.wallGroup}>
          <p className={`label ${styles.wallGroupHead}`}>Wand {wallIndex + 1}</p>
          <ul className={styles.wallElements}>
            {features
              .filter((each) => each.wallIndex === wallIndex)
              .sort((a, b) => a.offsetMm - b.offsetMm)
              .map((each) => (
                <li key={each.id}>
                  <button
                    type="button"
                    className={`${styles.elementRow} ${
                      selected === each.id ? styles.elementRowPicked : ""
                    }`}
                    onClick={() => onSelect(each.id)}
                  >
                    <span>{each.label ?? FEATURE_DEFAULTS[each.featureType].name}</span>
                    <span className={`num ${styles.elementMeta}`}>
                      {each.id} · {formatMm(each.lengthMm)}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function ElementList({
  placements,
  elements,
  wallCount,
  selected,
  cuts,
  onSelect,
}: {
  placements: Placement[];
  /** The answer's own laid elements, by `placementId` — read for their
   * holes (§11). `undefined` for an element the answer has not laid. */
  elements: ReadonlyMap<string, DemoPlacedElement>;
  wallCount: number;
  selected: string | null;
  cuts: ReadonlyMap<string, ElementCut>;
  onSelect: (id: string) => void;
}) {
  const walls = Array.from({ length: wallCount }, (_, index) => index).filter((index) =>
    placements.some((each) => each.wallIndex === index),
  );
  // One wall open at a time by default — the picked element's (20.10.2026):
  // thirty-four rows under the panel pulled the page far below the board.
  // A wall a person opened stays open; picking on another wall opens that one.
  const pickedWall = placements.find((each) => each.id === selected)?.wallIndex ?? null;
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set(pickedWall === null ? [] : [pickedWall]));
  const [openedFor, setOpenedFor] = useState(pickedWall);
  if (pickedWall !== openedFor) {
    setOpenedFor(pickedWall);
    if (pickedWall !== null && !open.has(pickedWall)) setOpen(new Set([...open, pickedWall]));
  }

  return (
    <ul className={styles.elements} aria-label="Elemente">
      {walls.map((wallIndex) => (
        <li key={wallIndex} className={styles.wallGroup}>
          <details
            className={styles.wallDetails}
            open={open.has(wallIndex)}
            onToggle={(event) => {
              const isOpen = event.currentTarget.open;
              if (isOpen === open.has(wallIndex)) return;
              const next = new Set(open);
              if (isOpen) next.add(wallIndex);
              else next.delete(wallIndex);
              setOpen(next);
            }}
          >
            <summary className={styles.wallSummary}>
              <span className={`label ${styles.wallGroupHead}`}>Wand {wallIndex + 1}</span>
              <span className={`num ${styles.wallCount}`}>
                {placements.filter((each) => each.wallIndex === wallIndex).length}
              </span>
              <span className={`num ${styles.wallPattern}`}>{wallPattern(placements, wallIndex)}</span>
            </summary>
          <ul className={styles.wallElements}>
            {placements
              .filter((each) => each.wallIndex === wallIndex)
              .sort((a, b) => a.offsetMm - b.offsetMm)
              .map((each) => (
                <li key={each.id}>
                  <button
                    type="button"
                    className={`${styles.elementRow} ${
                      selected === each.id ? styles.elementRowPicked : ""
                    }`}
                    onClick={() => onSelect(each.id)}
                  >
                    <span className="num">{each.label ?? "—"}</span>
                    <span className={styles.elementMeta} data-role="element-meta">
                      {each.kind === "grating"
                        ? [blankNote(each, cuts.get(each.id)), cutoutNote(elements.get(each.id))]
                            .filter((part) => part !== null)
                            .join(" · ")
                        : each.kind === "gap"
                          ? // The kind of interruption, not the word
                            // „Unterbrechung": four downpipes on A-10 are four
                            // identical rows otherwise, and the one property
                            // the bracket count hangs on — whether it divides
                            // the run — would not be in the list at all.
                            `${each.splitsRun ? "Tür" : "Fallrohr"} ${formatMm(each.lengthMm)}`
                          : `${KIND_NAMES[each.kind]} ${formatMm(each.lengthMm)}`}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
          </details>
        </li>
      ))}
    </ul>
  );
}

/**
 * A wall's gratings as one line, in the board's own shorthand: runs of equal
 * lengths as „4×1.408“, bare numbers (20.10.2026) — what the closed group says
 * without opening it.
 */
function wallPattern(placements: readonly Placement[], wallIndex: number): string {
  const lengths = placements
    .filter((each) => each.wallIndex === wallIndex && each.kind === "grating")
    .sort((a, b) => a.offsetMm - b.offsetMm)
    .map((each) => each.lengthMm);
  const runs: { mm: number; count: number }[] = [];
  for (const mm of lengths) {
    const last = runs.at(-1);
    if (last && last.mm === mm) last.count += 1;
    else runs.push({ mm, count: 1 });
  }
  return runs.map((run) => (run.count > 1 ? `${run.count}×${plain(run.mm)}` : plain(run.mm))).join(" · ");
}

/**
 * „Aufteilung“ on a grating (Denis, 20.10.2026): two fields, the part away from
 * the mitre and the part at it, each with the blank it comes to — adjusted
 * before the press, and always adding up to the element's wall stretch: typing
 * one sets the other. Halves to begin with. The press is held, with the reason
 * beside it, while a piece would fall outside the buildable band or a blank
 * past the maximum (the rule „Rohling aufteilen“ keeps, E-16 — no rounding).
 */
function SplitControl({
  totalMm,
  growsStartMm,
  growsEndMm,
  minMm,
  maxMm,
  onSplit,
}: {
  totalMm: number;
  growsStartMm: number;
  growsEndMm: number;
  minMm: number;
  maxMm: number;
  onSplit: (firstMm: number) => void;
}) {
  const [firstText, setFirstText] = useState(String(Math.floor(totalMm / 2)));
  const first = /^\d+$/.test(firstText.trim()) ? Number(firstText) : null;
  const second = first === null ? null : totalMm - first;
  const mitred = (grows: number) => grows !== 0;
  const name = (grows: number, other: number, position: "Anfang" | "Ende") =>
    mitred(grows) && mitred(other)
      ? `${position} · Gehrung`
      : mitred(grows)
        ? "An der Gehrung"
        : mitred(other)
          ? "Ohne Gehrung"
          : position === "Anfang"
            ? "Teil 1"
            : "Teil 2";
  const pieces = [
    { id: "p-split-first", name: name(growsStartMm, growsEndMm, "Anfang"), mm: first, grows: growsStartMm },
    { id: "p-split-second", name: name(growsEndMm, growsStartMm, "Ende"), mm: second, grows: growsEndMm },
  ];
  const problem =
    first === null || second === null
      ? "Ganze Millimeter, z. B. 700."
      : first < 1 || second < 1
        ? `Beide Teile brauchen eine Länge — zusammen ${formatMm(totalMm)}.`
        : pieces.some((piece) => piece.mm! < minMm || piece.mm! > maxMm)
          ? `Jeder Teil misst ${plain(minMm)} bis ${formatMm(maxMm)} Wandstrecke.`
          : pieces.some((piece) => piece.mm! + piece.grows > maxMm)
            ? `Ein Rohling käme über ${formatMm(maxMm)} — verschieb die Teilung zur Gehrung hin.`
            : null;
  const reasonId = "p-split-reason";

  return (
    <div className={styles.splitBox} data-role="split-control">
      <p className={`label ${styles.fieldLabel}`}>Aufteilung</p>
      <div className={styles.splitFields}>
        {pieces.map((piece, index) => (
          <div key={piece.id} className={styles.splitField}>
            <label className={styles.splitName} htmlFor={piece.id}>
              {piece.name}
            </label>
            <input
              id={piece.id}
              className={`${styles.input} ${styles.inputNum} ${problem !== null && first !== null ? styles.inputBad : ""}`}
              inputMode="numeric"
              autoComplete="off"
              value={piece.mm === null ? (index === 0 ? firstText : "") : String(piece.mm)}
              onChange={(event) => {
                const raw = event.target.value.trim();
                if (index === 0) setFirstText(raw);
                else if (/^\d+$/.test(raw)) setFirstText(String(totalMm - Number(raw)));
                else setFirstText("");
              }}
            />
            <span className={`num ${styles.splitBlank}`}>
              {piece.mm !== null && piece.grows !== 0 ? `Rohling ${plain(piece.mm + piece.grows)}` : "\u00a0"}
            </span>
          </div>
        ))}
      </div>
      <Button
        variant="secondary"
        aria-disabled={problem !== null ? "true" : undefined}
        aria-describedby={reasonId}
        onClick={() => {
          if (problem === null && first !== null) onSplit(first);
        }}
      >
        Aufteilung
      </Button>
      <span id={reasonId} className={`${styles.hint} ${problem !== null ? styles.hintBad : ""}`}>
        {problem ?? `Zusammen ${formatMm(totalMm)} — die Teile werden zwei Elemente.`}
      </span>
    </div>
  );
}

function PlacementPanel({
  placement,
  specs,
  jobGratingType,
  lengthText,
  cut,
  onLengthText,
  onLengthDone,
  onChange,
  onSplit,
  onRemove,
}: {
  placement: Placement;
  /** What the answer says about this element's ends, where it has one. */
  cut: ElementCut | undefined;
  /** „Aufteilung“: divide this grating, the first piece `firstMm` long. */
  onSplit: (firstMm: number) => void;
  specs: GratingSpec[];
  jobGratingType: string | null;
  lengthText: string | null;
  onLengthText: (text: string) => void;
  onLengthDone: () => void;
  onChange: (patch: Partial<Placement>, live?: boolean) => void;
  onRemove: () => void;
}) {
  const spec = specs.find((each) => each.type === (placement.gratingType ?? jobGratingType));
  const editing = lengthText !== null;
  const text = lengthText ?? String(placement.lengthMm);
  const typed = Number(text);
  const valid = /^\d+$/.test(text.trim()) && Number.isFinite(typed);

  /**
   * The asymmetry, and both halves of it (design §5.5.2).
   *
   * **Too big colours at once** — „2400" cannot be a step towards anything
   * valid, so waiting would only delay a verdict that is already certain.
   *
   * **Too small colours when the field is left**, because the road to „1200"
   * runs through „1", „12" and „120", and a field that goes red under the
   * first keystroke of every edit teaches people to ignore red. The first
   * version had only the first half: too small never coloured at all, and the
   * `lengthText` state existed for a rule it did not carry (found in review,
   * 16.08.2026).
   */
  const tooBig = valid && spec ? typed > spec.maxLengthMm : false;
  const tooSmall = valid && spec && !editing ? typed < spec.minLengthMm : false;
  const refused = tooBig || tooSmall;

  return (
    <>
      <p className={`label ${styles.panelHead}`}>
        <span className="num">{placement.label ?? "—"}</span>{" "}
        {KIND_NAMES[placement.kind]?.toUpperCase()} · Wand {placement.wallIndex + 1}
      </p>

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="p-length">
          {/* „Wandstrecke" and not „Länge": at a treated corner an element is
              longer than the stretch it occupies, and the word „Länge" invited
              exactly the question of which of the two it meant (asked on
              screen, 27.08.2026). The parts list already calls it this. */}
          Wandstrecke
        </label>
        <input
          id="p-length"
          className={`${styles.input} ${styles.inputNum} ${refused ? styles.inputBad : ""}`}
          inputMode="numeric"
          autoComplete="off"
          value={text}
          onChange={(event) => {
            onLengthText(event.target.value);
            const next = Number(event.target.value);
            // `live` folds the keystrokes of one edit into one step on the undo
            // stack. Without it „1200" is four steps and „Terrassentür" is
            // twelve, which makes Rückgängig useless the moment anybody types
            // (design §5.5.2; found in review, 16.08.2026).
            if (/^\d+$/.test(event.target.value) && next > 0) {
              // The **first** keystroke of an edit opens the step; every one
              // after it overwrites that step. Marking the first one live too
              // would overwrite the state *before* the edit, and Rückgängig
              // would then undo the element instead of the typing.
              onChange({ lengthMm: next }, editing);
            }
          }}
          onBlur={onLengthDone}
        />
        <span className={`${styles.hint} ${refused ? styles.hintBad : ""}`}>
          {refused && spec
            ? `${formatMm(typed)} — ein Element misst ${plain(spec.minLengthMm)} bis ${formatMm(spec.maxLengthMm)}. ${
                tooBig
                  ? "Teile es, oder setz zwei Elemente."
                  : "Leg den Lauf anders auf."
              }`
            : spec
              ? `${plain(spec.minLengthMm)} bis ${formatMm(spec.maxLengthMm)} · Standard ${formatMm(spec.standardLengthMm)}`
              : "in Millimetern"}
        </span>
      </div>

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="p-offset">
          Lage
        </label>
        <input
          id="p-offset"
          className={`${styles.input} ${styles.inputNum}`}
          inputMode="numeric"
          autoComplete="off"
          value={String(placement.offsetMm)}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (/^\d+$/.test(event.target.value)) onChange({ offsetMm: next });
          }}
        />
        <span className={styles.hint}>mm vom Wandanfang</span>
      </div>

      {placement.kind === "grating" && cut ? (
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="p-blank">
            Rohling
          </label>
          <input
            id="p-blank"
            className={`${styles.input} ${styles.inputNum}`}
            inputMode="numeric"
            autoComplete="off"
            placeholder={String(cut.blankMm)}
            value={placement.blankLengthMm == null ? "" : String(placement.blankLengthMm)}
            onChange={(event) => {
              const raw = event.target.value.trim();
              if (raw === "") {
                // Emptied means „back to what the arithmetic makes of it" —
                // the same reading `null` has everywhere else on this DTO.
                onChange({ blankLengthMm: null, blankSnapshotMm: null });
                return;
              }
              const next = Number(raw);
              if (!/^\d+$/.test(raw) || next < 1) return;
              // The derived figure is stamped at the moment of typing, so the
              // screen can say later that the two have drifted apart.
              onChange({ blankLengthMm: next, blankSnapshotMm: cut.blankMm });
            }}
          />
          <span className={styles.hint}>{blankHint(placement, cut)}</span>
        </div>
      ) : null}

      {/* Only where two buildable pieces fit at all — a press that can never
          go is no offer. */}
      {placement.kind === "grating" && spec && placement.lengthMm >= 2 * spec.minLengthMm ? (
        <SplitControl
          // Fresh per element and per length: a half-typed division of one
          // element must not stand in the next one's fields.
          key={`${placement.id}:${placement.lengthMm}`}
          totalMm={placement.lengthMm}
          growsStartMm={cut?.growsStartPx ?? 0}
          growsEndMm={cut?.growsEndPx ?? 0}
          minMm={spec.minLengthMm}
          maxMm={spec.maxLengthMm}
          onSplit={onSplit}
        />
      ) : null}

      {placement.kind === "gap" ? (
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="p-gap-kind">
            Art
          </label>
          <Select
            id="p-gap-kind"
            className={styles.input}
            value={GAP_KINDS.find((each) => each.splitsRun === placement.splitsRun)?.key ?? "door"}
            onChange={(event) =>
              onChange({
                splitsRun:
                  GAP_KINDS.find((each) => each.key === event.target.value)?.splitsRun ?? true,
              })
            }
          >
            {GAP_KINDS.map((each) => (
              <option key={each.key} value={each.key}>
                {each.name}
              </option>
            ))}
          </Select>
          <span className={styles.hint}>
            {placement.splitsRun
              ? "Der Lauf endet hier und beginnt dahinter neu."
              : "Der Lauf geht darüber hinweg — eine Konsole, nicht zwei."}
          </span>
        </div>
      ) : null}

      {/* Without a depth an Außenfläche draws nothing at all — the board skips
          a hatch whose depth is null. Typed rather than dragged for the same
          reason the interruption is: it is a rough figure somebody knows
          („der Kiesstreifen ist 40 cm"), and a second drag direction on a
          board this scale buys nothing. */}
      {placement.kind === "area" ? (
        <div className={styles.field}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="p-depth">
            Tiefe
          </label>
          <input
            id="p-depth"
            className={`${styles.input} ${styles.inputNum}`}
            inputMode="numeric"
            autoComplete="off"
            placeholder="z. B. 400"
            value={placement.depthMm === null ? "" : String(placement.depthMm)}
            onChange={(event) => {
              const raw = event.target.value.trim();
              if (raw === "") onChange({ depthMm: null });
              else if (/^\d+$/.test(raw)) onChange({ depthMm: Number(raw) });
            }}
          />
          <span className={styles.hint}>mm von der Fassade weg · geht in keine Mengenliste</span>
        </div>
      ) : null}

      {placement.kind === "grating" ? (
        <>
          <div className={styles.field}>
            <label className={`label ${styles.fieldLabel}`} htmlFor="p-type">
              Rosttyp
            </label>
            <Select
              id="p-type"
              className={styles.input}
              value={placement.gratingType ?? jobGratingType ?? ""}
              onChange={(event) => onChange({ gratingType: event.target.value })}
            >
              {specs.map((each) => (
                <option key={each.type} value={each.type}>
                  {GRATING_TYPE_NAMES[each.type] ?? each.type}
                </option>
              ))}
            </Select>
            <span className={styles.hint}>
              Standard für diesen Vorgang:{" "}
              {jobGratingType ? (GRATING_TYPE_NAMES[jobGratingType] ?? jobGratingType) : "—"}
            </span>
          </div>

          {/* A reading, not a field. A greyed-out input looks broken because it
              is one — and the lever that changes the width stands directly
              above it, so the question „why can I not change this" is answered
              where it is asked (design §5.5.2). */}
          <div className={styles.reading}>
            <span className={`label ${styles.fieldLabel}`}>Breite</span>
            <span className={`num ${styles.readingValue}`} data-testid="width-reading">
              {formatMm(effectiveWidthMm(placement, specs, jobGratingType))}
            </span>
            <span className={styles.hint}>
              {placement.widthMm === null ? "folgt aus dem Rosttyp" : "Sonderbreite"}
            </span>
          </div>

          <div className={styles.field}>
            <label className={`label ${styles.fieldLabel}`} htmlFor="p-width">
              Sonderbreite
            </label>
            <input
              id="p-width"
              className={`${styles.input} ${styles.inputNum}`}
              inputMode="numeric"
              autoComplete="off"
              placeholder="leer = Rosttyp"
              value={placement.widthMm === null ? "" : String(placement.widthMm)}
              onChange={(event) => {
                const raw = event.target.value.trim();
                if (raw === "") onChange({ widthMm: null });
                else if (/^\d+$/.test(raw)) onChange({ widthMm: Number(raw) });
              }}
            />
            <span className={styles.hint}>Weicht vom Rosttyp ab. Geht so in die Mengenliste.</span>
          </div>

          {/* Only where a special width stands: without one there is nothing
              to lay either way. Drawing only — the area and the bracket
              follow from the width alone (Denis, 31.10.2026). */}
          {placement.widthMm !== null ? (
            <div className={styles.field}>
              <label className={styles.checkboxField}>
                <input
                  type="checkbox"
                  checked={placement.widensOutward ?? false}
                  onChange={(event) => onChange({ widensOutward: event.target.checked })}
                />
                Mehrbreite nach außen
              </label>
              <span className={styles.hint}>
                Innenkante bleibt an der Wand. Vor einem Rücksprung: Rostbreite plus Rücksprung, dann schließt der
                Rost außen bündig mit den Nachbarn ab.
              </span>
            </div>
          ) : null}
        </>
      ) : null}

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="p-label">
          Bezeichnung
        </label>
        <input
          id="p-label"
          className={styles.input}
          autoComplete="off"
          value={placement.label ?? ""}
          // Folded like the length: „Terrassentür" is one step, not twelve.
          onChange={(event) => onChange({ label: event.target.value || null }, true)}
        />
        <span className={styles.hint}>fortlaufend vergeben, änderbar</span>
      </div>

      <Button onClick={onRemove}>Element entfernen</Button>
    </>
  );
}

function WallPanel({
  outline,
  placements,
  wallIndex,
  busy,
  corner,
  miter,
  onCornerChange,
  onCover,
  measure,
  lockedBelowMeasure = false,
  layingWords,
  split,
}: {
  outline: DemoOutline;
  placements: Placement[];
  wallIndex: number;
  busy: boolean;
  /**
   * „Maß“ on a Vorgang (`WallMeasureField`), or `null` on the register demo
   * and on a wall without a document id. Where it stands it carries the
   * wall's figure, and the read-only „Länge“ row gives way to it.
   */
  measure: ReactNode;
  /**
   * Everything below {@link measure} is held (final review I-1): the board is
   * read-only because a chamfer cannot be drawn, and the measure is the one
   * way back. The panel's own fieldset is open then, so this one holds the
   * rest — „Wand belegen“, the variants, the corner — as the board's would.
   */
  lockedBelowMeasure?: boolean;
  /** What the answer settled on at this wall's end corner, where a run
   * crosses it at all. */
  corner: CornerFact | null;
  miter: CornerMark | null;
  onCornerChange: (patch: Partial<CornerMark>) => void;
  onCover: () => void;
  /** What „Wand belegen" lays, in words — the rule's name once known. */
  layingWords: string;
  /**
   * Fall C, in step 2 only (`null` elsewhere): the variants of this wall,
   * `wall: null` where it carries no grating yet — a variant is what a
   * re-laying would lay, and an empty wall has nothing to re-lay (design §6).
   */
  split: {
    wall: DemoWallSplitVariants | null;
    rules: readonly DemoSplitRule[];
    priority: SplitPriority;
    onPriority: (priority: SplitPriority) => void;
    onRelay: () => void;
  } | null;
}) {
  // Which of the two buttons was pressed — both wait on the same call, and
  // only the pressed one says what it is doing.
  const [pressed, setPressed] = useState<"cover" | "relay">("cover");
  const length = wallLengthMm(outline, wallIndex);
  const segment = outline.segments[wallIndex];
  /** Both lengths of a wall a chamfer shortened (design 2026-10-18 §5.1). */
  const shortened = segment ? shortenedWallLabel(segment) : null;
  const accepts = wallAcceptsGrating(outline, wallIndex);
  const free = freeStretches(outline, placements, wallIndex).reduce(
    (sum, stretch) => sum + stretch.lengthMm,
    0,
  );

  /** What stands below the field, held where {@link lockedBelowMeasure} says
   * so — a fieldset of its own, since the panel's is open for the field. */
  const below = (rest: ReactNode): ReactNode =>
    lockedBelowMeasure ? (
      <fieldset className={styles.heldBelow} disabled data-role="held-below-measure">
        {rest}
      </fieldset>
    ) : (
      rest
    );

  return (
    <>
      <p className={`label ${styles.panelHead}`}>Wand {wallIndex + 1}</p>
      {measure}
      {below(accepts && length !== null ? (
        <>
          {measure === null ? (
            <div className={styles.reading}>
              <span className={`label ${styles.fieldLabel}`}>Länge</span>
              <span className={`num ${styles.readingValue}`}>{shortened ?? formatMm(length)}</span>
            </div>
          ) : null}
          <div className={styles.reading}>
            <span className={`label ${styles.fieldLabel}`}>davon belegt</span>
            <span className={`num ${styles.readingValue}`}>{formatMm(length - free)}</span>
          </div>
          <div className={styles.reading}>
            <span className={`label ${styles.fieldLabel}`}>frei</span>
            <span className={`num ${styles.readingValue}`}>{formatMm(free)}</span>
          </div>
          <Button
            onClick={() => {
              setPressed("cover");
              onCover();
            }}
            disabled={busy}
          >
            {busy && pressed === "cover" ? `Wand ${wallIndex + 1} wird belegt …` : "Wand belegen"}
          </Button>
          {/* Under its button since Fall C put a list below it — a hint
              three sections away from what it explains is not read. */}
          <span className={styles.hint}>
            Legt {layingWords} über die freie Länge, nie ein Rest unter 400 mm.
            Fassadenobjekte, auf denen kein Rost liegt, werden ausgespart. Danach ist jedes
            Element einzeln änderbar.
          </span>

          {split?.wall ? (
            /* Fall C: the list, the setting that marks one of its rows, and
               — where the wall does not lie as marked — the gesture that lays
               it. In that order: compare, choose, act. Keyed by wall, so the
               list's live region speaks for a change on this wall and not for
               picking the next one. */
            <div className={styles.toolForm} key={wallIndex} data-role="split-variants">
              <SplitVariantList wall={split.wall} priority={split.priority} />
              <SplitPriorityField
                id="split-priority-wall"
                rules={split.rules}
                value={split.priority}
                onChange={split.onPriority}
              />
              <RelayOffer
                wall={split.wall}
                priority={split.priority}
                busy={busy && pressed === "relay"}
                disabled={busy}
                onRelay={() => {
                  setPressed("relay");
                  split.onRelay();
                }}
              />
            </div>
          ) : null}

          {miter && corner ? (
            <>
              <p className={`label ${styles.panelHead}`}>
                Gehrung an der Ecke nach Wand {wallIndex + 1}
              </p>

              <div className={styles.field}>
                <label className={`label ${styles.fieldLabel}`} htmlFor="m-kind">
                  Eckart
                </label>
                <Select
                  id="m-kind"
                  className={styles.input}
                  value={miter.kindOverride ?? ""}
                  onChange={(event) =>
                    onCornerChange({
                      kindOverride:
                        event.target.value === ""
                          ? null
                          : (event.target.value as "aussen" | "innen"),
                    })
                  }
                >
                  <option value="">
                    abgeleitet{corner.kind ? ` — ${CORNER_NAMES[corner.kind]}` : ""}
                  </option>
                  <option value="aussen">Außenecke</option>
                  <option value="innen">Innenecke</option>
                </Select>
                <span className={styles.hint}>
                  Nur an der Außenecke wächst ein Rohling. Der Notausgang für einen Umriss,
                  den die Ableitung falsch einordnet — er wird nie zurückgewiesen, nur
                  vermerkt.
                </span>
              </div>

              <div className={styles.field}>
                <label className={`label ${styles.fieldLabel}`} htmlFor="m-gain">
                  Zuwachs
                </label>
                <input
                  id="m-gain"
                  className={`${styles.input} ${styles.inputNum}`}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={String(corner.gainMm)}
                  value={miter.gainOverrideMm === null ? "" : String(miter.gainOverrideMm)}
                  onChange={(event) => {
                    const raw = event.target.value.trim();
                    if (raw === "") {
                      onCornerChange({ gainOverrideMm: null });
                      return;
                    }
                    if (!/^\d+$/.test(raw)) return;
                    onCornerChange({ gainOverrideMm: Number(raw) });
                  }}
                />
                <span className={styles.hint}>
                  {miter.gainOverrideMm !== null
                    ? `von Hand · abgeleitet wären ${formatMm(corner.gainMm)}`
                    : corner.kind === "innen"
                      ? "An einer Innenecke wächst nichts — das Eckquadrat war doppelt belegt und wird geteilt."
                      : `leer heißt: die Rostbreite — ${formatMm(corner.gainMm)} an dieser Ecke`}
                </span>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          {/* With „Maß“ above, the field says „ohne Maß“ itself. */}
          {measure === null ? (
            <p className={styles.idle}>Wand {wallIndex + 1} trägt kein Maß.</p>
          ) : null}
          <span className={styles.hint}>
            Ohne getipptes oder gelesenes Maß gibt es kein Lineal, an dem ein Element liegen
            könnte. Eine nur gezogene Strecke ist kein Produktionsmaß.
          </span>
        </>
      ))}
    </>
  );
}
